/* =============================================================================
 * Arachne Audio — Native Cable Configurator (engine)
 * Vanilla JS. No framework, no build step, no dependencies.
 *
 * Self-initializes: scans the DOM for `[data-acfg-root]` and builds one
 * configurator per match. Each root carries its data in three embedded JSON
 * <script> blocks (config / assets / settings) plus a few data-attributes
 * (see cable-configurator.liquid).
 *
 * Design (see CLAUDE.md + the migrated catalog): the option data is
 * heterogeneous — 21+ distinct field signatures, 4 control types, British and
 * American spellings, even leaked raw ids. So we do NOT key behaviour on exact
 * field names. Each option is CLASSIFIED into a render role by (globo_field
 * pattern, type); anything unrecognised renders generically by type, so a new
 * option in a future metafield never breaks the page.
 *
 * Cart contract: base variant + properties{} using EXACT globo_field keys and
 * EXACT (untrimmed) value labels + a `_config_id` UUID. Then, for every
 * selected value that carries a `variant_id` (⟺ it has a price add-on), one
 * extra hidden line with the same `_config_id`. This mirrors Globo and keeps
 * order totals correct without Shopify Functions.
 * ========================================================================== */
(function () {
  "use strict";

  /* ---- colour family taxonomy (adopted from the design prototype) ----
   * Six browse groups by 1-based swatch number. Groups OVERLAP (a colour can sit
   * in several), so this is MEMBERSHIP, not a partition — the filter shows every
   * swatch whose number is in the active group. "All" shows the full palette.
   * Cosmetic only: the submitted value is always the option's own data label. */
  var FAMILY_GROUPS = [
    ["all", "All", null],
    ["neutral", "Neutral", [1, 2, 3, 4, 15, 22, 29]],
    ["warm", "Warm", [7, 8, 9, 10, 11, 12, 13, 14, 20, 21]],
    ["cool", "Cool", [16, 17, 18, 19, 22, 23, 24, 25]],
    ["earthy", "Earthy", [6, 7, 14, 25, 26]],
    ["statement", "Statement", [9, 10, 12, 20, 27, 28, 29]],
    ["patterned", "Patterned", [5, 6, 10]]
  ];
  var FAMILY_SET = {};
  FAMILY_GROUPS.forEach(function (g) {
    if (!g[2]) return;
    FAMILY_SET[g[0]] = {};
    g[2].forEach(function (n) { FAMILY_SET[g[0]][n] = true; });
  });
  function inFamily(num, fam) { return fam === "all" || !!(FAMILY_SET[fam] && FAMILY_SET[fam][num]); }

  /* ---- colour arrangement model (Y-cable), adopted from the design prototype ----
   * Maps a chosen palette (length = numColours) onto the four braid strands
   * [L1, L2, R1, R2] via slot indices. 1 and 4 colours have a single forced
   * arrangement; 2 and 3 offer a topology choice. UI only — it resolves down to the
   * four real colour-slot labels the cart already submits. */
  var ARRANGEMENTS = {
    1: [{ id: "solid", name: "Single colour", slots: [0, 0, 0, 0], desc: "All four strands the same." }],
    2: [
      { id: "per-side", name: "Per side", slots: [0, 0, 1, 1], desc: "Left side colour A. Right side colour B." },
      { id: "alternating", name: "Alternating", slots: [0, 1, 0, 1], desc: "Each side mixes both colours." }
    ],
    3: [
      // The NAMED side is the WEARER'S side where the two matching strands (the
      // same-colour pair) sit. The glyph renders as-worn (wearer's L on the viewer's
      // right — see arrangementGlyphSVG), so "Two on left" shows its pair under the
      // glyph's "L" tag on the right. pair-right is the exact MIRROR of pair-left
      // ([0,0,1,2] reversed): the pair is always palette colour A on its named side,
      // so the two cards read as reflections of each other.
      { id: "pair-left", name: "Two on left", slots: [0, 0, 1, 2], desc: "Two matching strands on the left; two colours on the right." },
      { id: "pair-right", name: "Two on right", slots: [2, 1, 0, 0], desc: "Two colours on the left; two matching strands on the right." },
      { id: "split", name: "Split", slots: [0, 1, 2, 0], desc: "Three colours — the outer strand on each side shares one." }
    ],
    4: [{ id: "custom", name: "Custom", slots: [0, 1, 2, 3], desc: "Every strand a different colour." }]
  };
  function arrangementsFor(n) { return ARRANGEMENTS[n] || ARRANGEMENTS[1]; }
  function arrangementById(n, id) {
    var a = arrangementsFor(n);
    for (var i = 0; i < a.length; i++) if (a[i].id === id) return a[i];
    return a[0];
  }
  // Smallest colour count + arrangement that reproduces four strand numbers exactly
  // (with that many DISTINCT palette colours). Inverse of the resolve; any layout
  // that matches no named arrangement falls back to 4-colour "custom".
  function inferArrangement(nums) {
    for (var n = 1; n <= 4; n++) {
      var arrs = ARRANGEMENTS[n];
      for (var a = 0; a < arrs.length; a++) {
        var slots = arrs[a].slots, pal = [], ok = true;
        for (var i = 0; i < 4; i++) {
          var idx = slots[i];
          if (pal[idx] === undefined) pal[idx] = nums[i];
          else if (pal[idx] !== nums[i]) { ok = false; break; }
        }
        if (!ok) continue;
        var distinct = {}, cnt = 0;
        for (var j = 0; j < n; j++) { if (pal[j] === undefined) pal[j] = nums[0]; if (!distinct[pal[j]]) { distinct[pal[j]] = 1; cnt++; } }
        if (cnt === n) return { num: n, arr: arrs[a].id, pal: pal };
      }
    }
    return { num: 4, arr: "custom", pal: nums.slice() };
  }

  /* ---------------------------------- utils ------------------------------- */
  function h(tag, props, children) {
    var el = document.createElement(tag);
    props = props || {};
    Object.keys(props).forEach(function (k) {
      var v = props[k];
      if (v == null) return;
      if (k === "class") el.className = v;
      else if (k === "text") el.textContent = v;
      else if (k === "html") el.innerHTML = v;
      else if (k === "style") el.setAttribute("style", v);
      else if (k === "on" && typeof v === "object") {
        Object.keys(v).forEach(function (evt) { el.addEventListener(evt, v[evt]); });
      } else if (k.indexOf("data-") === 0 || k.indexOf("aria-") === 0 || k === "type" || k === "title" || k === "min" ||
                 k === "max" || k === "step" || k === "value" ||
                 k === "role" || k === "tabindex" || k === "inputmode") {
        el.setAttribute(k, v);
      } else {
        el[k] = v;
      }
    });
    appendChildren(el, children);
    return el;
  }
  function appendChildren(el, children) {
    if (children == null) return;
    if (!Array.isArray(children)) children = [children];
    children.forEach(function (c) {
      if (c == null || c === false) return;
      el.appendChild(typeof c === "string" || typeof c === "number" ? document.createTextNode(String(c)) : c);
    });
  }
  function btn(props, children) {
    props = props || {};
    props.type = "button";
    return h("button", props, children);
  }
  function parseJSONScript(root, selector) {
    var node = root.querySelector(selector);
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { console.error("[acfg] bad JSON in " + selector, e); return null; }
  }
  function uuid() {
    if (window.crypto && crypto.randomUUID) { try { return crypto.randomUUID(); } catch (e) {} }
    return "cfg-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
  }
  function money(n, currency) {
    var sym = currency || "€";
    return sym + (Math.round((n + Number.EPSILON) * 100) / 100).toFixed(2);
  }
  // money() for prose (the organizer hint): drops ".00" so EUR reads "€25 · €20",
  // while converted presentment amounts keep real cents ("$22.36").
  function moneyTrim(n, currency) { return money(n, currency).replace(/\.00$/, ""); }
  function colourNumber(label) { var m = /^\s*(\d+)/.exec(label || ""); return m ? parseInt(m[1], 10) : 0; }
  function metresOf(label) {
    // Sub-metre stops (adapter cables: "35cm / 1.1ft") parse to fractional metres so the
    // slider, notes (length_note_0_35) and unit toggle all keep working.
    var s = label || "", cm = /^\s*([\d.]+)\s*cm/i.exec(s);
    if (cm) return parseFloat(cm[1]) / 100;
    var m = /^\s*([\d.]+)\s*m/i.exec(s); return m ? parseFloat(m[1]) : null;
  }
  function clean(s) { return (s == null ? "" : String(s)).trim(); }
  function lc(s) { return clean(s).toLowerCase(); }
  // Reflect a toggle-button's selected state for assistive tech (the visual state is class-only).
  function ariaPressed(el, on) { if (el && el.setAttribute) el.setAttribute("aria-pressed", on ? "true" : "false"); }
  // Seasonal "special" label (Halloween 2026): a colour or organizer value whose data
  // carries `special` (e.g. "halloween") gets two short text lines BELOW its swatch
  // name / tile price: "Halloween special" + "Limited qty". Text only and never over
  // the photo (Albert 2026-10-05: the picture is the hook). Unknown kinds render nothing.
  var SPECIAL_NAMES = { halloween: "Halloween special" };
  function specialName(kind) { return kind && SPECIAL_NAMES[kind] ? T("special_" + kind, SPECIAL_NAMES[kind]) : ""; }
  // `inline` = ONE horizontal line ("Halloween special · Limited qty") for organizer tiles:
  // it never touches the photo (Albert 2026-10-05). It may still wrap on a very narrow
  // tile; it is never nowrap, because the strap grid's 1fr columns grow to fit
  // unbreakable text, and that would SHRINK the photos in the other columns.
  function specialLabel(kind, extraClass, inline) {
    var name = specialName(kind);
    if (!name) return null;
    var qty = T("special_limited", "Limited qty");
    if (inline) {
      // Two phrases side by side, told apart by weight and a CSS gap (no dot: a dot
      // either starts a line or makes the first phrase too wide on narrow desktop
      // tiles). A narrow tile puts "Limited qty" on the next line, never mid-phrase.
      return h("span", { class: "acfg__special" + (extraClass ? " " + extraClass : "") }, [
        h("span", { class: "acfg__special-name", text: name }),
        " ",
        h("span", { class: "acfg__special-qty", text: qty })
      ]);
    }
    return h("span", { class: "acfg__special" + (extraClass ? " " + extraClass : "") }, [
      h("span", { class: "acfg__special-name", text: name }),
      h("span", { class: "acfg__special-qty", text: qty })
    ]);
  }
  // Lightweight funnel telemetry. Pushes to window.dataLayer (GTM/GA4) and mirrors
  // each event as a DOM CustomEvent ("acfg:track") for any other listener (Shopify
  // pixels, heatmaps, ...). Fire-and-forget: never throws, never blocks the UI,
  // carries no PII — product title, step titles and totals only.
  function track(event, data) {
    var payload = { event: "acfg_" + event };
    if (data) { for (var k in data) { if (Object.prototype.hasOwnProperty.call(data, k)) payload[k] = data[k]; } }
    try { (window.dataLayer = window.dataLayer || []).push(payload); } catch (e) {}
    try { document.dispatchEvent(new CustomEvent("acfg:track", { detail: payload })); } catch (e) {}
  }

  /* i18n: strings arrive translated via the settings island (settings.strings, emitted
     by the section from the theme locale files). T() falls back to the built-in English
     literal so a missing key can never blank the UI. */
  var STRINGS = {};
  function T(key, fallback) { var s = STRINGS[key]; return (s == null || s === "") ? fallback : s; }
  function TF(key, fallback, vars) { var s = T(key, fallback); for (var k in vars) { if (Object.prototype.hasOwnProperty.call(vars, k)) s = s.replace(new RegExp("%\\{" + k + "\\}", "g"), String(vars[k])); } return s; }

  // Set el's background-image to the FIRST candidate URL that actually loads, so
  // a missing/404 asset never leaves a blank "text-only" tile. Sets the first
  // candidate optimistically (a 404 background simply shows nothing — no broken
  // icon), then probes each in turn and advances on error; when every candidate
  // fails, onAllFail() applies a non-photo fallback (e.g. a procedural pattern).
  // Guarded for environments without Image() (the node test shim) — there it just
  // keeps the optimistic value, which is all those tests inspect.
  function bgLoadFirst(el, urls, onAllFail) {
    urls = (urls || []).filter(Boolean);
    if (!urls.length) { if (onAllFail) onAllFail(); return; }
    function run() {
      var i = 0;
      (function attempt() {
        var url = urls[i++];
        el.style.backgroundImage = "url('" + url + "')";
        if (typeof Image !== "function") return; // no probe available -> keep optimistic
        var probe = new Image();
        probe.onload = function () {};
        probe.onerror = function () { if (i < urls.length) attempt(); else if (onAllFail) onAllFail(); };
        probe.src = url;
      })();
    }
    // Lazy: hidden-ancestor CSS backgrounds are never fetched by the browser, but the
    // probe Image() bypassed that — forcing ~750KB of splitter/braid photos while the
    // visitor was still on step 1. Defer the whole load until the tile approaches the
    // viewport; display:none tiles (hidden wizard steps) never intersect until shown.
    if (typeof IntersectionObserver !== "function") { run(); return; }
    var io = new IntersectionObserver(function (entries) {
      for (var k = 0; k < entries.length; k++) {
        if (entries[k].isIntersecting) { io.disconnect(); run(); return; }
      }
    }, { rootMargin: "200px" });
    io.observe(el);
  }

  /* Probe-first chain for photo swaps (Fermata stage/About): apply(url) fires
   * for the first URL that actually loads, apply(null) when none does; guard()
   * is re-checked at every async step so a stale selection never applies.
   * Failed URLs are memoized — otherwise a missing file (Graphite2 pending)
   * would re-fetch its 404 on every state change. */
  var probeBad = {};
  function probeFirst(urls, guard, apply) {
    urls = (urls || []).filter(Boolean);
    (function attempt(n) {
      while (n < urls.length && probeBad[urls[n]]) n++;
      if (n >= urls.length) { if (guard()) apply(null); return; }
      if (typeof Image !== "function") { if (guard()) apply(urls[n]); return; }
      var probe = new Image();
      probe.onload = function () { if (guard()) apply(urls[n]); };
      probe.onerror = function () { probeBad[urls[n]] = true; attempt(n + 1); };
      probe.src = urls[n];
    })(0);
  }

  /* --------------------- share-link encode / decode ----------------------- *
   * The whole build travels IN the link (?acfg=…) — no backend, no storage.
   * UTF-8-safe base64 (the catalog has a U+2028 artifact in some termination
   * labels, which plain btoa would choke on) mapped onto the URL-safe alphabet
   * with padding stripped: shorter than percent-encoding the standard alphabet
   * and needs no %-escaping inside a query string. _consumeShareParam runs the
   * exact inverse on load. */
  var MAX_ACFG = 8192; // hard cap on the encoded ?acfg= length (legit max ~1.5k); anything longer is swallowed.
  function b64urlEncode(str) {
    var b64 = btoa(unescape(encodeURIComponent(str))); // string -> UTF-8 bytes -> base64
    return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }
  function b64urlDecode(s) {
    var b64 = String(s).replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "="; // restore stripped padding
    return decodeURIComponent(escape(atob(b64))); // base64 -> UTF-8 bytes -> string
  }
  // Read / drop a single query param without URLSearchParams (older in-app
  // browsers); operates on a raw "?a=b&c=d" search string.
  function getQueryParam(search, key) {
    var q = String(search || "").replace(/^\?/, "");
    if (!q) return null;
    var pairs = q.split("&");
    for (var i = 0; i < pairs.length; i++) {
      if (!pairs[i]) continue;
      var eq = pairs[i].indexOf("=");
      if ((eq === -1 ? pairs[i] : pairs[i].slice(0, eq)) === key) return eq === -1 ? "" : pairs[i].slice(eq + 1);
    }
    return null;
  }
  function dropQueryParam(search, key) {
    var q = String(search || "").replace(/^\?/, "");
    if (!q) return "";
    var kept = [];
    q.split("&").forEach(function (pair) {
      if (!pair) return;
      var eq = pair.indexOf("=");
      if ((eq === -1 ? pair : pair.slice(0, eq)) !== key) kept.push(pair);
    });
    return kept.length ? "?" + kept.join("&") : "";
  }

  /* ------------------------------ classification -------------------------- */
  function roleOf(opt) {
    var f = lc(opt.globo_field);
    var t = opt.type;
    if (t === "swatch") return "colour";
    if (t === "tiles") {
      if (/braid/.test(f)) return "braiding";
      if (/split/.test(f)) return "splitter";
      if (/strap/.test(f)) return "strap";
      return "generic";
    }
    if (t === "dropdown" || t === "select") {
      if (/length/.test(f)) return "length";
      // "Socket End" = the adapter cable's female end: termination role WITHOUT the headphone special case.
      if (/termination|terminations|socket end/.test(f)) return "termination";
      return "generic";
    }
    return "generic";
  }

  /* ============================ Configurator ============================= */
  function Configurator(root) {
    this.root = root;
    this.cfg = parseJSONScript(root, "[data-acfg-config]");
    this.assets = parseJSONScript(root, "[data-acfg-assets]") || {};
    this.settings = parseJSONScript(root, "[data-acfg-settings]") || {};
    // Shopify's `t` filter HTML-escapes locale values (can't → can&#39;t). Decode once
    // via a detached <textarea> (entities only — nothing executes) so textContent and
    // aria attributes carry the real characters.
    STRINGS = {};
    var rawStrings = this.settings.strings || {};
    var strDecoder = document.createElement("textarea");
    for (var strKey in rawStrings) {
      if (Object.prototype.hasOwnProperty.call(rawStrings, strKey)) {
        strDecoder.innerHTML = rawStrings[strKey];
        STRINGS[strKey] = strDecoder.value;
      }
    }
    // Per-option copy carried INSIDE the config island (pro/adapter title/question/help, emitted
    // with `| t | json`) is HTML-escaped the same way — decode it here so a French/Italian
    // apostrophe never prints as &#39; (2026-09-15).
    if (this.cfg && Array.isArray(this.cfg.options)) {
      this.cfg.options.forEach(function (o) {
        ["title", "question", "help"].forEach(function (k) {
          if (o && typeof o[k] === "string" && o[k].indexOf("&") !== -1) { strDecoder.innerHTML = o[k]; o[k] = strDecoder.value; }
        });
      });
    }
    this.baseVariantId = root.getAttribute("data-acfg-base-variant") || "";
    this.productTitle = clean(root.getAttribute("data-acfg-product-title")); // change 9: model name for summary + _model line property
    this.currency = this.settings.currency_symbol || "€";
    this.series = lc((this.cfg && this.cfg.series) || ""); // "aranea" (braided) | "medusa" (sleeved); drives the colour model
    // v4 (ProAudio): optional config keys. "kind" ("instrument" | "microphone";
    // "" = headphone) reshapes wording-level behaviour only — cart contract
    // untouched. colour_families:false hides the family filter (the pro paracord
    // palette has its own numbering; legacy family membership would mislabel it).
    this.kind = lc((this.cfg && this.cfg.kind) || "");
    this.colourFamilies = !(this.cfg && this.cfg.colour_families === false);

    if (!this.cfg || !Array.isArray(this.cfg.options) || !this.cfg.options.length) {
      root.appendChild(h("div", { class: "acfg__error", text: T("error_data_unavailable", "Configurator data unavailable.") }));
      return;
    }

    // Prefer the REAL base-variant price (emitted by the section as data-acfg-base-price) so the
    // displayed base always equals what /cart/add.js charges for baseVariantId. Fall back to the
    // config blob's base_price, and never silently invent a price.
    var attrBasePrice = parseFloat(root.getAttribute("data-acfg-base-price"));
    this.basePrice = !isNaN(attrBasePrice) ? attrBasePrice
      : (typeof this.cfg.base_price === "number" ? this.cfg.base_price : 0);
    this.options = this.cfg.options.slice();
    this.controllers = [];
    this.state = {};
    this.submitting = false;
    this._shareLabel = T("share_this_build", "Share this build"); // resting label for the copy-link button(s) (restored after the "Link copied" flash)
    this._shareWidgets = [];               // {btn,label} per action bar (commit + wizard); both flash together
    this._sampleLabel = T("request_a_sample", "Request a sample"); // resting label for the sample button(s) (restored after a flash)
    this._sampleWidgets = [];              // {root,btn,form,email,hp,meta,status} per action bar; state helpers act on all
    this._sampleSubmitting = false;
    this.customerEmail = clean(root.getAttribute("data-acfg-customer-email") || ""); // logged-in email, "" when logged out

    this._prepare();
    this._initState();
    this.viewMode = this._loadViewMode();
    this.step = 0;
    this.maxStep = 0; // furthest step reached — drives the monotonic progress fill
    // Fail-safe: a presentation error must NEVER white-screen the page (the rest of
    // the product page is independent of this build). On any render error, fall back
    // to a quiet inline message and keep going.
    try {
      this._build();
      this._update();
      track("view", { product: this.productTitle, mode: this.viewMode });
    } catch (e) {
      if (typeof console !== "undefined" && console.error) console.error("[acfg] render failed", e);
      try {
        var fbMount = this.root.querySelector("[data-acfg-mount]") || this.root;
        fbMount.innerHTML = "";
        fbMount.appendChild(h("div", { class: "acfg__error", text: T("error_render_failed", "The configurator couldn't load here. Please refresh, or contact us and we'll build it with you.") }));
      } catch (e2) {}
    }
  }

  Configurator.prototype._prepare = function () {
    var self = this;
    this.options.forEach(function (o) { o._role = roleOf(o); });

    // Colour layout detection. The Y-cable canonical topology is four strand slots
    // that resolve to Left1/Left2/Right1/Right2. We detect it by field PATTERN (not
    // an exact field-name set) so it also catches the migration artifact where the
    // 4th field leaked as a raw id — e.g. the Abyss set's "image-dropdown-4" IS
    // Color Right 2. buildPayload still emits each slot's own globo_field untouched.
    this.colourOpts = this.options.filter(function (o) { return o._role === "colour"; });
    // v4: derive the working palette from the FIRST colour option's values. Only
    // overrides the built-in 28 when the data itself carries hex/image — legacy
    // option-sets (bare "1 Black" labels) keep the hardcoded palette and photos.
    PAL_OVERRIDE = null;
    this.paletteNums = [];
    this.paletteSpecial = {}; // colour number -> special kind (seasonal label), from the data
    var palVals = this.colourOpts.length ? this.colourOpts[0].values : [];
    var palCustom = false;
    for (var pv = 0; pv < palVals.length; pv++) if (palVals[pv].hex || palVals[pv].image) { palCustom = true; break; }
    for (var pn = 0; pn < palVals.length; pn++) {
      var pnum = colourNumber(palVals[pn].label);
      if (pnum > 0 && this.paletteNums.indexOf(pnum) === -1) this.paletteNums.push(pnum);
      if (pnum > 0 && palVals[pn].special) this.paletteSpecial[pnum] = String(palVals[pn].special);
    }
    if (!this.paletteNums.length) for (var pd = 1; pd <= 29; pd++) this.paletteNums.push(pd);
    if (palCustom) {
      PAL_OVERRIDE = {};
      for (var pj = 0; pj < palVals.length; pj++) {
        var pvv = palVals[pj], pnn = colourNumber(pvv.label);
        if (!pnn) continue;
        PAL_OVERRIDE[pnn] = {
          name: clean(pvv.label).replace(/^\d+\s*/, "") || (PALETTE[pnn] || "Colour"),
          hex: pvv.hex || COLOUR_HEX[String(pnn)] || "",
          image: pvv.image || null
        };
      }
    }
    var firstColour = function (re) {
      for (var i = 0; i < self.colourOpts.length; i++) if (re.test(lc(self.colourOpts[i].globo_field))) return self.colourOpts[i];
      return null;
    };
    var yl1 = firstColour(/color\s*left\s*1\b/), yl2 = firstColour(/color\s*left\s*2\b/),
        yr1 = firstColour(/color\s*right\s*1\b/), yr2 = firstColour(/color\s*right\s*2\b/);
    if (this.colourOpts.length === 4 && yl1 && yl2 && yr1 && !yr2) {
      // 4th slot is the leftover (a leaked-id Right 2, e.g. image-dropdown-4).
      yr2 = this.colourOpts.filter(function (o) { return o !== yl1 && o !== yl2 && o !== yr1; })[0] || null;
    }
    this.canonicalColour = !!(this.colourOpts.length === 4 && yl1 && yl2 && yr1 && yr2);
    this.colourOrder = this.canonicalColour ? [yl1, yl2, yr1, yr2] : this.colourOpts.slice();

    // Medusa (sleeved) split topology: three single colour slots — Base, Left, Right —
    // with a live cable preview. Detected by field pattern so it works even if the
    // series flag is missing; the length===3 + !canonical guard means it never
    // collides with the 4-slot Aranea model or a single-colour set.
    var mBase = firstColour(/base/), mLeft = firstColour(/\bleft\b/), mRight = firstColour(/\bright\b/);
    this.medusaSplit = !this.canonicalColour && this.colourOpts.length === 3 && !!(mBase && mLeft && mRight);
    if (this.medusaSplit) {
      this.medusaSlots = { base: mBase, left: mLeft, right: mRight };
      this.colourOrder = [mBase, mLeft, mRight]; // Base first (the trunk), then Left, Right
    }

    // Section order (README §3.3): Length, Braidings, Colours (merged count +
    // arrangement + palette), Splitter, then each remaining option (terminations
    // + generics) in data order, with Strap LAST.
    this.sectionPlan = [];
    var lengthOpts = this.options.filter(function (o) { return o._role === "length"; });
    var braid = this.options.filter(function (o) { return o._role === "braiding"; });
    var split = this.options.filter(function (o) { return o._role === "splitter"; });
    var strap = this.options.filter(function (o) { return o._role === "strap"; });
    var rest = this.options.filter(function (o) {
      return ["length", "colour", "braiding", "splitter", "strap"].indexOf(o._role) === -1;
    });
    lengthOpts.forEach(function (o) { self.sectionPlan.push({ kind: "length", opt: o, title: o.title || T("title_length", "Length") }); });
    braid.forEach(function (o) { self.sectionPlan.push({ kind: "braiding", opt: o, title: T("title_braidings", "Braidings") }); });
    if (this.colourOpts.length) this.sectionPlan.push({ kind: "colour", opts: this.colourOpts, title: this.colourOpts[0].title || T("title_colours", "Colours") });
    split.forEach(function (o) { self.sectionPlan.push({ kind: "splitter", opt: o, title: T("title_splitter", "Splitter") }); });
    rest.forEach(function (o) {
      // Termination = the connector that plugs into the amp / DAC (the source end).
      // The headphone-end termination is a separate, brand-only choice (the connector
      // type is fixed by the headphone). Both keep the two-level builder but get
      // their own title/help; the cart keys (globo_fields) are untouched.
      if (o._role === "termination") {
        if (/head\s*phone|earcup|\bhp\b/.test(lc(o.globo_field))) {
          self.hpTermOpt = self.hpTermOpt || o; // finish-matching: the headphone end (see _setAmpTermination)
          self.sectionPlan.push({ kind: "termination", opt: o, title: o.title || T("title_headphone_connector", "Headphone connector"), help: o.help || T("hp_help", "The connector is fixed for this model — choose the metal finish.") });
        } else {
          self.ampTermOpt = self.ampTermOpt || o; // finish-matching: the amp/source end
          // Argentea: its own hint (every plug is Furutech, dearer ones add only the difference).
          // Read from the decoded strings island, never from the config JSON (the `t` filter
          // HTML-escapes and the config island is not entity-decoded).
          self.sectionPlan.push({ kind: "termination", opt: o, title: o.title || T("title_amplifier_connector", "Amplifier connector"), help: o.help || (self.series === "argentea" ? T("amp_help_argentea", "Furutech at every option. The 3.5 mm plug is included; a dearer plug adds only the difference in connector price.") : T("amp_help", "The plug that connects to your amplifier or DAC.")) });
        }
      } else {
        self.sectionPlan.push({ kind: "generic", opt: o, title: o.title || fieldTitle(o.globo_field) });
      }
    });
    // Strap goes LAST (Albert 2026-07-26): the accessory closes the build,
    // after every cable-defining choice.
    strap.forEach(function (o) { self.sectionPlan.push({ kind: "strap", opt: o, title: T("title_strap", "Organizer") }); });
  };

  function fieldTitle(field) {
    var f = clean(field);
    if (/^image-dropdown/i.test(f)) return T("option_title", "Option"); // leaked raw id
    // Field renamed to Organizer (localized); the order-property key stays "Strap"
    // (English house style, and the role regex matches /strap/).
    if (/strap/i.test(f)) return T("title_strap", "Organizer");
    return f;
  }

  Configurator.prototype._initState = function () {
    var self = this;
    this.options.forEach(function (o) {
      var labels = o.values.map(function (v) { return v.label; });
      var def = o.default;
      // Resolve default robustly: some Globo defaults aren't in the option's
      // own values (template artifact). Fall back to the first value so a
      // required field is always satisfied and the cart is always valid.
      self.state[o.id] = (def != null && def !== "" && labels.indexOf(def) !== -1) ? def : (labels[0] || "");
      // Length steps open on the 1.5m stop when the product has one (Albert
      // 2026-07-14). The Globo data default is the 1m first value on every
      // product — an export artifact, not a merchandising choice — so 1.5m
      // wins over it. ?acfg= prefills (share links / cart edits) apply after
      // init and still overwrite this.
      // Adapter cables open on their shortest (included) stop instead — the €45 "from" price.
      if (o._role === "length" && self.kind !== "adapter") {
        for (var li = 0; li < o.values.length; li++) {
          if (metresOf(o.values[li].label) === 1.5) { self.state[o.id] = o.values[li].label; break; }
        }
      }
    });
    // UI-only state (never enters the cart payload).
    this.state._family = "all";
    // Finish-matching (Furutech both ends): reset with the rest of the state.
    this._hpTouched = false;    // customer explicitly chose the headphone end — never auto-change it
    this._hpAutoPrev = null;    // hp label to restore if the amp end leaves Furutech
    this._hpAutoLabel = null;   // the hp label WE auto-set (only ever undo our own change)
    this.state._hpMatchedNote = false;
    this.state._lengthUnit = this._loadLengthUnit();
    this.state._activePaletteIdx = 0;
    this.state._activeColour = this.colourOrder.length ? this.colourOrder[0].id : null;
    // Canonical Y-cable: derive the friendly count/arrangement/palette model from
    // the four default strand labels (inverse of the resolve used while editing).
    if (this.canonicalColour) this._inferColourModel();
  };

  /* ------------------------------ value helpers --------------------------- */
  Configurator.prototype.valueByLabel = function (opt, label) {
    for (var i = 0; i < opt.values.length; i++) if (opt.values[i].label === label) return opt.values[i];
    return opt.values[0];
  };
  // The value that drives price + variant + cart for an option: the standard,
  // discrete value whose label matches current state.
  Configurator.prototype.selectedValue = function (opt) {
    return this.valueByLabel(opt, this.state[opt.id]);
  };
  // A length renders as a slider when every value parses to metres, else a list.
  Configurator.prototype._lengthParsable = function (opt) {
    return opt.values.every(function (v) { return metresOf(v.label) != null; });
  };
  Configurator.prototype.total = function () {
    var self = this, t = this.basePrice;
    this.options.forEach(function (o) { t += (self.selectedValue(o).addon || 0); });
    return t;
  };
  Configurator.prototype.swatchSrc = function (num) {
    // v4: an override palette supplies its own photo per colour — or none yet, in
    // which case the hex behind the (absent) image paints the swatch. Never fall
    // through to the legacy 28 photos: they are the wrong material.
    if (PAL_OVERRIDE && PAL_OVERRIDE[num]) return PAL_OVERRIDE[num].image || "";
    var arr = this.assets.swatches || [];
    return arr[num - 1] || this.assets.placeholder || "";
  };
  // Real colour hexes (1..29) — painted BEHIND the swatch photo so a slow or missing
  // image never shows a grey box. Mirrors the design palette (shared.jsx).
  var COLOUR_HEX = { "1":"#0A0A0A","2":"#4A4A4A","3":"#D4D4D4","4":"#F5F5F5","5":"#0D1729","6":"#3A2E15","7":"#6B4423","8":"#6E1F2A","9":"#C8232C","10":"#FF8C00","11":"#F29030","12":"#F5C91A","13":"#B88A25","14":"#7A6020","15":"#6B6C7E","16":"#1A3FB8","17":"#2EABD0","18":"#A99BC8","19":"#6B21A8","20":"#D83870","21":"#D68BC0","22":"#C0D2E0","23":"#B5DCE0","24":"#2EB3A0","25":"#8C9555","26":"#2A3E2B","27":"#4EA82A","28":"#B8E62A","29":"#E4E8DA" };
  Configurator.prototype.colourHex = function (num) {
    if (PAL_OVERRIDE && PAL_OVERRIDE[num] && PAL_OVERRIDE[num].hex) return PAL_OVERRIDE[num].hex;
    return COLOUR_HEX[String(num)] || "";
  };
  Configurator.prototype.swatchStyle = function (num) {
    var hex = this.colourHex(num), src = this.swatchSrc(num);
    return (hex ? "background-color:" + hex + ";" : "") + (src ? "background-image:url('" + src + "');" : "");
  };

  /* -------------------------------- update -------------------------------- */
  Configurator.prototype.setState = function (patch) { Object.assign(this.state, patch); this._update(); };
  // "Start over" — restore every option to its default and re-render. UI-pref state
  // (view mode, length unit) is preserved.
  Configurator.prototype.reset = function () {
    track("reset", { product: this.productTitle });
    // Start over declares "this is a NEW build" — disarm replace-on-edit. Without
    // this, Edit → Start over → Add silently zeroed the previously added cable's
    // cart lines (audit 2026-07-14 #1). Worst case now is the old behaviour:
    // both builds sit visibly in the cart.
    this._pendingReplaceKeys = null;
    this._lastAddKeys = null;
    this._initState();
    this.step = 0; this.maxStep = 0;
    this._build();
    this._update();
  };
  Configurator.prototype._update = function () {
    var self = this;
    this.controllers.forEach(function (c) { c.update(self.state); });
    var total = money(this.total(), this.currency);
    var changed = this._lastTotal != null && this._lastTotal !== total;
    this._lastTotal = total;
    if (this.elPriceTotal) this.elPriceTotal.textContent = total;
    if (this.elCtaPrice) this.elCtaPrice.textContent = total;
    if (this.elCtaPriceMain) this.elCtaPriceMain.textContent = total;
    if (this.elBarTotal) this._animateTotal(this.total());
    if (this.elSpecBar) this.elSpecBar.textContent = this._specSummary();
    this._renderBarSwatches();
    this._refreshHeadPills();
    if (this.elBreakdown) this._renderBreakdown();
    if (changed) this._pulsePrice();
    // Announce the new total to screen readers, debounced so mid-drag slider changes
    // collapse to one announcement (the visible total animates per-frame — aria-live
    // there would spam). elSrTotal is a visually-hidden role=status div in _build.
    if (changed && this.elSrTotal) {
      var srEl = this.elSrTotal, srText = T("total", "Total") + " " + total;
      if (this._srTotalTimer) clearTimeout(this._srTotalTimer);
      this._srTotalTimer = setTimeout(function () { srEl.textContent = srText; }, 900);
    }
    this._saveBuild();
    this._syncStrapMedia();
  };

  /* ---- section-head value pills (current selection, live) ----------------- *
   * Cosmetic only — mirrors the prototype's per-section value chip (e.g. "Spiral",
   * "None", "3.5mm Jack"). Each builder may register a {pill, fn} via _headValue;
   * _refreshHeadPills() repaints them from current state on every _update(). The
   * cart payload never reads these. */
  Configurator.prototype._headValue = function (sec, fn) {
    var head = sec.querySelector(".acfg__section-head");
    if (!head) return;
    var pill = h("span", { class: "acfg__section-value" });
    head.appendChild(pill);
    (this._headPills = this._headPills || []).push({ pill: pill, fn: fn });
  };
  Configurator.prototype._refreshHeadPills = function () {
    var a = this._headPills || [];
    for (var i = 0; i < a.length; i++) {
      var v = "";
      try { v = a[i].fn() || ""; } catch (e) { v = ""; }
      a[i].pill.textContent = v;
      a[i].pill.style.display = v ? "" : "none";
    }
  };

  /* ---- price breakdown popover (cosmetic; pure read of the current selection) ---- */
  Configurator.prototype._renderBreakdown = function () {
    if (!this.elBreakdown) return;
    var self = this, rows = [];
    rows.push(h("div", { class: "acfg__breakdown-head", text: T("your_build", "Your build") }));
    rows.push(h("div", { class: "acfg__breakdown-row" }, [h("span", { class: "acfg__breakdown-name", text: T("base_price", "Base price") }), h("span", { class: "acfg__breakdown-amt", text: money(this.basePrice, this.currency) })]));
    this.options.forEach(function (o) {
      var v = self.selectedValue(o), add = v.addon || 0;
      if (add > 0) rows.push(h("div", { class: "acfg__breakdown-row" }, [h("span", { class: "acfg__breakdown-name", text: fieldTitle(o.globo_field) + " · " + clean(v.label) }), h("span", { class: "acfg__breakdown-amt", text: "+" + money(add, self.currency) })]));
    });
    // Included-value rows: a default build used to read "Base price -> Total" with
    // nothing in between — the customer saw what they pay and none of what they get.
    function includedRow(name) {
      return h("div", { class: "acfg__breakdown-row" }, [h("span", { class: "acfg__breakdown-name", text: name }), h("span", { class: "acfg__breakdown-amt is-included", text: T("included_in_base", "Included in base price") })]);
    }
    // Braiding/palette row is Aranea-only — a sleeved Medusa cable has no braid
    // (same gate as the sample flow; review 2026-07-20). The craft row is universal.
    if (this.kind && this.kind !== "adapter") rows.push(includedRow(T("included_row_palette_pro", "Custom paracord colour palette")));
    // Argentea (top tier): no colours/braiding to include — the value rows are the wire, the
    // Furutech ends and the leather splitter, all in the base price (2026-09).
    else if (this.series === "argentea") {
      rows.push(includedRow(T("included_row_argentea_wire", "Silver-plated copper, 8-conductor braid")));
      rows.push(includedRow(T("included_row_argentea_plugs", "Furutech connectors at both ends")));
      rows.push(includedRow(T("included_row_argentea_splitter", "Laegas leather splitter")));
    }
    else if (!(this.series === "medusa" || this.medusaSplit)) rows.push(includedRow(TF("included_row_palette", "Braiding & %{n}-colour palette", { n: (this.paletteNums || []).length || 29 })));
    if (this.kind === "instrument") rows.push(includedRow(T("included_row_plugs_instrument", "Neutrik 6.35mm jacks & Sommer Spirit XXL core")));
    if (this.kind === "microphone") rows.push(includedRow(T("included_row_plugs_microphone", "Neutrik XLR pair & Mogami core")));
    if (this.kind === "adapter") rows.push(includedRow(T("included_row_plugs_adapter", "Rean 3.5mm plug, 3.5mm socket & braided OFC core")));
    rows.push(includedRow(T("included_row_craft", "Hand-built & tested in the EU")));
    rows.push(h("div", { class: "acfg__breakdown-row acfg__breakdown-total" }, [h("span", { class: "acfg__breakdown-name", text: T("total", "Total") }), h("span", { class: "acfg__breakdown-amt", text: money(this.total(), this.currency) })]));
    // Tax/VAT note (settings.price_note) — rendered with the total it qualifies.
    if (this.settings.price_note) rows.push(h("div", { class: "acfg__price-note", text: this.settings.price_note }));
    this.elBreakdown.innerHTML = "";
    for (var i = 0; i < rows.length; i++) this.elBreakdown.appendChild(rows[i]);
  };

  /* Smoothly count the bar total up/down to its new value (easeOutCubic). No-op on
   * first paint and under prefers-reduced-motion. Cosmetic only. */
  Configurator.prototype._animateTotal = function (toNum) {
    var self = this, el = this.elBarTotal;
    if (!el) return;
    var from = (this._displayTotal == null) ? toNum : this._displayTotal;
    this._displayTotal = toNum;
    var reduce = false;
    try { reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) {}
    if (from === toNum || reduce || typeof requestAnimationFrame !== "function") { el.textContent = money(toNum, this.currency); return; }
    if (this._totalRAF) { try { cancelAnimationFrame(this._totalRAF); } catch (e) {} }
    var start = null, dur = 380;
    function step(ts) {
      if (start == null) start = ts;
      var p = Math.min(1, (ts - start) / dur), eased = 1 - Math.pow(1 - p, 3);
      el.textContent = money(Math.round(from + (toNum - from) * eased), self.currency);
      if (p < 1) self._totalRAF = requestAnimationFrame(step);
      else el.textContent = money(toNum, self.currency);
    }
    this._totalRAF = requestAnimationFrame(step);
  };

  // Live colour-palette preview for the sticky bar (a few swatch squares). Pure read
  // of the current selection; cosmetic only.
  Configurator.prototype._colourPreviewNums = function () {
    var self = this;
    if (this.canonicalColour) {
      var n = Math.max(1, this.state._numColours || 1);
      return (this.state._palette || [1]).slice(0, n);
    }
    var out = [];
    this.colourOrder.forEach(function (o) {
      var num = colourNumber(self.state[o.id]);
      if (out.indexOf(num) === -1) out.push(num);
    });
    return out;
  };
  Configurator.prototype._renderBarSwatches = function () {
    if (!this.elBarSwatches) return;
    var nums = this._colourPreviewNums().slice(0, 4);
    this.elBarSwatches.innerHTML = "";
    for (var i = 0; i < nums.length; i++) {
      this.elBarSwatches.appendChild(h("span", { class: "acfg__bar-swatch", title: paletteName(nums[i]),
        style: this.swatchStyle(nums[i]) }));
    }
    this.elBarSwatches.style.display = nums.length ? "" : "none";
  };
  // Brief pulse on the running price when the total changes (re-triggered via reflow;
  // the CSS keyframe is disabled under prefers-reduced-motion).
  Configurator.prototype._pulsePrice = function () {
    var els = [this.elPriceTotal, this.elBarTotal];
    for (var i = 0; i < els.length; i++) {
      var el = els[i]; if (!el || !el.classList) continue;
      el.classList.remove("acfg__pulse");
      try { void el.offsetWidth; } catch (e) {}
      el.classList.add("acfg__pulse");
    }
  };
  // Pad the scrollable column so its last content clears the sticky bar (bottom
  // padding ≈ bar height). offsetHeight is 0 under the headless shim -> no padding,
  // which is harmless.
  Configurator.prototype._syncBarSpacing = function () {
    if (!this.elMain || !this.elBar) return;
    var hh = 0;
    try { hh = this.elBar.offsetHeight || 0; } catch (e) {}
    this.elMain.style.paddingBottom = (hh ? hh + 20 : 0) + "px";
  };

  /* --------------------------------- build -------------------------------- *
   * Two-column shell: a sticky product-photo MEDIA pane (left) + the configurator
   * MAIN column (right: view toggle, running price, progress, the steps). A sticky
   * BAR spans the full width below — live spec summary + wizard nav / commit. On
   * narrow viewports the CSS stacks media over main. */
  Configurator.prototype._build = function () {
    var self = this;
    // The bar's height changes when the spec line wraps (rotate/resize) — keep the
    // main column's clearance in sync. Hooked once per instance; survives reset().
    if (!this._resizeHooked && typeof window !== "undefined" && window.addEventListener) {
      this._resizeHooked = true;
      var rsT = null;
      window.addEventListener("resize", function () {
        if (rsT) clearTimeout(rsT);
        rsT = setTimeout(function () { rsT = null; self._syncBarSpacing(); }, 150);
      });
    }
    var mount = this.root.querySelector("[data-acfg-mount]") || this.root;
    mount.innerHTML = "";
    this.controllers = []; // rebuilt fresh below — clear first so reset()/re-build doesn't append a second, orphaned set (would blank the wizard)
    this._shareWidgets = []; // rebuilt fresh below — one share button per action bar
    this._sampleWidgets = []; // rebuilt fresh below — one sample widget per action bar (when sample-eligible)
    this._headPills = []; // rebuilt fresh below — section-head value chips (cosmetic)

    var main = h("div", { class: "acfg__main" });
    this.elMain = main; // padded to clear the sticky bar (see _syncBarSpacing)

    // v3 hero split: clone the "Make it yours" configure block + compatibility card
    // (the liquid section's hidden source) into the TOP of the form column.
    this._cloneChromeInto(main, "[data-acfg-chrome-main]");

    // View toggle row ("All options" | "Step by step"), right-aligned.
    main.appendChild(h("div", { class: "acfg__toolbar" }, [this._buildViewToggle()]));

    // Running total lives ONLY in the sticky bar now — no duplicate price header in
    // the form column. elPriceTotal is intentionally absent; _update and _pulsePrice
    // already guard for it, so the bar total is the single on-screen price.

    // Wizard progress (step-by-step mode only).
    main.appendChild(this._buildProgress());

    // Sections (one per logical option group; these are also the wizard steps).
    var sectionsWrap = h("div", { class: "acfg__sections" });
    this.elSectionsWrap = sectionsWrap;
    this.sectionPlan.forEach(function (plan, i) {
      var num = i + 1;
      var ctrl;
      if (plan.kind === "length") ctrl = self._buildLength(plan.opt, num, plan.title);
      else if (plan.kind === "colour") ctrl = self._buildColours(plan.opts, num, plan.title);
      else if (plan.kind === "braiding") ctrl = self._buildBraiding(plan.opt, num, plan.title);
      else if (plan.kind === "splitter") ctrl = self._buildSplitter(plan.opt, num, plan.title);
      else if (plan.kind === "strap") ctrl = self._buildStrap(plan.opt, num, plan.title);
      else if (plan.kind === "termination") ctrl = self._buildTermination(plan.opt, num, plan.title, plan.help);
      else ctrl = self._buildGeneric(plan.opt, num, plan.title);
      if (ctrl) { self.controllers.push(ctrl); sectionsWrap.appendChild(ctrl.el); }
    });
    main.appendChild(sectionsWrap);

    // In-flow PRIMARY pair — DIRECTLY under the options, ABOVE the build summary
    // (Albert 2026-07-05: don't make people scroll past "Your build" to hit Next).
    // In wizard mode this pair IS the step nav: the primary reads "Next" (advances)
    // and the secondary (the Buy-it-now slot) reads "Back"; on the FINAL step they
    // morph into the real commits — "Add to cart · total" and "Buy it now" (the
    // express path: same add, add-ons included, then straight to /checkout where
    // the wallets live — native wallet widgets can't be embedded here, they'd buy
    // the bare variant and skip every priced add-on line). In See-all mode they are
    // always the commits. _applyView owns the morphing; handlers route by mode+step.
    this.elCtaMainLabel = h("span", { text: this.settings.cta_label || T("add_to_cart", "Add to cart") });
    this.elCtaMainDot = h("span", { class: "acfg__cta-dot", "aria-hidden": "true", text: "·" });
    this.elCtaPriceMain = h("span", { class: "acfg__cta-price", text: money(this.total(), this.currency) });
    this.elAddBtnMain = btn({ class: "acfg__cta acfg__cta--add", on: { click: function () {
      if (self.viewMode === "wizard" && self.step < self.sectionPlan.length - 1) self._goStep(1);
      else self.addToCart();
    } } }, [this.elCtaMainLabel, this.elCtaMainDot, this.elCtaPriceMain]);
    this.elBuyNowLabel = h("span", { text: T("buy_it_now", "Buy it now") });
    this.elBuyNowBtn = btn({ class: "acfg__cta acfg__cta--buynow", on: { click: function () {
      if (self.viewMode === "wizard" && self.step < self.sectionPlan.length - 1) self._goStep(-1);
      else self.addToCart({ checkout: true });
    } } }, [this.elBuyNowLabel]);
    // The final step still needs a way back once the secondary has become
    // "Buy it now": a subtle text link under the pair.
    this.elInflowBack = btn({ class: "acfg__inflow-back", on: { click: function () { self._goStep(-1); } } }, "← " + T("back", "Back"));
    this.elMainStatus = h("div", { class: "acfg__status acfg__status--inflow", role: "status" });
    // Per-visitor shipping cost line (settings island `shipping_line`, computed
    // server-side by snippets/shipping-line.liquid from the visitor's market
    // country). Sits with the CTAs so checkout stops being the only place the
    // delivery cost is discoverable.
    var shipTxt = T("shipping_line", "");
    var shipLine = shipTxt ? h("p", { class: "acfg__shipline", text: shipTxt }) : null;
    var ctaBlock = h("div", { class: "acfg__cta-block" }, [this.elAddBtnMain, this.elBuyNowBtn, this.elInflowBack, this._buildCtaTrust(), shipLine, this.elMainStatus]);
    main.appendChild(ctaBlock);

    // Visually-hidden live region: _update announces the debounced total here (the
    // visible total animates per-frame — aria-live on it would spam AT).
    this.elSrTotal = h("div", { class: "acfg__sr", role: "status", "aria-live": "polite" });
    main.appendChild(this.elSrTotal);

    // Price breakdown — the static "Your build" summary, BELOW the CTA pair.
    // Repainted on every change in _update.
    this.elBreakdown = h("div", { class: "acfg__breakdown" });
    main.appendChild(this.elBreakdown);

    // End row: "Start over" + "Share this build" + "Request a sample" share ONE
    // horizontal line (they were a tall vertical stack), with the store's payment
    // icons below. Reset stays at the END so it's never hit accidentally mid-build;
    // the sample flow + state sync are unchanged (placement is cosmetic only).
    main.appendChild(h("div", { class: "acfg__endrow" }, [
      btn({ class: "acfg__reset", "aria-label": T("reset_aria", "Reset all options to defaults"), on: { click: function () { self.reset(); } } }, T("start_over", "↺ Start over")),
      this._makeShareWidget("acfg__sharebtn--form"),
      this._sampleEligible() ? this._makeSampleWidget("acfg__samplebtn--form") : null
    ]));
    this._cloneChromeInto(main, "[data-acfg-chrome-commit]"); // store-enabled payment icons (server-rendered)

    // Mount the two-column layout.
    mount.appendChild(h("div", { class: "acfg__layout" }, [this._buildMedia(), main]));

    // Commit bar contents (Add to cart) — "see all" mode only. In wizard mode the
    // in-flow CTA pair above is the ONLY action (Albert 2026-07-05: the bar's
    // duplicate Back/Next read as disconnected), so the bar carries no buttons and
    // stays a compact live summary.
    this.elStatus = h("div", { class: "acfg__status", role: "status" });
    this.elCtaPrice = h("span", { class: "acfg__addbtn-price", text: money(this.total(), this.currency) });
    this.elAddBtn = btn({
      class: "acfg__addbtn",
      on: { click: function () { self.addToCart(); } }
    }, [h("span", { text: this.settings.cta_label || T("add_to_cart", "Add to cart") }), h("span", { class: "acfg__addbtn-dot", text: "·" }), this.elCtaPrice]);
    this.elCommit = h("div", { class: "acfg__commit" }, [this.elAddBtn, this.elStatus]);

    // Sticky action bar (prototype ConfigBar): a live spec strip on the LEFT
    // (colour-palette preview + one-line spec summary) and a RIGHT cluster carrying
    // trust microcopy, the running TOTAL (+ ships note), and — in See-all mode
    // only — the commit. Solid + blurred + elevated so page content can never show
    // through (the CSS owns the surface; this is just structure).
    this.elBarSwatches = h("div", { class: "acfg__bar-swatches", "aria-hidden": "true" });
    this.elSpecBar = h("div", { class: "acfg__bar-spec" });
    this.elBarTotal = h("div", { class: "acfg__bar-total-val", text: money(this.total(), this.currency) });
    var totalCol = [h("span", { class: "acfg__bar-total-label", text: T("total", "Total") }), this.elBarTotal];
    if (this.settings.ships_note) totalCol.push(h("span", { class: "acfg__bar-ships", text: this.settings.ships_note }));
    var barRight = h("div", { class: "acfg__bar-right" }, [
      this._buildTrust(),
      this._buildSocial(),
      h("div", { class: "acfg__bar-total" }, totalCol),
      this.elCommit
    ]);
    var barSummary = h("div", { class: "acfg__bar-summary" }, [
      h("div", { class: "acfg__bar-specwrap" }, [this.elBarSwatches, this.elSpecBar]),
      barRight
    ]);
    this.elBar = h("div", { class: "acfg__bar" }, [barSummary]);
    // NOT mounted (Albert 2026-07-26): the sticky bar duplicated what's already
    // on screen — a second Add to cart, second total, second trust row, second
    // spec line. Built but left detached so every elBar*/elStatus writer stays
    // null-safe and re-mounting is a one-line revert; _syncBarSpacing reads
    // offsetHeight 0 from the detached node, so the reserved bottom padding
    // disappears with it.

    // Recap drawer (appended to <body>/root so it can overlay).
    this._buildDrawer();

    // Apply the persisted view mode + initial step.
    this._applyView();
  };

  /* Clone the hidden [data-acfg-chrome] source nodes (the liquid section renders them
   * OUTSIDE [data-acfg-mount]) into a freshly-built pane. Used to split the hero:
   * series + headline go atop the media pane; the configure block + compatibility
   * card go atop the form column. Cloning (not moving) leaves the source intact so
   * the engine re-clones on every Shopify section re-init. Cosmetic only — these
   * nodes never feed the cart payload. */
  Configurator.prototype._cloneChromeInto = function (target, selector) {
    var src = this.root.querySelector(selector);
    if (!src) return;
    var kids = src.childNodes;
    for (var i = 0; i < kids.length; i++) {
      var node = kids[i];
      if (node.nodeType === 3 && !/\S/.test(node.nodeValue || "")) continue; // skip blank text
      if (node.nodeType === 8) continue; // skip comment nodes
      target.appendChild(node.cloneNode(true));
    }
  };

  /* ------------------------------- media pane ----------------------------- *
   * The product's real photos (left). NEVER a live render. Falls back to the
   * generic cable-main.jpg when the product has no image (never an empty box).
   * The series + headline (cloned from the liquid chrome source) sit above the
   * photo STAGE; the stage holds the main photo + thumbnails. */
  Configurator.prototype._buildMedia = function () {
    var list = (this.assets.product_images && this.assets.product_images.length)
      ? this.assets.product_images.slice(0, 6)
      : [{ src: this.assets.placeholder || (this.assets.gallery && this.assets.gallery.main) || "", alt: this.productTitle }];
    var self = this;
    var media = h("div", { class: "acfg__media" });
    // v3 hero split: series + headline at the top of the media pane.
    this._cloneChromeInto(media, "[data-acfg-chrome-media]");
    var stage = h("div", { class: "acfg__media-stage" });
    var curIdx = 0; // which photo the main card shows — the lightbox opens on it
    var mainImg = h("div", { class: "acfg__media-main", role: "button", tabindex: "0",
      "aria-label": T("zoom_prefix", "Zoom: ") + ((list[0] && list[0].alt) || this.productTitle || T("product_image", "Product image")) });
    if (list[0] && list[0].src) mainImg.style.backgroundImage = "url('" + list[0].src + "')";
    // click / Enter / Space → open a full-screen lightbox of the current photo
    // (while a takeover is showing — the Fermata shot or, on the pro line, the
    // picked colour's cable — zoom opens that shot alone).
    var openStageZoom = function () {
      if (self._mediaOverrideUrl) self._openLightbox(mainImg.style.backgroundImage, 0, [{ src: self._mediaOverrideUrl, alt: self._mediaOverrideAlt || "Fermata" }]);
      else self._openLightbox(mainImg.style.backgroundImage, curIdx);
    };
    mainImg.addEventListener("click", openStageZoom);
    mainImg.addEventListener("keydown", function (e) { if (e.key === "Enter" || e.key === " " || e.keyCode === 13 || e.keyCode === 32) { e.preventDefault(); openStageZoom(); } });
    // Stage takeover (Albert 2026-08-01: "so they can see in big how it looks
    // and is used"): _syncStrapMedia drives this with either the Fermata in-use
    // shot or — pro line — the picked colour's cable photo; null restores the
    // photo the thumbs point at. `alt` names what is on the stage so the zoom
    // and screen readers stop saying "Fermata" for a guitar cable.
    this._mediaOverrideUrl = null;
    this._mediaOverrideAlt = "";
    var stageLabel = function (alt) {
      return T("zoom_prefix", "Zoom: ") + (alt || (list[curIdx] && list[curIdx].alt) ||
        self.productTitle || T("product_image", "Product image"));
    };
    this._mediaShowOverride = function (url, alt) {
      if (self._mediaOverrideUrl === (url || null)) return;
      self._mediaOverrideUrl = url || null;
      self._mediaOverrideAlt = url ? clean(alt) : "";
      if (url) mainImg.style.backgroundImage = "url('" + url + "')";
      else if (list[curIdx] && list[curIdx].src) mainImg.style.backgroundImage = "url('" + list[curIdx].src + "')";
      else mainImg.style.backgroundImage = "";
      mainImg.setAttribute("aria-label", stageLabel(self._mediaOverrideAlt));
    };
    stage.appendChild(mainImg);
    if (list.length > 1) {
      var thumbs = h("div", { class: "acfg__media-thumbs" });
      list.forEach(function (im, i) {
        var t = btn({ class: "acfg__media-thumb" + (i === 0 ? " is-on" : ""), "aria-label": TF("view_image_n", "View image %{n}", { n: i + 1 }),
          // real 144px thumb URL when the island provides one — painting a 68px
          // button with the 1400px src cost ~150-350KB per image
          style: (im.thumb || im.src) ? "background-image:url('" + (im.thumb || im.src) + "');" : "",
          on: { click: function () {
            self._mediaOverrideUrl = null; // a thumb click asks for the cable photo back
            curIdx = i;
            mainImg.style.backgroundImage = im.src ? "url('" + im.src + "')" : "";
            var all = thumbs.querySelectorAll(".acfg__media-thumb");
            for (var k = 0; k < all.length; k++) all[k].classList.remove("is-on");
            t.classList.add("is-on");
          } } });
        thumbs.appendChild(t);
      });
      stage.appendChild(thumbs);
    }
    media.appendChild(stage);
    return media;
  };

  /* Full-screen image zoom (lightbox). Cosmetic — opens the current photo; when the
   * browse list has several photos it browses them: ‹ › buttons, arrow keys, swipe.
   * Closes on backdrop click or Esc. Appended to the root so it overlays the page.
   * listOverride: optional replacement browse list (e.g. the strap step's single
   * about-photo) — omitted, it browses the product gallery as before. */
  Configurator.prototype._openLightbox = function (bg, startIdx, listOverride) {
    if (!bg || bg === "none") return;
    if (this._lightbox) return; // already open — don't stack overlays or duplicate the document keydown listener
    var self = this;
    var list = (listOverride || this.assets.product_images || []).filter(function (im) { return im && im.src; });
    var idx = Math.max(0, Math.min(list.length - 1, startIdx || 0));
    var box = h("div", { class: "acfg__lightbox", role: "dialog", "aria-modal": "true", "aria-label": T("product_image", "Product image") });
    var img = h("div", { class: "acfg__lightbox-img", style: "background-image:" + bg + ";" });
    var close = btn({ class: "acfg__lightbox-close", "aria-label": T("close_image", "Close image"), text: "×" });
    box.appendChild(img); box.appendChild(close);
    var counter = null;
    function show(i) {
      if (!list.length) return;
      idx = (i + list.length) % list.length;
      img.style.backgroundImage = "url('" + list[idx].src + "')";
      if (counter) counter.textContent = (idx + 1) + " / " + list.length;
    }
    if (list.length > 1) {
      var prev = btn({ class: "acfg__lightbox-nav acfg__lightbox-nav--prev", "aria-label": T("previous_image", "Previous image"), text: "‹",
        on: { click: function (e) { e.stopPropagation(); show(idx - 1); } } });
      var next = btn({ class: "acfg__lightbox-nav acfg__lightbox-nav--next", "aria-label": T("next_image", "Next image"), text: "›",
        on: { click: function (e) { e.stopPropagation(); show(idx + 1); } } });
      counter = h("div", { class: "acfg__lightbox-counter", "aria-hidden": "true", text: (idx + 1) + " / " + list.length });
      box.appendChild(prev); box.appendChild(next); box.appendChild(counter);
      // swipe: a horizontal move over ~40px flips the photo
      var touchX = null;
      box.addEventListener("touchstart", function (e) { if (e.touches && e.touches.length === 1) touchX = e.touches[0].clientX; }, { passive: true });
      box.addEventListener("touchend", function (e) {
        if (touchX == null || !e.changedTouches || !e.changedTouches.length) { touchX = null; return; }
        var dx = e.changedTouches[0].clientX - touchX; touchX = null;
        if (Math.abs(dx) > 40) show(dx < 0 ? idx + 1 : idx - 1);
      }, { passive: true });
    }
    // Remember the opener so closing returns focus where the user was (a11y).
    var returnFocus = null;
    try { returnFocus = document.activeElement; } catch (e) {}
    function shut() {
      self._lightbox = null;
      try { box.parentNode.removeChild(box); } catch (e) {}
      document.removeEventListener("keydown", onKey);
      document.documentElement.style.overflow = "";
      if (returnFocus && returnFocus.focus) { try { returnFocus.focus(); } catch (e) {} }
    }
    function onKey(e) {
      if (e.key === "Escape" || e.keyCode === 27) { shut(); return; }
      if (list.length > 1 && (e.key === "ArrowLeft" || e.keyCode === 37)) show(idx - 1);
      else if (list.length > 1 && (e.key === "ArrowRight" || e.keyCode === 39)) show(idx + 1);
      // Focus trap — same pattern as openDrawer. aria-modal promises the page
      // behind is inert, but Tab could still walk into invisible controls there.
      else if (e.key === "Tab" || e.keyCode === 9) {
        var f = box.querySelectorAll("button");
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        else if (!box.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      }
    }
    box.addEventListener("click", shut);
    document.addEventListener("keydown", onKey);
    document.documentElement.style.overflow = "hidden"; // scroll lock (mirrors openDrawer)
    (this.root || document.body).appendChild(box);
    this._lightbox = box;
    try { close.focus(); } catch (e) {}
  };

  /* ------------------------------ spec summary ---------------------------- */
  // One-line live summary for the sticky bar: length · weave · colours · splitter ·
  // connector type. Pure read of the current selection; cosmetic only.
  Configurator.prototype._colourSummary = function () {
    var self = this;
    if (this.canonicalColour) {
      var n = this.state._numColours || 1;
      return n === 1 ? paletteName((this.state._palette || [1])[0]) : TF("n_colours", "%{n} colours", { n: n });
    }
    var seen = {};
    this.colourOrder.forEach(function (o) { seen[colourNumber(self.state[o.id])] = 1; });
    var c = Object.keys(seen).length;
    return c + (c === 1 ? T("colour_suffix", " colour") : T("colours_suffix", " colours"));
  };
  Configurator.prototype._specSummary = function () {
    var self = this, parts = [];
    this.sectionPlan.forEach(function (plan) {
      if (plan.kind === "length") parts.push(splitLengthLabel(self.selectedValue(plan.opt).label).metric);
      else if (plan.kind === "colour") parts.push(self._colourSummary());
      else if (plan.kind === "braiding") parts.push(clean(self.selectedValue(plan.opt).label));
      else if (plan.kind === "splitter") { var s = self.selectedValue(plan.opt); if (splitterKey(s.label) !== "none") parts.push(clean(s.label)); }
      else if (plan.kind === "strap") { var sp = self.selectedValue(plan.opt); if (!strapIsNone(sp)) parts.push("Fermata " + clean(sp.label)); }
      else if (plan.kind === "termination") { parts.push(connectorType(self.selectedValue(plan.opt).label).name); }
    });
    return parts.filter(Boolean).join("  ·  ");
  };

  /* ----------------------- view toggle + wizard chrome -------------------- */
  Configurator.prototype._buildViewToggle = function () {
    var self = this;
    // Order: [Step by step][All options] — the wizard is the default view
    // (Albert 2026-07-05); "All options" is the secondary toggle.
    this.elViewAll = btn({ class: "acfg__viewtoggle-btn", "aria-label": T("see_all_options_aria", "See all options at once"),
      on: { click: function () { self._setViewMode("all"); } } }, T("all_options", "All options"));
    this.elViewStep = btn({ class: "acfg__viewtoggle-btn", "aria-label": T("step_by_step_aria", "Step-by-step view"),
      on: { click: function () { self._setViewMode("wizard"); } } }, T("step_by_step", "Step by step"));
    return h("div", { class: "acfg__viewtoggle", role: "group", "aria-label": T("configurator_view_aria", "Configurator view") }, [this.elViewStep, this.elViewAll]);
  };

  Configurator.prototype._buildProgress = function () {
    this.elProgressLabel = h("span", { class: "acfg__progress-label", text: T("step_1", "Step 1") });
    this.elProgressTitle = h("span", { class: "acfg__progress-step-title" });
    // Segmented fill: one slim segment per wizard step. Completed steps fill solid;
    // the current step is marked in-progress (animated). Built once — the step count
    // is fixed per product. Each fill animates as you advance (CSS width transition).
    var track = h("div", { class: "acfg__progress-track" });
    this.elProgressSegs = [];
    var n = this.sectionPlan.length || 1;
    for (var i = 0; i < n; i++) {
      var seg = h("div", { class: "acfg__progress-seg" }, [h("i", { class: "acfg__progress-seg-fill" })]);
      this.elProgressSegs.push(seg); track.appendChild(seg);
    }
    this.elProgress = h("div", { class: "acfg__progress" }, [
      // aria-live: the label already receives "Step X of N" + the step title on every
      // advance — announcing the head makes each step change audible to AT for free.
      h("div", { class: "acfg__progress-head", "aria-live": "polite" }, [this.elProgressLabel, this.elProgressTitle]),
      track
    ]);
    return this.elProgress;
  };

  // Trust microcopy for the sticky bar (e.g. "2-yr warranty", "Tallinn-made",
  // "Free worldwide"). Read from settings.trust_items (array of strings). Returns
  // null when none are configured, so the bar simply omits the cluster. Cosmetic.
  Configurator.prototype._buildTrust = function () {
    var items = this.settings.trust_items;
    if (!items || !items.length) return null;
    var kids = [];
    for (var i = 0; i < items.length; i++) {
      var label = String(items[i] == null ? "" : items[i]).trim();
      if (!label) continue;
      kids.push(h("span", { class: "acfg__trust-item" }, [
        h("span", { class: "acfg__trust-dot", "aria-hidden": "true" }),
        h("span", { text: label })
      ]));
    }
    return kids.length ? h("div", { class: "acfg__bar-trust" }, kids) : null;
  };

  // Star rating + review count for the sticky bar (social proof at the CTA). Read from
  // settings.rating + settings.rating_count (product reviews metafields). Null when
  // there are no reviews. Cosmetic.
  Configurator.prototype._buildSocial = function () {
    var rating = Math.max(0, Math.min(5, parseFloat(this.settings.rating) || 0)); // clamp to the 0–5 domain
    var count = parseInt(this.settings.rating_count, 10) || 0;
    if (count <= 0) return null;
    var pct = (rating / 5) * 100;
    var stars = h("span", { class: "acfg__stars", "aria-hidden": "true" }, [
      h("span", { class: "acfg__stars-track", text: "★★★★★" }),
      h("span", { class: "acfg__stars-fill", style: "width:" + pct + "%", text: "★★★★★" })
    ]);
    var reviews = count + (count === 1 ? T("review_suffix", " review") : T("reviews_suffix", " reviews"));
    var txt = h("span", { class: "acfg__social-text", text: (rating ? rating.toFixed(1) + " · " : "") + reviews });
    // Mirror the visible text: only claim a score when one is set, so screen readers never hear "Rated 0 out of 5".
    var label = (rating ? TF("rated_from", "Rated %{rating} out of 5 from ", { rating: rating.toFixed(1) }) : "") + reviews;
    return h("div", { class: "acfg__social", "aria-label": label }, [stars, txt]);
  };

  // Concrete ready-to-ship window ("Ready to ship 28 Jul – 4 Aug") computed from the
  // raw lead range settings.lead_days ("5-10", single source: shop metafield
  // arachne.lead_time). Concrete dates beat "X-Y days" — the customer stops doing
  // calendar math. Falls back to the ships_note text when the range is unparsable.
  Configurator.prototype._shipDateText = function () {
    var lead = clean(this.settings.lead_days || "");
    var m = lead.match(/^(\d+)\s*[-–]\s*(\d+)$/), one = lead.match(/^\d+$/);
    var from, to;
    if (m) { from = parseInt(m[1], 10); to = parseInt(m[2], 10); }
    else if (one) { from = to = parseInt(lead, 10); }
    else return this.settings.ships_note || "";
    try {
      var lang = (document.documentElement && document.documentElement.lang) || "en";
      var fmt = new Intl.DateTimeFormat(lang, { day: "numeric", month: "short" });
      var now = Date.now();
      var f = fmt.format(new Date(now + from * 86400000));
      var t = fmt.format(new Date(now + to * 86400000));
      return TF("ready_to_ship_range", "Ready to ship %{from} – %{to}", { from: f, to: from === to ? f : t });
    } catch (e) { return this.settings.ships_note || ""; }
  };

  // Reassurance row directly under the in-flow CTA pair, visible on ALL viewports —
  // the bar's trust/social/ships cluster is desktop-only, which left mobile buyers
  // committing €90+ to a made-to-order product with zero reassurance near the button
  // (2026-07-20 review). Reuses settings.trust_items; leads with the ship window.
  Configurator.prototype._buildCtaTrust = function () {
    var kids = [];
    var ship = this._shipDateText();
    if (ship) kids.push(h("span", { class: "acfg__cta-ship" }, [h("span", { class: "acfg__trust-dot", "aria-hidden": "true" }), h("span", { text: ship })]));
    var items = this.settings.trust_items || [];
    for (var i = 0; i < items.length; i++) {
      var label = String(items[i] == null ? "" : items[i]).trim();
      if (!label) continue;
      kids.push(h("span", { class: "acfg__trust-item" }, [h("span", { class: "acfg__trust-dot", "aria-hidden": "true" }), h("span", { text: label })]));
    }
    return kids.length ? h("div", { class: "acfg__cta-trust" }, kids) : null;
  };

  Configurator.prototype._loadViewMode = function () {
    try {
      var v = window.localStorage.getItem("acfg_view_mode");
      if (v === "wizard" || v === "all") return v;
    } catch (e) {}
    return "wizard"; // step-by-step is the default
  };
  Configurator.prototype._setViewMode = function (mode) {
    if (mode !== "wizard" && mode !== "all") return;
    this.viewMode = mode;
    try { window.localStorage.setItem("acfg_view_mode", mode); } catch (e) {}
    track("view_mode", { mode: mode });
    this._applyView();
  };

  // Length display unit (metric|imperial). The order ALWAYS records metric — this
  // only flips which half of "1.5m / 5ft" the UI emphasises. Persisted like the view.
  Configurator.prototype._loadLengthUnit = function () {
    try { var v = window.localStorage.getItem("acfg_length_unit"); if (v === "metric" || v === "imperial") return v; } catch (e) {}
    return "metric";
  };
  Configurator.prototype._setLengthUnit = function (u) {
    if (u !== "metric" && u !== "imperial") return;
    try { window.localStorage.setItem("acfg_length_unit", u); } catch (e) {}
    this.setState(kv("_lengthUnit", u));
  };

  // Soft-validate a step before advancing: an untouched required option falls
  // back to its default (or first value). It never blocks Next — every option
  // already carries a value from _initState, so this is a belt-and-braces guard.
  Configurator.prototype._softValidate = function (stepIdx) {
    var self = this, plan = this.sectionPlan[stepIdx];
    if (!plan) return;
    var opts = plan.opts || (plan.opt ? [plan.opt] : []);
    var patch = null;
    opts.forEach(function (o) {
      if (self.state[o.id]) return;
      var labels = o.values.map(function (v) { return v.label; });
      (patch = patch || {})[o.id] = (o.default != null && labels.indexOf(o.default) !== -1) ? o.default : (labels[0] || "");
    });
    if (patch) this.setState(patch);
  };

  Configurator.prototype._goStep = function (delta) {
    var n = this.sectionPlan.length;
    if (delta > 0) this._softValidate(this.step);
    var next = this.step + delta;
    if (next < 0) next = 0;
    if (next > n - 1) next = n - 1;
    if (next === this.step) { this._applyView(); return; }
    this.step = next;
    track("step", { step: next + 1, title: (this.sectionPlan[next] && this.sectionPlan[next].title) || "" });
    this._applyView();
    if (this.elProgress && typeof this.elProgress.scrollIntoView === "function") {
      this.elProgress.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
    }
    // Move keyboard focus into the section that just appeared — without this a
    // keyboard user's focus stays on the Next button while the content swaps under it.
    var ctrl = this.controllers[this.step], secEl = ctrl && ctrl.el;
    if (secEl && secEl.setAttribute && secEl.focus) {
      secEl.setAttribute("tabindex", "-1");
      try { secEl.focus({ preventScroll: true }); } catch (e) { try { secEl.focus(); } catch (e2) {} }
    }
  };

  // Reflect the current view mode (+ step) into the DOM. Cheap; pure show/hide
  // on the single shared set of section controllers, so payload/state never move.
  Configurator.prototype._applyView = function () {
    var wizard = this.viewMode === "wizard";
    var n = this.sectionPlan.length;
    if (this.step > n - 1) this.step = n - 1;
    if (this.step < 0) this.step = 0;

    if (this.elViewStep) { this.elViewStep.classList.toggle("is-on", wizard); this.elViewStep.setAttribute("aria-pressed", wizard ? "true" : "false"); }
    if (this.elViewAll) { this.elViewAll.classList.toggle("is-on", !wizard); this.elViewAll.setAttribute("aria-pressed", wizard ? "false" : "true"); }

    if (this.elSectionsWrap) this.elSectionsWrap.classList.toggle("acfg__sections--wizard", wizard);
    for (var i = 0; i < this.controllers.length; i++) {
      this.controllers[i].el.style.display = (!wizard || i === this.step) ? "" : "none";
    }

    if (this.elProgress) this.elProgress.style.display = wizard ? "" : "none";
    if (this.elCommit) this.elCommit.style.display = wizard ? "none" : "";

    // The in-flow CTA pair morphs with the mode: mid-wizard it IS the step nav
    // ("Next" / "Back"); on the final step — and always in See-all — it's the real
    // commit pair ("Add to cart · total" / "Buy it now", plus a small ← Back link
    // so the last step keeps a way back).
    var committing = !wizard || this.step === n - 1;
    if (this.elCtaMainLabel) this.elCtaMainLabel.textContent = committing ? (this.settings.cta_label || T("add_to_cart", "Add to cart")) : T("next", "Next");
    if (this.elCtaMainDot) this.elCtaMainDot.style.display = committing ? "" : "none";
    if (this.elCtaPriceMain) this.elCtaPriceMain.style.display = committing ? "" : "none";
    if (this.elBuyNowLabel) this.elBuyNowLabel.textContent = committing ? T("buy_it_now", "Buy it now") : T("back", "Back");
    if (this.elBuyNowBtn) {
      this.elBuyNowBtn.classList.toggle("is-back", !committing);
      this.elBuyNowBtn.disabled = !committing && this.step === 0;
    }
    if (this.elInflowBack) this.elInflowBack.style.display = (wizard && committing && n > 1) ? "" : "none";

    if (wizard) {
      if (this.step > this.maxStep) this.maxStep = this.step;
      if (this.maxStep > n - 1) this.maxStep = n - 1;
      var planTitle = this.sectionPlan[this.step] && this.sectionPlan[this.step].title;
      if (this.elProgressLabel) this.elProgressLabel.textContent = TF("step_x_of_n", "Step %{step} of %{total}", { step: this.step + 1, total: n });
      if (this.elProgressTitle) this.elProgressTitle.textContent = planTitle || "";
      // Segmented progress: each visited step fills solid; the current step is the
      // in-progress segment (animated). Visited-but-behind stays filled when you step
      // Back (keyed on maxStep), so progress never visibly un-fills.
      if (this.elProgressSegs) {
        for (var pi = 0; pi < this.elProgressSegs.length; pi++) {
          var seg = this.elProgressSegs[pi];
          seg.classList.toggle("is-current", pi === this.step);
          seg.classList.toggle("is-done", pi !== this.step && pi <= this.maxStep);
        }
      }
    }
    this._animateStep();
    this._syncBarSpacing();
    this._syncStrapMedia();
  };

  // Re-trigger the step-enter animation on the currently-shown wizard section, so
  // each advance fades/slides in. No-op outside wizard mode (all sections visible)
  // and under prefers-reduced-motion (the CSS disables the keyframe). Reflow read is
  // guarded for the headless test shim (no layout).
  Configurator.prototype._animateStep = function () {
    if (this.viewMode !== "wizard") return;
    var ctrl = this.controllers[this.step];
    var el = ctrl && ctrl.el;
    if (!el || !el.classList) return;
    el.classList.remove("acfg__sec-anim");
    try { void el.offsetWidth; } catch (e) {}
    el.classList.add("acfg__sec-anim");
  };

  /* ------------------------------ section: head --------------------------- */
  function sectionHead(num, title, hint) {
    // step + title share one accent "eyebrow" group (so the CSS underline hugs just
    // the text, with any value pill sitting apart on the right — matches the prototype).
    // h2 (was a span): heading navigation is the #1 AT browsing strategy, and the
    // page had a single hidden h1 with zero outline. CSS resets the UA h2 styles.
    var eyebrow = h("h2", { class: "acfg__eyebrow" }, [
      h("span", { class: "acfg__step", text: (num < 10 ? "0" + num : String(num)) + " · " }),
      h("span", { class: "acfg__section-title", text: title })
    ]);
    var kids = [eyebrow];
    if (hint) kids.push(h("span", { class: "acfg__hint", text: hint }));
    return h("div", { class: "acfg__section-head" }, kids);
  }

  /* ------------------------------ section: length ------------------------- *
   * Slider + ticks + use-case captions, plus a metric/imperial DISPLAY toggle. The
   * cart label is never touched ("1.5m / 5ft" is submitted verbatim) — the toggle
   * only flips which half the readout + ticks emphasise. Captions adopted from the
   * design prototype, keyed by metres. */
  var LENGTH_NOTES = {
    // Adapter-cable stops (section supplies localized copy via length_note_0_35 etc.).
    0.35: "Pocket adapter. DAP or dongle straight into your cable.",
    0.5: "Short and tidy. Amp on the desk, cable within reach.",
    0.75: "Room to route around a desktop stack.",
    1: "Source right on the desk. Tightest possible reach.",
    1.25: "Tight desktop setup. No slack to spare.",
    1.5: "Standard desktop nearfield — the most common length.",
    2: "Desk to a rack on the floor or under the table.",
    2.5: "Stand up at the desk without unplugging.",
    3: "Sofa or armchair across from the rack.",
    3.5: "Across a small room. Walk a few steps.",
    4: "Living-room listening — sofa to gear on a sideboard.",
    4.5: "Generous slack. Roam between desk and seating.",
    5: "Studio control room. Pace while you mix.",
    6: "Large studio or modest stage setup.",
    7: "Maximum reach. Stage and professional use."
  };
  function splitLengthLabel(label) {
    var parts = clean(label).split("/");
    return { metric: clean(parts[0]) || clean(label), imperial: clean(parts[1]) };
  }
  function lengthShort(unitStr) {
    // Tick labels are unit-less numbers under the Metric/Imperial toggle, so a "35cm"
    // stop shows as 0.35 next to 1 / 1.5 (its readout still says 35cm).
    var cm = /^\s*([\d.]+)\s*cm\b/i.exec(clean(unitStr));
    if (cm) return String(parseFloat(cm[1]) / 100);
    var m = clean(unitStr).replace(/[a-z].*$/i, ""); return m || clean(unitStr);
  }

  // Length-slider glide: cubic-bezier(.22,.61,.36,1) evaluated in JS (Newton-Raphson
  // on x, then y) — the same curve the fill's CSS transition used. It lives in JS
  // because ONE rAF loop must move the thumb (input value) and the fill (--pct)
  // together; two animation systems (native thumb snap + CSS fill transition) is
  // exactly the disconnect Albert flagged 2026-07-14.
  var GLIDE_MS = 320;
  var GLIDE_EASE = (function () {
    var p1x = 0.22, p1y = 0.61, p2x = 0.36, p2y = 1;
    var cx = 3 * p1x, bx = 3 * (p2x - p1x) - cx, ax = 1 - cx - bx;
    var cy = 3 * p1y, by = 3 * (p2y - p1y) - cy, ay = 1 - cy - by;
    function xOf(u) { return ((ax * u + bx) * u + cx) * u; }
    function dxOf(u) { return (3 * ax * u + 2 * bx) * u + cx; }
    function yOf(u) { return ((ay * u + by) * u + cy) * u; }
    return function (t) {
      if (t <= 0) return 0;
      if (t >= 1) return 1;
      var u = t;
      for (var i = 0; i < 5; i++) {
        var e = xOf(u) - t;
        if (e > -1e-4 && e < 1e-4) break;
        var d = dxOf(u);
        if (d > -1e-6 && d < 1e-6) break;
        u -= e / d;
      }
      if (u < 0) u = 0; else if (u > 1) u = 1;
      return yOf(u);
    };
  })();
  function prefersReducedMotion() {
    try { return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches); } catch (e) { return false; }
  }

  Configurator.prototype._buildLength = function (opt, num, title) {
    var self = this;
    var parsable = this._lengthParsable(opt);
    var sec = h("section", { class: "acfg__section acfg__section--length" });

    // Single allowed stop (cable.length_allowed = ["1.5m"], e.g. HD25 + vintage
    // HD Sennheisers): the length is FIXED for this model. A one-stop slider
    // reads as a broken control, so render only the read-out card — no slider,
    // no ticks, no unit toggle, no "how long do you need it?" question. State,
    // cart payload and recap are untouched (the only value is the default).
    if (opt.values.length === 1) {
      var fixed = opt.values[0], fp = splitLengthLabel(fixed.label);
      sec.appendChild(h("div", { class: "acfg__len-head" }, [sectionHead(num, title)]));
      sec.appendChild(h("div", { class: "acfg__len-readout acfg__len-readout--fixed" }, [
        h("div", { class: "acfg__len-readout-main" }, [
          h("span", { class: "acfg__len-value", text: fp.metric }),
          h("span", { class: "acfg__len-sub", text: fp.imperial || "" })
        ]),
        h("div", { class: "acfg__len-readout-info" }, [
          h("p", { class: "acfg__len-note", text: T("length_fixed", "This model ships in one fixed length.") }),
          h("span", { class: "acfg__len-add" + (fixed.addon ? "" : " is-free"),
            text: fixed.addon ? TF("over_base", "+%{amount}", { amount: money(fixed.addon, self.currency) }) : T("included_in_base", "Included in base price") })
        ])
      ]));
      return { el: sec, update: function () {} };
    }

    // Head: step label + a metric/imperial display toggle (parsable lengths only).
    var headRow = h("div", { class: "acfg__len-head" });
    headRow.appendChild(sectionHead(num, title));
    var unitMetric, unitImperial;
    if (parsable) {
      unitMetric = btn({ class: "acfg__unit-btn", text: T("metric", "Metric"), "aria-label": T("metric_aria", "Show length in metres"), on: { click: function () { self._setLengthUnit("metric"); } } });
      unitImperial = btn({ class: "acfg__unit-btn", text: T("imperial", "Imperial"), "aria-label": T("imperial_aria", "Show length in feet"), on: { click: function () { self._setLengthUnit("imperial"); } } });
      headRow.appendChild(h("div", { class: "acfg__unit", role: "group", "aria-label": T("length_units_aria", "Length units") }, [unitMetric, unitImperial]));
    }
    sec.appendChild(headRow);

    sec.appendChild(h("p", { class: "acfg__q", text: T("length_question", "How long do you need it?") }));
    if (parsable) sec.appendChild(h("p", { class: "acfg__q-hint", text: T("length_hint", "Drag the slider or tap a length. From workbench-short to whole-room long.") }));
    var recHas25 = parsable && opt.values.some(function (v) { return /^\s*2\.5m/.test(v.label || ""); });
    // Recommend note only when the 2.5m stop actually exists (length_allowed can remove it).
    // (Argentea has no purchase history yet, so the "most customers" claim is withheld there.)
    // Headphone-only nudge: pro/adapter kinds (and Argentea, no purchase history yet) carry a 2.5m stop without that customer base.
    if (recHas25 && !self.kind && this.series !== "argentea") sec.appendChild(h("p", { class: "acfg__rec-note", text: T("length_recommend", "★ Most customers choose 2.5m / 8.2ft.") }));

    // Standard discrete lengths only: ticks-above-track slider when parsable, else a list.
    var slider, tickEls = [], rowList = null;
    if (parsable) {
      // Ticks ABOVE the track (label on top), then the filled slider — mirrors the
      // prototype LengthSlider. The fill is driven by a --pct CSS var set in update.
      var ticksWrap = h("div", { class: "acfg__ticks" });
      opt.values.forEach(function (v, i) {
        var pct = opt.values.length > 1 ? (i / (opt.values.length - 1)) * 100 : 0;
        var left = "calc(" + pct.toFixed(3) + "% + " + (9 - (pct / 100) * 18).toFixed(2) + "px)";
        var tlabel = h("span", { class: "acfg__tick-label" });
        var tick = btn({ class: "acfg__tick", style: "left:" + left + ";", "aria-label": clean(v.label),
          on: { click: function () { if (slider) slider._glideNext = true; self.setState(kv(opt.id, v.label)); } } },
          [tlabel, h("span", { class: "acfg__tick-line" })]);
        tick._units = splitLengthLabel(v.label);
        tickEls.push(tick); ticksWrap.appendChild(tick);
      });
      // ── Glide machinery: one rAF loop moves the thumb (fractional value while
      // step is briefly "any" — Chrome snaps assigned values to the step otherwise)
      // and the fill (--pct) through GLIDE_EASE, so the thumb rides the fill edge.
      var maxIdx = opt.values.length - 1;
      var glide = { raf: 0, watchdog: 0, target: -1 };
      var pctOf = function (v) { return maxIdx > 0 ? (v / maxIdx) * 100 : 0; };
      var setPct = function (p) {
        // setProperty is the only way to set a CSS var; guarded for the headless
        // test shim (plain-object style).
        if (slider.style && typeof slider.style.setProperty === "function") slider.style.setProperty("--pct", p.toFixed(2) + "%");
      };
      var popThumb = function () {
        // A quick thumb pop marks the landing (reduced-motion CSS disables the keyframe).
        if (!slider.classList) return;
        slider.classList.remove("is-pop");
        try { void slider.offsetWidth; } catch (e) {}
        slider.classList.add("is-pop");
      };
      var stopGlide = function () {
        if (glide.raf) { try { cancelAnimationFrame(glide.raf); } catch (e) {} }
        if (glide.watchdog) { try { clearTimeout(glide.watchdog); } catch (e) {} }
        glide.raf = 0; glide.watchdog = 0; glide.target = -1; slider.step = "1";
      };
      var landGlide = function () {
        // User grabbed the slider mid-flight: settle instantly at the target so
        // they interact with a resolved control, not a moving one.
        var t = glide.target;
        if (!glide.raf || t < 0) return;
        stopGlide();
        slider.value = String(t); setPct(pctOf(t));
      };
      var glideTo = function (toIdx) {
        var from = parseFloat(slider.value);
        if (isNaN(from)) from = 0;
        stopGlide();
        glide.target = toIdx;
        slider.step = "any";
        var t0 = Date.now();
        var frame = function () {
          var u = Math.min(1, (Date.now() - t0) / GLIDE_MS);
          var v = from + (toIdx - from) * GLIDE_EASE(u);
          slider.value = String(v);
          setPct(pctOf(v));
          if (u < 1) { glide.raf = requestAnimationFrame(frame); return; }
          stopGlide();
          slider.value = String(toIdx); setPct(pctOf(toIdx)); // exact landing, step restored
          popThumb();
        };
        glide.raf = requestAnimationFrame(frame);
        // rAF pauses in hidden/occluded documents (background tab, webview) —
        // without this the glide would freeze mid-flight with step="any".
        // landGlide is a no-op if the flight already finished (stopGlide cleared us).
        glide.watchdog = setTimeout(landGlide, GLIDE_MS + 130);
      };
      // Track clicks should glide exactly like tick clicks (Albert 2026-07-14
      // follow-up): pointerdown on the bar makes the native control JUMP the value
      // before any drag. Telling a click from a drag: the FIRST input after a
      // pointerdown that lands ≥2 stops away is a jump-click — rewind the value
      // (same task, pre-paint: no flash) and glide from where we were. A real
      // drag's first move is the adjacent stop (Δ1, stays native), and any later
      // input cancels a glide anyway, so even a fast flick self-corrects.
      var jump = { armed: false, from: 0 };
      slider = h("input", {
        class: "acfg__slider", type: "range", min: "0", max: String(opt.values.length - 1), step: "1",
        value: "0", "aria-label": T("cable_length_aria", "Cable length"),
        // Math.round: value can be fractional if an input sneaks in mid-glide
        // (step is "any" during the flight).
        on: {
          input: function (e) {
            var to = Math.round(+e.target.value) || 0;
            var armed = jump.armed; jump.armed = false;
            if (glide.raf) stopGlide();
            if (armed && Math.abs(to - jump.from) >= 2 && slider._lastIdx !== undefined) {
              slider.value = String(jump.from);
              slider._glideNext = true; // update() glides from the rewound position
            }
            self.setState(kv(opt.id, opt.values[to].label));
          },
          pointerdown: function () { landGlide(); jump.armed = true; jump.from = Math.round(parseFloat(slider.value)) || 0; },
          pointerup: function () { jump.armed = false; },
          pointercancel: function () { jump.armed = false; },
          keydown: function () { landGlide(); jump.armed = false; }
        }
      });
      sec.appendChild(h("div", { class: "acfg__len-track" }, [ticksWrap, slider]));
    }

    // Prototype two-column read-out: big value + alt unit (left) | use-case + upcharge
    // (right). For non-parsable lengths the rows sit above it.
    var valueLabel = h("span", { class: "acfg__len-value" });
    var subLabel = h("span", { class: "acfg__len-sub" });
    var noteLabel = h("p", { class: "acfg__len-note" });
    var addLabel = h("span", { class: "acfg__len-add" });
    if (!parsable) { rowList = self._rowList(opt); sec.appendChild(rowList.el); }
    sec.appendChild(h("div", { class: "acfg__len-readout" }, [
      h("div", { class: "acfg__len-readout-main" }, [valueLabel, subLabel]),
      h("div", { class: "acfg__len-readout-info" }, [noteLabel, addLabel])
    ]));

    return {
      el: sec,
      update: function (st) {
        var sv = self.selectedValue(opt);
        var imperial = st._lengthUnit === "imperial";
        if (parsable) {
          if (unitMetric) { unitMetric.classList.toggle("is-on", !imperial); ariaPressed(unitMetric, !imperial); }
          if (unitImperial) { unitImperial.classList.toggle("is-on", imperial); ariaPressed(unitImperial, imperial); }
          var idx = Math.max(0, opt.values.indexOf(sv));
          // Tick clicks glide thumb + fill together (one rAF loop, see glideTo);
          // everything else — drag, keyboard, first paint, prefill — sets directly.
          var canGlide = typeof requestAnimationFrame === "function" && slider.classList && !prefersReducedMotion();
          var wantGlide = slider._glideNext === true && slider._lastIdx !== undefined && slider._lastIdx !== idx && canGlide;
          slider._glideNext = false;
          if (wantGlide) {
            glideTo(idx); // pops the thumb on landing
          } else if (glide.raf && glide.target === idx) {
            // unrelated re-render while a glide is mid-flight — let it land on its own
          } else {
            if (glide.raf) stopGlide();
            slider.value = String(idx);
            setPct(pctOf(idx));
            // pop marks any direct jump (drag steps, keyboard; not the first paint)
            if (slider._lastIdx !== undefined && slider._lastIdx !== idx) popThumb();
          }
          slider._lastIdx = idx;
          tickEls.forEach(function (t, i) {
            t.classList.toggle("is-active", i === idx);
            var u = t._units;
            t.querySelector(".acfg__tick-label").textContent = lengthShort(imperial ? (u.imperial || u.metric) : u.metric);
          });
          var parts = splitLengthLabel(sv.label);
          valueLabel.textContent = imperial ? (parts.imperial || parts.metric) : parts.metric;
          var other = imperial ? parts.metric : parts.imperial;
          subLabel.textContent = other || "";
          // aria-valuetext: without it the slider announces its stop INDEX ("3") —
          // meaningless. Announce the real value (unit-aware) + any surcharge.
          slider.setAttribute("aria-valuetext", clean(sv.label) + (sv.addon ? " (+" + money(sv.addon, self.currency) + ")" : ""));
          var metres = metresOf(sv.label);
          var note = T("length_note_" + String(metres).replace(".", "_"), LENGTH_NOTES[metres] || "");
          noteLabel.textContent = note || "";
          noteLabel.style.display = note ? "" : "none";
        } else if (rowList) {
          rowList.update(st);
          valueLabel.textContent = clean(sv.label);
          subLabel.textContent = "";
          noteLabel.style.display = "none";
        }
        addLabel.textContent = sv.addon ? TF("over_base", "+%{amount}", { amount: money(sv.addon, self.currency) }) : T("included_in_base", "Included in base price");
        addLabel.classList.toggle("is-free", !sv.addon);
      }
    };
  };

  /* ------------------------------ section: colours ------------------------ *
   * MERGED step (count + arrangement + palette). Canonical Y-cable sets get the
   * full friendly model: pick HOW MANY colours (1–4), then — for 2 or 3 — WHERE
   * they go (per-side / alternating / …), then the colours themselves. It all
   * resolves down to the four real strand-slot labels the cart already submits.
   * Non-canonical sets (Color 1-4, single Color, …) keep a direct per-slot picker. */
  // Schematic Y-cable topology glyph (mirrors the design prototype's ArrangementGlyph),
  // drawn AS WORN facing you: the wearer's LEFT earcup lands on the VIEWER'S RIGHT —
  // the same convention as the Medusa preview — so the glyph shows where the strands
  // actually sit on the headphone. Four strand dots pair R (viewer left) | L (viewer
  // right), converging on a centred splitter node and trunk. Dots are painted with the
  // REAL picked palette colours and carry their colour LETTER (A–D), so which colour
  // repeats — and on which side — reads at a glance. Re-rendered on every palette change.
  // Accent for inline-SVG paints — read once from the live --acfg-accent token so a
  // merchant accent_color change propagates here too instead of silently staying lime
  // (duplicated-constant drift, 2026-07-20 review). Falls back to the historical default.
  var _cssAccent = null;
  function cssAccent() {
    if (_cssAccent) return _cssAccent;
    try {
      var el = document.querySelector("[data-acfg-root]") || document.documentElement;
      var v = (getComputedStyle(el).getPropertyValue("--acfg-accent") || "").trim();
      if (v) return (_cssAccent = v);
    } catch (e) {}
    return (_cssAccent = "#C2E812");
  }
  var ARR_TINTS = ["", "#9aa0a6", "#e2e2e2", "#5f6368"]; // fallback tints while a palette slot is unset (slot 0 = live accent)
  var ARR_TOPS = [12, 24, 56, 68]; // R1,R2 | gap | L1,L2  (viewBox width 80; as-worn)
  var ARR_ORDER = [2, 3, 0, 1];    // strand slot drawn at each position: R pair on the viewer's left
  function glyphInk(hex) { // letter colour with enough contrast on the dot
    var m = /^#([0-9a-f]{6})$/i.exec(hex || "");
    if (!m) return "#0A0A0A";
    var v = parseInt(m[1], 16);
    var lum = (0.299 * (v >> 16 & 255) + 0.587 * (v >> 8 & 255) + 0.114 * (v & 255)) / 255;
    return lum > 0.52 ? "#0A0A0A" : "#F5F5F5";
  }
  function arrangementGlyphSVG(slots, palette, hexOf) {
    var dotY = 22, splitY = 48, trunkBottom = 72, r = 8;
    var stems = "", dots = "";
    for (var i = 0; i < 4; i++) {
      var slot = slots[ARR_ORDER[i]];
      var hex = ((palette && palette[slot] && hexOf) ? hexOf(palette[slot]) : "") || ARR_TINTS[slot] || cssAccent() || "#888";
      stems += '<path d="M' + ARR_TOPS[i] + ' ' + dotY + ' Q' + ARR_TOPS[i] + ' ' + (splitY - 8) + ' 40 ' + splitY + '" stroke="rgba(255,255,255,0.18)" stroke-width="1.4" fill="none"/>';
      dots += '<circle cx="' + ARR_TOPS[i] + '" cy="' + dotY + '" r="' + r + '" fill="' + hex + '" stroke="rgba(255,255,255,0.22)" stroke-width="1"/>' +
        '<text x="' + ARR_TOPS[i] + '" y="' + (dotY + 2.5) + '" text-anchor="middle" font-size="7.5" font-weight="700" fill="' + glyphInk(hex) + '">' + String.fromCharCode(65 + slot) + '</text>';
    }
    return '<svg viewBox="0 0 80 78" width="80" height="78" aria-hidden="true" focusable="false">' +
      '<text x="18" y="9" text-anchor="middle" font-size="7" fill="rgba(255,255,255,0.45)">R</text>' +
      '<text x="62" y="9" text-anchor="middle" font-size="7" fill="rgba(255,255,255,0.45)">L</text>' + stems +
      '<line x1="40" y1="' + (splitY + 2) + '" x2="40" y2="' + trunkBottom + '" stroke="rgba(255,255,255,0.5)" stroke-width="3" stroke-linecap="round"/>' +
      '<circle cx="40" cy="' + splitY + '" r="2.5" fill="rgba(255,255,255,0.4)"/>' +
      dots + '</svg>';
  }

  Configurator.prototype._buildColours = function (opts, num, title) {
    return this.canonicalColour ? this._buildColoursCanonical(opts, num, title)
         : this.medusaSplit ? this._buildColoursMedusa(opts, num, title)
                                : this._buildColoursGeneric(opts, num, title);
  };

  // Shared 6-group family filter + 28-swatch grid + count footer. onPick(n) fires
  // on a swatch click; update(activeNum, family) reflects the active colour + filter.
  Configurator.prototype._buildSwatchPicker = function (onPick) {
    var self = this;
    var wrap = h("div", { class: "acfg__picker" });
    // v4: family chips only for the legacy palette — the membership numbers were
    // curated for the 28 paracord colours and would mislabel a custom palette.
    var famWrap = null, famEls = [];
    if (this.colourFamilies) {
      famWrap = h("div", { class: "acfg__families" });
      famEls = FAMILY_GROUPS.map(function (g) {
        var b = btn({ class: "acfg__family", text: T("family_" + g[0], g[1]), on: { click: function () { self.setState(kv("_family", g[0])); } } });
        b._fam = g[0]; famWrap.appendChild(b); return b;
      });
      wrap.appendChild(famWrap);
    }
    var grid = h("div", { class: "acfg__swatches" });
    var swatchEls = [];
    var palNums = this.paletteNums || [];
    for (var ni = 0; ni < palNums.length; ni++) {
      (function (cn) {
        var img = h("span", { class: "acfg__swatch-img", style: self.swatchStyle(cn) });
        var special = self.paletteSpecial && self.paletteSpecial[cn];
        var cell = btn({
          class: "acfg__swatch" + (special && specialName(special) ? " has-special" : ""),
          title: paletteName(cn) + (special && specialName(special) ? " · " + specialName(special) + ", " + T("special_limited", "Limited qty") : ""),
          "data-num": String(cn),
          on: { click: function () { onPick(cn); } }
        }, [img, h("span", { class: "acfg__swatch-name", text: paletteName(cn) }, [specialLabel(special)])]);
        cell._num = cn; swatchEls.push(cell); grid.appendChild(cell);
      })(palNums[ni]);
    }
    wrap.appendChild(grid);
    // Seasonal note: shown while ANY colour slot of the build uses a special colour
    // (not just the active one; canonical picking moves on to the next slot).
    var specialNote = h("p", { class: "acfg__special-note", style: "display:none" });
    wrap.appendChild(specialNote);
    var foot = h("div", { class: "acfg__picker-foot" });
    wrap.appendChild(foot);
    return {
      el: wrap,
      update: function (activeNum, family) {
        famEls.forEach(function (b) { var on = b._fam === family; b.classList.toggle("is-on", on); ariaPressed(b, on); });
        var shown = 0;
        swatchEls.forEach(function (cell) {
          var visible = inFamily(cell._num, family);
          cell.classList.toggle("is-hidden", !visible);
          var cellOn = cell._num === activeNum;
          cell.classList.toggle("is-selected", cellOn);
          ariaPressed(cell, cellOn);
          if (visible) shown++;
        });
        var used = 0;
        (self.colourOrder || []).forEach(function (o) {
          var n = colourNumber(self.state[o.id]);
          if (!used && self.paletteSpecial && specialName(self.paletteSpecial[n])) used = n;
        });
        specialNote.style.display = used ? "" : "none";
        if (used) {
          var kind = self.paletteSpecial[used];
          var bare = paletteName(used).replace(/^\d+\s*/, "");
          var extra = T("colour_note_" + used, COLOUR_NOTES[used] || "");
          specialNote.innerHTML = "";
          specialNote.appendChild(h("b", { text: TF("special_note_" + kind, "%{name} is a " + SPECIAL_NAMES[kind] + " in limited quantity.", { name: bare }) }));
          if (extra) specialNote.appendChild(document.createTextNode(" " + extra));
        }
        var groupName = T("family_all", "All");
        for (var i = 0; i < FAMILY_GROUPS.length; i++) if (FAMILY_GROUPS[i][0] === family) groupName = T("family_" + FAMILY_GROUPS[i][0], FAMILY_GROUPS[i][1]);
        foot.textContent = family === "all"
          ? (self.paletteNums && self.paletteNums.length !== 28
              ? TF("n_colours_available", "%{n} colours available.", { n: shown })
              : T("all_colours_available", "28 colours available."))
          : TF("n_in_group", "%{n} in %{group}.", { n: shown, group: groupName });
      }
    };
  };

  // Canonical Y-cable: count picker + (conditional) arrangement + palette.
  Configurator.prototype._buildColoursCanonical = function (opts, num, title) {
    var self = this;
    var sec = h("section", { class: "acfg__section acfg__section--colours" });
    sec.appendChild(sectionHead(num, title, T("colours_hint_canonical", "Each earcup cable is two braided strands")));
    self._headValue(sec, function () { return String(self.state._numColours || 1); });

    // 0) Signature combos — one-tap curated palettes to anchor the step before the
    // 28-swatch grid (choice-overload relief, 2026-07-20 review). Applies through
    // the SAME setters the manual controls use; everything stays editable after.
    var presets = this._palettePresets();
    if (presets.length) {
      sec.appendChild(h("div", { class: "acfg__presets-label", text: T("presets_title", "Signature combos") }));
      var presetWrap = h("div", { class: "acfg__presets" });
      presets.forEach(function (p) {
        var dots = h("span", { class: "acfg__preset-swatches" });
        for (var d = 0; d < p.colours.length && d < 4; d++) {
          var dn = p.colours[d];
          var dot = h("span", { class: "acfg__preset-dot" });
          var hex = self.colourHex(dn); if (hex) dot.style.backgroundColor = hex;
          var img = self.assets.swatches && self.assets.swatches[dn - 1];
          if (img) dot.style.backgroundImage = "url('" + img + "')";
          dots.appendChild(dot);
        }
        presetWrap.appendChild(btn({ class: "acfg__preset", on: { click: function () { self._applyPreset(p); } } },
          [dots, h("span", { class: "acfg__preset-name", text: p.name })]));
      });
      sec.appendChild(presetWrap);
    }

    // 1) How many colours (always visible).
    sec.appendChild(h("p", { class: "acfg__q", text: T("how_many_colours", "How many colours?") }));
    sec.appendChild(h("p", { class: "acfg__q-hint", text: T("how_many_hint", "More colours create richer patterns. Most cables use one or two.") }));
    var countWrap = h("div", { class: "acfg__count", role: "group", "aria-label": T("number_of_colours_aria", "Number of colours") });
    var countEls = [1, 2, 3, 4].map(function (k) {
      var b = btn({ class: "acfg__count-btn", text: String(k), "aria-label": k + (k > 1 ? T("colours_suffix", " colours") : T("colour_suffix", " colour")),
        on: { click: function () { self._setColourCount(k); } } });
      b._k = k; countWrap.appendChild(b); return b;
    });
    sec.appendChild(countWrap);

    // 2) Arrangement: a real choice for 2–3 colours; a single non-interactive
    //    PREVIEW for 4 (one topology — every strand its own colour). Hidden for 1.
    var arrQ = h("p", { class: "acfg__q", text: T("where_do_colours_go", "Where do the colours go?") });
    var arrHint = h("p", { class: "acfg__q-hint", text: T("arrangement_hint", "The cable splits into two sides at the splitter. Pick how colours map onto each side.") });
    var arrSec = h("div", { class: "acfg__arrange" }, [arrQ, arrHint]);
    var arrGrid = h("div", { class: "acfg__arrange-grid" });
    arrSec.appendChild(arrGrid);
    sec.appendChild(arrSec);
    var arrCards = [];
    function renderArrangements() {
      // Signature includes the palette: the glyphs paint the REAL picked colours,
      // so a colour pick re-renders the cards (selection state is re-applied by
      // the update() loop right after).
      var n = self.state._numColours, pal = self.state._palette || [];
      var sig = "a" + n + "|" + pal.join(".");
      if (arrGrid._sig === sig) return;
      arrGrid._sig = sig; arrGrid.innerHTML = ""; arrCards = [];
      var preview = n === 4; // 4 colours has only one layout -> show it, don't offer a choice
      arrGrid.classList.toggle("acfg__arrange-grid--preview", preview);
      arrGrid.style.gridTemplateColumns = "repeat(" + arrangementsFor(n).length + ", 1fr)";
      arrangementsFor(n).forEach(function (a) {
        var card = btn({ class: "acfg__arrange-card", "aria-disabled": preview ? "true" : null,
          tabindex: preview ? "-1" : null, // inert preview card: keep it out of the tab order (aria-disabled alone doesn't)
          on: { click: function () { if (!preview) self._setArrangement(a.id); } } }, [
          h("span", { class: "acfg__arrange-glyph", html: arrangementGlyphSVG(a.slots, pal, function (cn) { return self.colourHex(cn); }) }),
          h("span", { class: "acfg__arrange-name", text: T("arrangement_" + a.id.replace(/-/g, "_") + "_name", a.name) }),
          h("span", { class: "acfg__arrange-desc", text: T("arrangement_" + a.id.replace(/-/g, "_") + "_desc", a.desc) }),
          preview ? null : h("span", { class: "acfg__arrange-check", "aria-hidden": "true" })
        ]);
        card._id = a.id; arrCards.push(card); arrGrid.appendChild(card);
      });
    }

    // 3) Palette: active-colour chips (Colour A…N) + the shared swatch picker.
    sec.appendChild(h("p", { class: "acfg__q acfg__q--pal", text: T("pick_your_colours", "Pick your colours.") }));
    var palHint = h("p", { class: "acfg__q-hint" });
    sec.appendChild(palHint);
    var chipsWrap = h("div", { class: "acfg__palchips" });
    sec.appendChild(chipsWrap);
    var chipEls = [];
    function renderChips() {
      var n = self.state._numColours, sig = "c" + n;
      if (chipsWrap._sig === sig) return;
      chipsWrap._sig = sig; chipsWrap.innerHTML = ""; chipEls = [];
      chipsWrap.style.display = n < 2 ? "none" : "";
      if (n < 2) return;
      for (var i = 0; i < n; i++) {
        (function (idx) {
          var thumb = h("span", { class: "acfg__palchip-thumb" });
          var name = h("span", { class: "acfg__palchip-name" });
          var b = btn({ class: "acfg__palchip", on: { click: function () { self.setState(kv("_activePaletteIdx", idx)); } } },
            [thumb, h("span", { class: "acfg__palchip-meta" }, [h("span", { class: "acfg__palchip-label", text: TF("colour_letter", "Colour %{letter}", { letter: String.fromCharCode(65 + idx) }) }), name])]);
          b._idx = idx; chipEls.push({ btn: b, thumb: thumb, name: name }); chipsWrap.appendChild(b);
        })(i);
      }
    }

    var picker = this._buildSwatchPicker(function (n) { self._pickColour(n); });
    sec.appendChild(picker.el);

    // Inline sample prompt — the free braid-photo service answers exactly the doubt
    // THIS step creates ("will these look good together?"), but the button used to
    // sit only in the endrow below the payment icons. Triggers the same flow.
    if (this._sampleEligible()) {
      sec.appendChild(h("p", { class: "acfg__sample-inline" }, [
        btn({ class: "acfg__sample-inline-btn", on: { click: function () {
          self.requestSample();
          var w = (self._sampleWidgets && self._sampleWidgets[0] && self._sampleWidgets[0].root) || null;
          if (w && typeof w.scrollIntoView === "function") w.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "center" });
        } } }, T("sample_colours_prompt", "Unsure on colours? We'll email you a free photo of this exact braid"))
      ]));
    }

    return {
      el: sec,
      update: function (st) {
        countEls.forEach(function (b) { var on = b._k === st._numColours; b.classList.toggle("is-on", on); ariaPressed(b, on); });
        var showArr = st._numColours >= 2; // 2–3 = choice, 4 = single-layout preview, 1 = hidden
        arrSec.style.display = showArr ? "" : "none";
        if (showArr) {
          var four = st._numColours === 4;
          arrQ.textContent = four ? T("four_strand_layout", "Your four-strand layout") : T("where_do_colours_go", "Where do the colours go?");
          arrHint.textContent = four
            ? T("four_colours_hint", "Four colours — one on each strand. Here's how they sit across the two sides.")
            : T("arrangement_hint", "The cable splits into two sides at the splitter. Pick how colours map onto each side.");
          renderArrangements();
          arrCards.forEach(function (c) { var on = c._id === st._arrangement; c.classList.toggle("is-selected", on); ariaPressed(c, on); });
        }
        renderChips();
        palHint.textContent = st._numColours < 2 ? T("one_colour_hint", "One colour for all four strands.") : T("tap_chip_hint", "Tap a chip to choose which colour you're editing.");
        var pal = st._palette || [];
        chipEls.forEach(function (ref, i) {
          var cn = pal[i] || 1;
          ref.thumb.style.backgroundColor = self.colourHex(cn); ref.thumb.style.backgroundImage = "url('" + self.swatchSrc(cn) + "')";
          ref.name.textContent = paletteName(cn);
          var chipOn = (st._activePaletteIdx || 0) === i;
          ref.btn.classList.toggle("is-active", chipOn);
          ariaPressed(ref.btn, chipOn);
        });
        var activeNum = (st._palette && st._palette[st._activePaletteIdx || 0]) || 1;
        picker.update(activeNum, st._family);
      }
    };
  };

  // Non-canonical colour sets: one chip per real slot + the shared swatch picker.
  Configurator.prototype._buildColoursGeneric = function (opts, num, title) {
    var self = this;
    var sec = h("section", { class: "acfg__section acfg__section--colours" });
    sec.appendChild(sectionHead(num, title, this.colourOrder.length > 1 ? T("tap_slot_hint", "Tap a slot, then pick its colour") : null));
    var chipsWrap = h("div", { class: "acfg__palchips" });
    sec.appendChild(chipsWrap);
    var slotEls = {};
    this.colourOrder.forEach(function (o) {
      var thumb = h("span", { class: "acfg__palchip-thumb" });
      var name = h("span", { class: "acfg__palchip-name" });
      var b = btn({ class: "acfg__palchip", on: { click: function () { self.setState(kv("_activeColour", o.id)); } } },
        [thumb, h("span", { class: "acfg__palchip-meta" }, [h("span", { class: "acfg__palchip-label", text: fieldTitle(o.globo_field) }), name])]);
      slotEls[o.id] = { btn: b, thumb: thumb, name: name }; chipsWrap.appendChild(b);
    });
    var picker = this._buildSwatchPicker(function (n) { self._pickColourGeneric(n); });
    sec.appendChild(picker.el);
    return {
      el: sec,
      update: function (st) {
        self.colourOrder.forEach(function (o) {
          var cn = colourNumber(st[o.id]), ref = slotEls[o.id];
          if (ref) {
            ref.thumb.style.backgroundColor = self.colourHex(cn); ref.thumb.style.backgroundImage = "url('" + self.swatchSrc(cn) + "')";
            ref.name.textContent = paletteName(cn);
            var slotOn = st._activeColour === o.id;
            ref.btn.classList.toggle("is-active", slotOn);
            ariaPressed(ref.btn, slotOn);
          }
        });
        var activeOpt = self._optById(st._activeColour);
        var activeNum = activeOpt ? colourNumber(st[activeOpt.id]) : -1;
        picker.update(activeNum, st._family);
      }
    };
  };

  /* ---- Medusa (sleeved) split colour model: Base/Left/Right + live cable preview ---- */
  function medusaSlotLabel(seg) { return seg === "base" ? T("medusa_base", "Base") : seg === "left" ? T("medusa_left", "Left") : T("medusa_right", "Right"); }
  // "Worn" composition (Albert's pick, 2026-07-02): the cable dressed on a blurred
  // mannequin head — two leads run up to the ears (wearer's left = viewer right),
  // meet at the real Arachne leather splitter, and the base trunk drops below.
  // Each segment carries data-seg so the builder can repaint it from the chosen
  // colour and clicking it selects that slot. Purely cosmetic — no cart effect.
  function medusaCableSVG(assets) {
    var img = (assets && assets.medusa) || {};
    // Segment layers: selection halo (thin accent rim, CSS-toggled) → casing (edge so
    // black reads on black) → colour "fill" (repainted) → sleeve-weave texture →
    // sheen highlight → letter-spaced label. Lead ends are bare rounded caps, like
    // the trunk end (no ear-pad blocks). Texture paint lives on an attribute because
    // {% stylesheet %} compiles to an external file where url(#id) would not resolve.
    function seg(name, mod, d, ds, tx, ty) {
      return '<g class="acfg__mpreview-seg' + (mod ? ' acfg__mpreview-seg--' + mod : '') + '" data-seg="' + name + '">'
        + '<path class="acfg__mpreview-halo" d="' + d + '"></path>'
        + '<path class="acfg__mpreview-casing" d="' + d + '"></path>'
        + '<path class="acfg__mpreview-fill" stroke="#8a8a8a" d="' + d + '"></path>'
        + '<path class="acfg__mpreview-tex" stroke="url(#acfgMweave)" d="' + d + '"></path>'
        + '<path class="acfg__mpreview-sheen" d="' + ds + '"></path>'
        + '<text class="acfg__mpreview-tag" x="' + tx + '" y="' + ty + '" text-anchor="middle">' + medusaSlotLabel(name) + '</text>'
        + '</g>';
    }
    return '<svg viewBox="0 0 360 580" class="acfg__mpreview-svg" role="img" aria-label="' + T("medusa_preview_aria", "Live preview of your cable colours on a mannequin") + '">'
      + '<defs><pattern id="acfgMweave" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(48)">'
      + '<line x1="0" y1="0" x2="0" y2="7" stroke="rgba(255,255,255,0.09)" stroke-width="2.6"></line>'
      + '</pattern></defs>'
      + (img.head ? '<image class="acfg__mpreview-head" href="' + img.head + '" x="133" y="25" width="94" height="134"></image>' : '')
      + seg("right", null, "M128,174 C124,232 154,270 178,300", "M125,174 C121,231 151,268 175,298", 76, 196)
      + seg("left", null, "M232,174 C236,232 206,270 182,300", "M229,174 C233,231 203,268 179,298", 286, 196)
      + seg("base", "base", "M180,300 C180,366 181,448 180,524", "M176,302 C176,366 177,446 176,522", 236, 508)
      + '<ellipse class="acfg__mpreview-shadow" cx="180" cy="302" rx="34" ry="11"></ellipse>'
      + (img.splitter ? '<image class="acfg__mpreview-splitter" href="' + img.splitter + '" x="158" y="283" width="43" height="91"></image>' : '')
      + '</svg>';
  }
  Configurator.prototype._buildColoursMedusa = function (opts, num, title) {
    var self = this;
    var segOf = {}; segOf[this.medusaSlots.base.id] = "base"; segOf[this.medusaSlots.left.id] = "left"; segOf[this.medusaSlots.right.id] = "right";
    var sec = h("section", { class: "acfg__section acfg__section--colours acfg__section--medusa" });
    sec.appendChild(sectionHead(num, title, T("medusa_hint", "Tap a part of the cable (or a slot), then pick its colour. Base sets the whole cable — Left/Right override it")));
    var preview = h("div", { class: "acfg__mpreview", html: medusaCableSVG(this.assets) });
    sec.appendChild(preview);
    sec.appendChild(h("p", { class: "acfg__mpreview-note", text: T("medusa_preview_note", "Illustrative preview — shows your colours, not exact proportions") }));
    var segNodes = {};
    ["base", "left", "right"].forEach(function (seg) {
      var node = preview.querySelector('[data-seg="' + seg + '"]');
      segNodes[seg] = node;
      if (node) node.addEventListener("click", function () { var o = self.medusaSlots[seg]; if (o) self.setState(kv("_activeColour", o.id)); });
    });
    var chipsWrap = h("div", { class: "acfg__palchips acfg__palchips--medusa" });
    sec.appendChild(chipsWrap);
    var slotEls = {};
    this.colourOrder.forEach(function (o) {
      var seg = segOf[o.id];
      var thumb = h("span", { class: "acfg__palchip-thumb" });
      var name = h("span", { class: "acfg__palchip-name" });
      // Leads show a small "matches base" tag while they follow the base colour
      // (i.e. untouched + same value); it disappears the moment they're overridden.
      var tag = seg === "base" ? null : h("span", { class: "acfg__palchip-tag", text: T("matches_base", "matches base") });
      var metaKids = [h("span", { class: "acfg__palchip-label", text: medusaSlotLabel(seg) }), name];
      if (tag) metaKids.push(tag);
      var b = btn({ class: "acfg__palchip", "data-seg": seg, on: { click: function () { self.setState(kv("_activeColour", o.id)); } } },
        [thumb, h("span", { class: "acfg__palchip-meta" }, metaKids)]);
      slotEls[o.id] = { btn: b, thumb: thumb, name: name, tag: tag }; chipsWrap.appendChild(b);
    });
    var picker = this._buildSwatchPicker(function (n) { self._pickColourGeneric(n); });
    sec.appendChild(picker.el);
    return {
      el: sec,
      update: function (st) {
        var baseOpt = self.medusaSlots.base;
        self.colourOrder.forEach(function (o) {
          var cn = colourNumber(st[o.id]), ref = slotEls[o.id], seg = segOf[o.id];
          if (ref) {
            ref.thumb.style.backgroundColor = self.colourHex(cn); ref.thumb.style.backgroundImage = "url('" + self.swatchSrc(cn) + "')";
            ref.name.textContent = paletteName(cn);
            var slotOn = st._activeColour === o.id;
            ref.btn.classList.toggle("is-active", slotOn);
            ariaPressed(ref.btn, slotOn);
            if (ref.tag) {
              var follows = !st["_mTouched_" + seg] && baseOpt && st[o.id] === st[baseOpt.id];
              ref.tag.hidden = !follows;
            }
          }
          if (segNodes[seg]) {
            var fillPath = segNodes[seg].querySelector(".acfg__mpreview-fill");
            if (fillPath) fillPath.setAttribute("stroke", self.colourHex(cn) || "#8a8a8a");
            segNodes[seg].classList.toggle("is-active", st._activeColour === o.id);
          }
        });
        var activeOpt = self._optById(st._activeColour);
        var activeNum = activeOpt ? colourNumber(st[activeOpt.id]) : -1;
        picker.update(activeNum, st._family);
      }
    };
  };

  Configurator.prototype._optById = function (id) {
    for (var i = 0; i < this.options.length; i++) if (this.options[i].id === id) return this.options[i];
    return null;
  };

  /* ---- canonical colour model: palette + count + arrangement <-> 4 slot labels ---- */
  // Pure: the four strand-label patch for a palette + count + arrangement. Always
  // emits real value labels via labelForNum, so no slot can ever be emptied.
  Configurator.prototype._colourSlotPatch = function (pal, n, arrId) {
    var arr = arrangementById(n, arrId), patch = {};
    for (var i = 0; i < 4; i++) {
      var cn = pal[arr.slots[i]]; if (cn == null) cn = pal[0];
      if (this.colourOrder[i]) patch[this.colourOrder[i].id] = labelForNum(this.colourOrder[i], cn);
    }
    return patch;
  };
  // Derive numColours / arrangement / palette from the four current strand labels
  // (UI only — never alters the cart labels). Inverse of _colourSlotPatch.
  Configurator.prototype._inferColourModel = function () {
    if (!this.canonicalColour) return;
    var self = this;
    var nums = this.colourOrder.map(function (o) { return colourNumber(self.state[o.id]) || 1; });
    var m = inferArrangement(nums);
    var pal = m.pal.slice();
    while (pal.length < 4) pal.push(pal[pal.length - 1] || 1);
    this.state._numColours = m.num;
    this.state._arrangement = m.arr;
    this.state._palette = pal;
    if ((this.state._activePaletteIdx || 0) >= m.num) this.state._activePaletteIdx = 0;
  };
  // ---- signature colour presets (canonical Aranea only) ----
  // Data: settings.palette_presets (JSON string from the section setting) — each
  // entry {name, colours:[…]} with colours as swatch numbers (1-29) or full labels
  // ("8 Wine"). Invalid/blank input falls back to the built-in set; an explicit []
  // hides the presets. Purely a shortcut into the existing colour setters.
  var PRESET_DEFAULTS = [
    { name: "Stealth", colours: [1, 2] },
    { name: "Classic Silver", colours: [3] },
    { name: "Wine & Gold", colours: [8, 13] },
    { name: "Forest", colours: [26, 25] },
    { name: "Neon Pop", colours: [28, 1] }
  ];
  Configurator.prototype._palettePresets = function () {
    var raw = this.settings.palette_presets;
    var list = null;
    if (typeof raw === "string" && clean(raw)) {
      try { list = JSON.parse(raw); } catch (e) { list = null; }
    }
    if (Object.prototype.toString.call(list) !== "[object Array]") list = null;
    if (list === null) list = PRESET_DEFAULTS; // an explicit [] stays [] and hides the presets
    if (!list.length) return [];
    var out = [];
    for (var i = 0; i < list.length && out.length < 6; i++) {
      var p = list[i];
      if (!p || typeof p.name !== "string" || !p.colours || !p.colours.length) continue;
      var nums = [];
      for (var j = 0; j < p.colours.length && nums.length < 4; j++) {
        var n = parseInt(String(p.colours[j]).trim(), 10); // "8 Wine" and 8 both parse to 8
        if (n >= 1 && PALETTE[n]) nums.push(n);
      }
      if (nums.length) out.push({ name: p.name, colours: nums });
    }
    return out;
  };
  Configurator.prototype._applyPreset = function (p) {
    var nums = p.colours || [];
    var n = Math.max(1, Math.min(4, nums.length));
    var pal = nums.slice(0, 4);
    while (pal.length < 4) pal.push(pal[pal.length - 1] || 1);
    var arrId = arrangementsFor(n)[0].id;
    var patch = { _numColours: n, _arrangement: arrId, _palette: pal, _activePaletteIdx: 0 };
    Object.assign(patch, this._colourSlotPatch(pal, n, arrId));
    this.setState(patch);
    track("preset", { name: p.name });
  };

  Configurator.prototype._setColourCount = function (n) {
    n = Math.max(1, Math.min(4, n));
    var pal = (this.state._palette || [1, 1, 1, 1]).slice();
    while (pal.length < 4) pal.push(pal[pal.length - 1] || 1);
    var arrId = arrangementsFor(n)[0].id; // a count change resets to the first valid arrangement
    var idx = this.state._activePaletteIdx || 0; if (idx >= n) idx = 0;
    var patch = { _numColours: n, _arrangement: arrId, _palette: pal, _activePaletteIdx: idx };
    Object.assign(patch, this._colourSlotPatch(pal, n, arrId));
    this.setState(patch);
  };
  Configurator.prototype._setArrangement = function (id) {
    var n = this.state._numColours || 1, pal = this.state._palette || [1, 1, 1, 1];
    var patch = { _arrangement: id };
    Object.assign(patch, this._colourSlotPatch(pal, n, id));
    this.setState(patch);
  };
  // Canonical: paint the active palette colour, then advance to the next palette entry.
  Configurator.prototype._pickColour = function (num) {
    if (!this.canonicalColour) return this._pickColourGeneric(num);
    var n = this.state._numColours || 1;
    var idx = this.state._activePaletteIdx || 0; if (idx >= n) idx = 0;
    var pal = (this.state._palette || [1, 1, 1, 1]).slice();
    pal[idx] = num;
    var patch = { _palette: pal, _activePaletteIdx: (idx + 1) % n };
    Object.assign(patch, this._colourSlotPatch(pal, n, this.state._arrangement));
    this.setState(patch);
  };
  // Non-canonical: set the active slot directly, then advance to the next slot.
  Configurator.prototype._pickColourGeneric = function (num) {
    var activeId = this.state._activeColour, activeOpt = this._optById(activeId);
    if (!activeOpt) return;
    var patch = kv(activeId, labelForNum(activeOpt, num));
    if (this.medusaSplit) {
      // Base recolours the whole cable (saves two clicks on single-colour builds) —
      // but never a lead the customer explicitly set (_mTouched_*) or one that
      // already differs from the current base (e.g. a cart-edit / share prefill).
      var slots = this.medusaSlots, self = this;
      if (slots.base && activeId === slots.base.id) {
        var oldBase = this.state[slots.base.id];
        ["left", "right"].forEach(function (k) {
          var o = slots[k];
          if (o && !self.state["_mTouched_" + k] && self.state[o.id] === oldBase) patch[o.id] = labelForNum(o, num);
        });
      } else if (slots.left && activeId === slots.left.id) patch._mTouched_left = true;
      else if (slots.right && activeId === slots.right.id) patch._mTouched_right = true;
    }
    var order = this.colourOrder.map(function (o) { return o.id; });
    var pos = order.indexOf(activeId);
    patch._activeColour = order[(pos + 1) % order.length];
    this.setState(patch);
  };

  // v4: per-page palette override — built in _prepare from the colour option's own
  // values when any of them carry `hex`/`image` (the ProAudio paracord palette).
  // Module-level like STRINGS (one configurator per page in practice); null = the
  // legacy 28-colour behaviour, unchanged.
  var PAL_OVERRIDE = null;
  function paletteName(num) {
    if (PAL_OVERRIDE && PAL_OVERRIDE[num]) return num + " " + PAL_OVERRIDE[num].name;
    return num + " " + (PALETTE[num] || "Colour");
  }
  // Reference palette names (1-based). Used only for labels/titles; the
  // submitted value is always the option's own label from the data.
  var PALETTE = {
    1: "Black", 2: "Charcoal", 3: "Silver", 4: "White", 5: "Panther", 6: "Basilisk", 7: "Bear",
    8: "Wine", 9: "Red", 10: "Sunset", 11: "Orange", 12: "Yellow", 13: "Gold", 14: "Embergold",
    15: "Dusk", 16: "Marine", 17: "Turquoise", 18: "Lavender", 19: "Purple", 20: "Dragonfruit",
    21: "Pink", 22: "Neptune", 23: "Seaglass", 24: "Teal", 25: "Sage", 26: "Forest", 27: "Green", 28: "Neon",
    29: "Spooky Skeleton" // glow-in-the-dark, Halloween 2026
  };
  // Optional one-line colour descriptions (English fallbacks; section strings colour_note_<n>).
  var COLOUR_NOTES = { 29: "White in daylight, glows green in the dark." };
  // Find the option's actual value-label for a given colour number (preserves
  // the data's exact label text — never fabricates one).
  function labelForNum(opt, num) {
    for (var i = 0; i < opt.values.length; i++) if (colourNumber(opt.values[i].label) === num) return opt.values[i].label;
    return opt.values[0].label;
  }

  /* ------------------------------ section: braiding ----------------------- */
  var BRAID_PATTERNS = {
    spiral: "repeating-linear-gradient(125deg,#444 0 6px,#2b2b2b 6px 12px)",
    parallel: "repeating-linear-gradient(90deg,#444 0 6px,#272727 6px 12px)",
    ribbon: "repeating-linear-gradient(0deg,#444 0 7px,#2b2b2b 7px 14px)"
  };
  function braidPattern(label) {
    var f = lc(label);
    if (/spiral/.test(f)) return BRAID_PATTERNS.spiral;
    if (/parallel/.test(f)) return BRAID_PATTERNS.parallel;
    if (/ribbon|flat|wov/.test(f)) return BRAID_PATTERNS.ribbon;
    return BRAID_PATTERNS.spiral;
  }
  // Map a braiding value label to its real Globo photo key (or null -> CSS fallback).
  function braidKey(label) {
    var f = lc(label);
    if (/spiral/.test(f)) return "spiral";
    if (/parallel/.test(f)) return "parallel";
    if (/ribbon|flat|wov/.test(f)) return "ribbon";
    return null;
  }
  // Per-weave notes adopted from the design prototype (anglicised for the brand).
  var BRAID_NOTES = {
    spiral: "Twisted rope — colours wind around each other",
    parallel: "Tight 4-strand round braid — colours mix evenly",
    ribbon: "Flat 4-strand braid — chevron pattern"
  };
  Configurator.prototype._buildBraiding = function (opt, num, title) {
    var self = this;
    var sec = h("section", { class: "acfg__section acfg__section--braiding" });
    sec.appendChild(sectionHead(num, title));
    self._headValue(sec, function () { return clean(self.selectedValue(opt).label); });
    sec.appendChild(h("p", { class: "acfg__q", text: T("pick_the_weave", "Pick the weave.") }));
    sec.appendChild(h("p", { class: "acfg__q-hint", text: T("weave_hint", "Each pattern is hand-braided. Spiral has the tightest twist; Ribbon lays flat.") }));
    var grid = h("div", { class: "acfg__tiles" });
    var braids = this.assets.braids || {};
    var tiles = opt.values.map(function (v) {
      // Show the real braiding PHOTO (matched by label). If the asset is missing /
      // 404s, fall back to the procedural CSS weave pattern — never a blank tile.
      var bkey = braidKey(v.label);
      var bsrc = bkey && braids[bkey] ? braids[bkey] : null;
      var patEl = h("span", { class: "acfg__braid-pattern" });
      bgLoadFirst(patEl, [bsrc], function () { patEl.style.backgroundImage = braidPattern(v.label); });
      var t = btn({
        class: "acfg__tile",
        on: { click: function () { self.setState(kv(opt.id, v.label)); } }
      }, [
        patEl,
        bkey === "spiral" ? h("span", { class: "acfg__rec-badge", text: T("popular", "Popular") }) : null,
        h("span", { class: "acfg__tile-name", text: clean(v.label) }),
        bkey && BRAID_NOTES[bkey] ? h("span", { class: "acfg__tile-note", text: T("weave_note_" + bkey, BRAID_NOTES[bkey]) }) : null,
        v.addon ? h("span", { class: "acfg__tile-price", text: "+" + money(v.addon, self.currency) }) : null,
        h("span", { class: "acfg__tile-check", "aria-hidden": "true" })
      ]);
      t._label = v.label; grid.appendChild(t); return t;
    });
    sec.appendChild(grid);
    return { el: sec, update: function (st) { tiles.forEach(function (t) { var on = t._label === st[opt.id]; t.classList.toggle("is-selected", on); ariaPressed(t, on); }); } };
  };

  /* ------------------------------ section: splitter ----------------------- */
  function splitterKey(label) {
    var f = lc(label);
    if (/none|braid/.test(f)) return "none";
    if (/heat/.test(f)) return "heatshrink";
    if (/metal/.test(f)) return "metal";
    if (/leather/.test(f)) return "leather";
    return null;
  }
  // Per-finish notes adopted from the design prototype.
  var SPLITTER_SUBS = {
    none: "Strands re-braid into a single cable",
    heatshrink: "Branded shrink-tube sleeve",
    metal: "Anodized aluminium barrel",
    leather: "Hand-sewn cow leather"
  };
  Configurator.prototype._buildSplitter = function (opt, num, title) {
    var self = this;
    var sec = h("section", { class: "acfg__section acfg__section--splitter" });
    sec.appendChild(sectionHead(num, title));
    self._headValue(sec, function () { return clean(self.selectedValue(opt).label).split(" ")[0]; });
    sec.appendChild(h("p", { class: "acfg__q", text: T("pick_the_splitter", "Pick the splitter.") }));
    sec.appendChild(h("p", { class: "acfg__q-hint", text: T("splitter_hint", "Purely decorative — no effect on sound. Three finishes, plus the option to skip the hardware entirely.") }));
    var grid = h("div", { class: "acfg__split-tiles" });
    // The splitter PHOTOS live under the `splitters` key (splitter-metal.jpg …), with
    // the generic placeholder as the only fallback — never a blank tile. (The old
    // splitters_globo thumbnail fallback was removed 2026-07-20: those files were
    // byte-different old widget thumbs that the per-finish crop values would have
    // mis-framed badly if they ever actually loaded.)
    var real = (this.assets.splitters || {});
    var placeholder = this.assets.placeholder || "";
    var tiles = opt.values.map(function (v) {
      var key = splitterKey(v.label);
      // Per-finish modifier so the CSS can re-crop each photo (the source shots
      // carry FRONT/BACK captions that a plain cover-crop leaves half-visible).
      var imgEl = h("span", { class: "acfg__split-img" + (key ? " acfg__split-img--" + key : "") });
      bgLoadFirst(imgEl, [key && real[key], placeholder], null);
      var t = btn({
        class: "acfg__split",
        on: { click: function () { self.setState(kv(opt.id, v.label)); } }
      }, [
        imgEl,
        h("span", { class: "acfg__split-check", "aria-hidden": "true" }),
        h("span", { class: "acfg__split-name", text: clean(v.label) }),
        key && SPLITTER_SUBS[key] ? h("span", { class: "acfg__split-note", text: T("splitter_note_" + key, SPLITTER_SUBS[key]) }) : null,
        h("span", { class: "acfg__split-price", text: v.addon ? "+" + money(v.addon, self.currency) : "" })
      ]);
      t._label = v.label; grid.appendChild(t); return t;
    });
    sec.appendChild(grid);
    return { el: sec, update: function (st) { tiles.forEach(function (t) { var on = t._label === st[opt.id]; t.classList.toggle("is-selected", on); ariaPressed(t, on); }); } };
  };

  /* ------------------------------ section: strap --------------------------- */
  // Fermata Leather Cable Organizer — an optional add-on step shaped like the
  // splitter: a localized "No organizer" value is the free default, each colour
  // tile is a real Fermata variant. Prices and images ride IN the option values
  // (built by cable-config-build.liquid from the live product, so Markets
  // presentment pricing holds): `addon` = ×0.8 bundle price (mirrors the
  // "Fermata: 20% off with your cable" automatic discount — totals promise what
  // checkout charges), `full` = undiscounted price for the struck tile display.
  // The craft story and a bigger photo live behind an "About the Fermata"
  // disclosure so the step itself stays as light as the others. Since the
  // 2026-08 reshoot each colour also carries an in-use shot ("image2", a theme
  // asset — naming contract in the build snippet): while a colour is selected
  // the BIG MEDIA STAGE shows it (_syncStrapMedia; Albert's call — "see in big
  // how it looks and is used") and the About photo follows too; tiles keep
  // their product shots. Missing files (Graphite2 pending) degrade silently
  // via probe-first loads.
  // The none value is flagged by the Liquid block ("none":true) because its label
  // is localized — never sniff translated label text. The string fallback covers
  // stale resume-a-build/share states that still carry the old "No strap" label.
  // SOLD OUT (2026-09-07): a colour whose Shopify variant is unavailable arrives
  // as `soldout:true` (addon 0, no variant_id, no image2). It renders as a
  // disabled tile with a localized "Sold out" badge so the customer sees the
  // colour exists; it can never be selected — clicks are off, prefillFromLabels
  // skips it (share link / cart edit / resume fall back to the default), and
  // with no variant_id it could never reach the cart anyway.
  function strapIsNone(v) {
    if (v && typeof v === "object") return v.none === true || /^no\b|none/.test(lc(v.label));
    return /^no\b|none/.test(lc(v));
  }
  Configurator.prototype._buildStrap = function (opt, num, title) {
    var self = this;
    var sec = h("section", { class: "acfg__section acfg__section--strap" });
    sec.appendChild(sectionHead(num, title));
    // Full label, not first-word: "No strap" must not truncate to "No".
    self._headValue(sec, function () { return clean(self.selectedValue(opt).label); });
    sec.appendChild(h("p", { class: "acfg__q", text: T("pick_the_strap", "Add a Fermata Leather Cable Organizer?") }));
    // Bundle-price hint: %{standard_price}/%{bundle_price} are filled from the
    // first real variant (full = undiscounted, addon = the ×0.8 price the
    // automatic discount lands at). With no real variant (all sold out) the
    // price parenthetical is stripped instead of leaking raw tokens.
    var firstReal = null;
    for (var vi = 0; vi < opt.values.length; vi++) { if (opt.values[vi].variant_id) { firstReal = opt.values[vi]; break; } }
    var hintText = firstReal
      ? TF("strap_hint", "A coiled cable held with velcro is like studio monitors on milk crates. Let the Fermata do the holding: hand-cut Badalassi Carlo leather (usually %{standard_price} · %{bundle_price} with this cable).", {
          standard_price: moneyTrim(firstReal.full != null ? firstReal.full : firstReal.addon, self.currency),
          bundle_price: moneyTrim(firstReal.addon, self.currency)
        })
      : T("strap_hint", "A coiled cable held with velcro is like studio monitors on milk crates. Let the Fermata do the holding: hand-cut Badalassi Carlo leather.").replace(/\s*\([^()]*\)\s*\.?\s*$/, ".");
    sec.appendChild(h("p", { class: "acfg__q-hint", text: hintText }));
    var grid = h("div", { class: "acfg__split-tiles acfg__strap-tiles" });
    var placeholder = this.assets.placeholder || "";
    // Tiles always show the product shot; per-colour slug classes carry the
    // measured centring crops in the section CSS. The in-use shot (v.image2)
    // appears on the big media stage instead — see _syncStrapMedia. The "none"
    // value is typographic: its label sits big inside the square, no caption.
    var tiles = opt.values.map(function (v) {
      var none = strapIsNone(v);
      var soldout = v.soldout === true; // out of stock: shown, never selectable
      var slug = none ? "" : " acfg__strap-img--" + lc(clean(v.label)).replace(/[^a-z0-9]+/g, "-");
      var imgEl = h("span", { class: "acfg__split-img acfg__strap-img" + (none ? " acfg__strap-img--none" : slug) });
      if (!none) bgLoadFirst(imgEl, [v.image, placeholder], null);
      else imgEl.appendChild(h("span", { class: "acfg__strap-none-label", text: clean(v.label) }));
      var t = btn({
        class: "acfg__split acfg__strap" + (soldout ? " is-soldout" : ""),
        disabled: soldout ? true : null,
        "aria-disabled": soldout ? "true" : null,
        on: soldout ? null : { click: function () { self.setState(kv(opt.id, v.label)); } }
      }, [
        imgEl,
        h("span", { class: "acfg__split-check", "aria-hidden": "true" }),
        none ? null : h("span", { class: "acfg__split-name", text: clean(v.label) }),
        soldout ? h("span", { class: "acfg__split-soldout", text: T("sold_out", "Sold out") })
                : h("span", { class: "acfg__split-price", text: v.addon ? "+" + money(v.addon, self.currency) : "" }),
        !soldout && v.full && v.full > v.addon ? h("s", { class: "acfg__split-price-was", text: money(v.full, self.currency) }) : null,
        none ? null : specialLabel(v.special, "acfg__special--tile", true)
      ]);
      t._label = v.label; grid.appendChild(t); return t;
    });
    // Warm the in-use shots once the organizer step is actually on screen so
    // the stage swap lands instantly (~200-500KB each; skipped on Save-Data).
    if (typeof IntersectionObserver === "function" && typeof Image === "function" &&
        !(typeof navigator !== "undefined" && navigator.connection && navigator.connection.saveData)) {
      var warmVals = opt.values.filter(function (v) { return !v.soldout && (v.image2 || v.image_big); });
      if (warmVals.length) {
        var wio = new IntersectionObserver(function (entries) {
          for (var wi = 0; wi < entries.length; wi++) {
            if (entries[wi].isIntersecting) {
              wio.disconnect();
              warmVals.forEach(function (v, n) { setTimeout(function () {
                var im = new Image();
                // in-use shot missing -> warm its stand-in product shot instead
                if (v.image2 && v.image_big) im.onerror = function () { (new Image()).src = v.image_big; };
                im.src = v.image2 || v.image_big;
              }, 300 + n * 200); });
              return;
            }
          }
        }, { rootMargin: "200px" });
        wio.observe(grid);
      }
    }
    sec.appendChild(grid);
    // "About the Fermata" disclosure (Albert's expand-for-info): bigger photo
    // (click/Enter opens the lightbox with a single-image list, so no gallery
    // arrows), 2-3 craft lines, and a PDP link that opens in a NEW TAB so the
    // build in progress isn't lost.
    // The About photo follows the selection: the picked colour's in-use shot
    // (image2), the product hero when nothing is picked or the file is missing.
    // Swaps are deferred while the panel is closed (the probe would force a
    // fetch the browser skips for hidden backgrounds) and applied on open.
    var aboutPhoto = null, aboutPanel = null;
    function applyAbout() {
      if (!aboutPhoto || aboutPanel.hidden) return;
      // Selected colour: in-use shot, else its big product shot (Graphite2
      // pending — Albert 2026-08-01), else the hero. No selection: the hero.
      var wantedKey = function () {
        var sv = self.selectedValue(opt);
        var c = (sv && !strapIsNone(sv)) ? [sv.image2, sv.image_big, opt.about_image] : [opt.about_image];
        return c.filter(Boolean).join("|");
      };
      var key = wantedKey();
      if (!key) return;
      var list = key.split("|");
      if (aboutPhoto._for === list[0]) return;
      probeFirst(list, function () { return wantedKey() === key; }, function (u) {
        if (u) { aboutPhoto._for = u; aboutPhoto.style.backgroundImage = "url('" + u + "')"; }
      });
    }
    if (opt.about_image) {
      var panelId = "acfg-strap-about-" + num;
      var photo = h("div", { class: "acfg__strap-about-photo", role: "button", tabindex: "0",
        "aria-label": T("zoom_prefix", "Zoom: ") + "Fermata" });
      photo._for = opt.about_image;
      bgLoadFirst(photo, [opt.about_image], null);
      // Zoom opens whatever the photo currently shows (hero or in-use shot).
      var openZoom = function () { self._openLightbox(photo.style.backgroundImage, 0, [{ src: photo._for, alt: "Fermata" }]); };
      photo.addEventListener("click", openZoom);
      photo.addEventListener("keydown", function (e) { if (e.key === "Enter" || e.key === " " || e.keyCode === 13 || e.keyCode === 32) { e.preventDefault(); openZoom(); } });
      var body = h("div", { class: "acfg__strap-about-body" }, [
        h("p", { class: "acfg__strap-about-text", text: T("strap_about_text", "A fermata holds a note for as long as it feels right. This one holds your coiled cable. Hand-cut by us from Badalassi Carlo vegetable-tanned leather and closed with a solid brass stud, it starts structured and ages into a deep personal patina.") }),
        opt.about_url ? h("a", { class: "acfg__strap-about-link", href: opt.about_url, target: "_blank", rel: "noopener", text: T("strap_about_link", "See the Fermata page") }) : null
      ]);
      var panel = h("div", { class: "acfg__strap-about", id: panelId }, [photo, body]);
      panel.hidden = true;
      aboutPhoto = photo; aboutPanel = panel;
      var tog = btn({ class: "acfg__strap-about-toggle", "aria-expanded": "false", "aria-controls": panelId,
        text: T("strap_about", "About the Fermata"),
        on: { click: function () {
          var opening = panel.hidden;
          panel.hidden = !opening;
          tog.setAttribute("aria-expanded", opening ? "true" : "false");
          tog.classList.toggle("is-open", opening);
          if (opening) applyAbout();
        } } });
      sec.appendChild(tog);
      sec.appendChild(panel);
    }
    return { el: sec, update: function (st) {
      tiles.forEach(function (t) { var on = t._label === st[opt.id]; t.classList.toggle("is-selected", on); ariaPressed(t, on); });
      applyAbout();
    } };
  };

  /* Media-stage Fermata takeover: while the organizer step is the one on screen
   * (wizard) — or always, in See-all — and a colour is selected, the big stage
   * shows that colour's in-use shot — or its big product shot until one exists
   * (Graphite2 pending; Albert 2026-08-01: "use the image1 till we shoot the
   * image2"). Never a stale other colour, never the cable photo mid-selection.
   * Called from _update (selection changes) and _applyView (step/mode moves). */
  Configurator.prototype._syncStrapMedia = function () {
    if (!this._mediaShowOverride) return;
    var self = this;

    /* ---- candidate 1: the Fermata organizer takeover (rules unchanged) ---- */
    var strapIdx = -1, strapOpt = null;
    for (var i = 0; i < this.sectionPlan.length; i++) {
      if (this.sectionPlan[i].kind === "strap") { strapIdx = i; strapOpt = this.sectionPlan[i].opt; break; }
    }
    var strapWant = function () {
      if (!strapOpt) return "";
      var on = self.viewMode !== "wizard" || self.step === strapIdx;
      var sv = self.selectedValue(strapOpt);
      if (!(on && sv && !strapIsNone(sv))) return "";
      return [sv.image2, sv.image_big].filter(Boolean).join("|");
    };

    /* ---- candidate 2: pro-line colour takeover ---------------------------
     * Albert 2026-09-08: "if they select Red, a red cable shows on the left".
     * Gated on this.kind, so the headphone families are untouched — they carry
     * four colour slots and no per-colour cable photo, this line has one of
     * each. Deliberately NOT step-scoped the way the strap is: once a colour is
     * picked the cable IS that colour, so the shot stays up through the length
     * and plug steps instead of snapping back to the hero. A colour with no
     * photo yet yields "" (hero stays); a photo whose URL 404s is dropped by
     * probeFirst's apply(null) — so a half-shot palette degrades quietly, one
     * colour at a time, with no broken images. */
    var proColourOpt = null;
    if (this.kind) {
      for (var j = 0; j < this.sectionPlan.length; j++) {
        if (this.sectionPlan[j].kind === "colour") { proColourOpt = this.sectionPlan[j].opts[0]; break; }
      }
    }
    var proWant = function () {
      if (!proColourOpt) return "";
      var sv = self.selectedValue(proColourOpt);
      return (sv && sv.image_big) ? sv.image_big : "";
    };
    // "2 Red" -> "Red": the palette number is a picker affordance, not a name.
    var proAlt = function () {
      var sv = proColourOpt && self.selectedValue(proColourOpt);
      return sv ? clean(sv.label).replace(/^\d+\s*/, "") : "";
    };

    var pick = function () {
      var s = strapWant();
      if (s) return { key: s, alt: "Fermata" };
      var p = proWant();
      if (p) return { key: p, alt: proAlt() };
      return { key: "", alt: "" };
    };
    var cur = pick();
    if (!cur.key) { this._mediaShowOverride(null); return; }
    var list = cur.key.split("|");
    if (this._mediaOverrideUrl === list[0]) return;
    probeFirst(list, function () { return pick().key === cur.key; }, function (url) { self._mediaShowOverride(url, cur.alt); });
  };

  /* ---------------------- section: termination / generic ------------------ */
  /* ---- amp-connector model: parse OUR flat Termination values into connector
   * TYPES (cards) and BRAND/FINISH variants (pills), then resolve a (type, brand)
   * pick BACK to the EXACT original value label for the cart. Model-agnostic: types
   * and brands are derived from whatever labels the option carries (never the
   * prototype's invented TERMS); a label that matches no known type becomes its own
   * card. Parsing reads a cleaned copy (lc() trims the leading U+2028 some labels
   * carry) — the cart always receives the untouched value.label. */
  // Connector TYPE of a label: a stable group key, a display name, and a PlugGlyph kind.
  function connectorType(label) {
    // Female sockets (adapter cables) keep their own type key so type cards never merge a
    // plug with its socket; name/glyph get the female treatment, `base` keeps the plug key
    // for the brand-strip tables.
    var t = connectorTypeBase(label), f = lc(label);
    if (t.glyph && /\bfemale\b|\bsocket\b/.test(f)) {
      return { key: t.key + ":f", base: t.key, name: T("female_prefix", "Female") + " " + t.name, glyph: t.glyph + "-f" };
    }
    t.base = t.key;
    return t;
  }
  function connectorTypeBase(label) {
    var f = lc(label);
    if (/4[.,]4|pentaconn/.test(f)) return { key: "4.4",  name: "4.4mm Pentaconn", glyph: "4.4" };
    if (/6[.,]35/.test(f))          return { key: "6.35", name: "6.35mm Jack",     glyph: "6.35" };
    if (/3[.,]5/.test(f))           return { key: "3.5",  name: "3.5mm Jack",      glyph: "3.5" };
    if (/xlr/.test(f)) {
      var m = /(\d)\s*-?\s*pin|xlr\s*(\d)/.exec(f), pins = m && (m[1] || m[2]);
      return { key: "xlr", name: (pins ? pins + "-pin XLR" : "XLR"), glyph: "4xlr" };
    }
    if (/\brca\b/.test(f))          return { key: "rca",  name: "RCA",             glyph: null };
    return { key: "other:" + f, name: clean(label), glyph: null };
  }
  // The brand/finish portion of a label = the label with its connector-type tokens
  // removed. An empty remainder (the type's bare/default variant) falls back to the
  // type's noun. DISPLAY ONLY — never fed to the cart.
  var TERM_STRIP = {
    "3.5":  /3[.,]5\s*mm|1\/8|\bmm\b|\bjack\b/gi,
    "6.35": /6[.,]35\s*mm|1\/4|\bmm\b|\bjack\b/gi,
    "4.4":  /4[.,]4\s*mm|\bmm\b|pentaconn/gi,
    "xlr":  /\bxlr\b|\d\s*-?\s*pin|\bpin\b/gi,
    "rca":  /\brca\b/gi
  };
  var TERM_DEFAULT_BRAND = { "3.5": "Standard", "6.35": "Standard", "4.4": "Pentaconn", "xlr": "Standard", "rca": "Standard" };
  function connectorBrand(label) {
    var ct = connectorType(label), key = ct.base || ct.key, rest = clean(label), rx = TERM_STRIP[key];
    if (rx) rest = rest.replace(rx, " ");
    rest = rest.replace(/\bfemale\b/gi, " ");
    rest = rest.replace(/\s+/g, " ").trim();
    var db = TERM_DEFAULT_BRAND[key];
    if (db === "Standard") db = T("standard_brand", "Standard");
    return rest || db || clean(label);
  }
  // One-line explanations for connector brands/finishes (adopted from the design
  // prototype). Matched by keyword in the value label → a tooltip on the brand pill.
  var VARIANT_INFO = {
    "Furutech": "Furutech FT-series — rhodium-plated copper, 24k-gold contacts. Built in Japan.",
    "Rhodium": "Rhodium-plated copper. Corrosion-resistant; lower noise floor on balanced rigs.",
    "Pentaconn": "Original Pentaconn 4.4mm — brass body, gold TRRRS contacts. The DAP reference.",
    "Neutrik": "Neutrik NC-series — pro-audio standard. Locking detent, nickel contacts. Made in Liechtenstein.",
    "Screw-on": "Threaded screw-on barrel — a secure, locking 3.5mm connection.",
    "Rean": "Rean (by Neutrik) — compact zinc-alloy body, nickel contacts. Our no-frills default."
  };
  function variantTip(label) {
    var keys = Object.keys(VARIANT_INFO);
    for (var i = 0; i < keys.length; i++) { if (label.indexOf(keys[i]) > -1) return T("info_" + keys[i].toLowerCase().replace(/-/g, "_"), VARIANT_INFO[keys[i]]); }
    return "";
  }
  // Inline PlugGlyph SVGs ported from the design prototype, re-themed to our tokens
  // (its gold contact -> our lime accent; light body; dark gaps); geometry preserved.
  // "" for types we don't illustrate -> the card renders without a glyph box.
  function plugGlyphSVG(kind) {
    var body = "#cfcfcf", accent = cssAccent(), dark = "#0D0D0D",
        metal = "#bdbdbd", metalEdge = "#8a8a8a", housing = "#161616", pin = "#8a8a8a";
    var open = '<svg viewBox="0 0 56 48" width="56" height="44" aria-hidden="true" focusable="false">';
    if (kind === "3.5") return open +
      '<rect x="4" y="18" width="16" height="12" rx="3" fill="' + body + '"/>' +
      '<rect x="20" y="21" width="26" height="6" fill="' + accent + '"/>' +
      '<rect x="28" y="21" width="1.5" height="6" fill="' + dark + '"/>' +
      '<rect x="36" y="21" width="1.5" height="6" fill="' + dark + '"/></svg>';
    if (kind === "6.35") return open +
      '<rect x="4" y="16" width="16" height="16" rx="3" fill="' + body + '"/>' +
      '<rect x="20" y="20" width="32" height="8" fill="' + metal + '" stroke="' + metalEdge + '"/>' +
      '<rect x="20" y="20" width="3" height="8" fill="' + accent + '"/>' +
      '<rect x="34" y="20" width="1.5" height="8" fill="' + dark + '"/></svg>';
    if (kind === "4.4") return open +
      '<rect x="4" y="17" width="16" height="14" rx="3" fill="' + body + '"/>' +
      '<rect x="20" y="21" width="28" height="6" fill="' + accent + '"/>' +
      '<rect x="26" y="21" width="1" height="6" fill="' + dark + '"/>' +
      '<rect x="32" y="21" width="1" height="6" fill="' + dark + '"/>' +
      '<rect x="38" y="21" width="1" height="6" fill="' + dark + '"/>' +
      '<rect x="44" y="21" width="1" height="6" fill="' + dark + '"/></svg>';
    // Female sockets: the same housing, a barrel with a dark opening instead of a pin.
    if (kind === "3.5-f" || kind === "4.4-f" || kind === "6.35-f") {
      var r = kind === "6.35-f" ? 5 : kind === "4.4-f" ? 4 : 3.4, bh = kind === "6.35-f" ? 16 : kind === "4.4-f" ? 14 : 12;
      return open +
        '<rect x="4" y="' + (24 - bh / 2) + '" width="24" height="' + bh + '" rx="3" fill="' + body + '"/>' +
        '<rect x="28" y="' + (24 - (r + 3)) + '" width="14" height="' + (2 * (r + 3)) + '" rx="2" fill="' + metal + '" stroke="' + metalEdge + '"/>' +
        '<circle cx="42" cy="24" r="' + (r + 3) + '" fill="' + metal + '" stroke="' + metalEdge + '"/>' +
        '<circle cx="42" cy="24" r="' + r + '" fill="' + dark + '" stroke="' + accent + '" stroke-width="1.5"/></svg>';
    }
    if (kind === "4xlr-f") return open +
      '<rect x="4" y="14" width="18" height="20" rx="3" fill="' + body + '"/>' +
      '<circle cx="38" cy="24" r="13" fill="' + housing + '" stroke="' + accent + '" stroke-width="2"/>' +
      '<circle cx="38" cy="18" r="2.2" fill="' + dark + '" stroke="' + pin + '" stroke-width="1"/>' +
      '<circle cx="44" cy="24" r="2.2" fill="' + dark + '" stroke="' + pin + '" stroke-width="1"/>' +
      '<circle cx="38" cy="30" r="2.2" fill="' + dark + '" stroke="' + pin + '" stroke-width="1"/>' +
      '<circle cx="32" cy="24" r="2.2" fill="' + dark + '" stroke="' + pin + '" stroke-width="1"/></svg>';
    if (kind === "4xlr") return open +
      '<rect x="4" y="14" width="18" height="20" rx="3" fill="' + body + '"/>' +
      '<circle cx="38" cy="24" r="13" fill="' + housing + '" stroke="' + accent + '" stroke-width="2"/>' +
      '<circle cx="38" cy="18" r="1.8" fill="' + pin + '"/>' +
      '<circle cx="44" cy="24" r="1.8" fill="' + pin + '"/>' +
      '<circle cx="38" cy="30" r="1.8" fill="' + pin + '"/>' +
      '<circle cx="32" cy="24" r="1.8" fill="' + pin + '"/></svg>';
    return "";
  }

  /* ---- finish-matching: Furutech on the amp end suggests Furutech on the hp end ----
   * Pure upsell nudge, data-driven (matched by label, works on any product whose hp
   * list carries a Furutech option). Rules:
   *   - fires only on an EXPLICIT amp-end click (never on prefill/share/edit restore),
   *   - never overrides a headphone end the customer has touched themselves,
   *   - leaving Furutech on the amp end quietly undoes OUR auto-upgrade (and only ours),
   *   - the note (state._hpMatchedNote) explains the switch; any manual hp pick clears it. */
  function isFurutech(label) { return /furutech/i.test(label || ""); }
  Configurator.prototype._hpFurutechValue = function () {
    if (!this.hpTermOpt) return null;
    for (var i = 0; i < this.hpTermOpt.values.length; i++) {
      if (isFurutech(this.hpTermOpt.values[i].label)) return this.hpTermOpt.values[i];
    }
    return null;
  };
  Configurator.prototype._setAmpTermination = function (opt, label) {
    var patch = kv(opt.id, label);
    var hp = this.hpTermOpt, fv = this._hpFurutechValue();
    if (hp && fv && !this._hpTouched) {
      var cur = this.state[hp.id];
      if (isFurutech(label)) {
        if (cur !== fv.label) {
          this._hpAutoPrev = cur;
          this._hpAutoLabel = fv.label;
          patch[hp.id] = fv.label;
          patch._hpMatchedNote = true;
        }
      } else if (this._hpAutoLabel && cur === this._hpAutoLabel && this._hpAutoPrev != null) {
        patch[hp.id] = this._hpAutoPrev;
        this._hpAutoPrev = null;
        this._hpAutoLabel = null;
        patch._hpMatchedNote = false;
      }
    }
    this.setState(patch);
  };

  /* ---- termination: amp-connector picker (PlugGlyph type cards -> brand pills) ----
   * Rebuilt to the design prototype's amp-connector pattern, scrapping the old
   * photo-based drill-down. Level 1 is a grid of connector-TYPE cards (a PlugGlyph
   * illustration + the type name + the default variant); Level 2 is a brand/finish
   * PILL row (with each variant's add-on price) for the chosen type. Resolves back to
   * our EXACT flat cart labels: a pill (or a type card's default) submits the option's
   * own value.label byte-for-byte, preserving the leading U+2028 several labels carry.
   * The active type is DERIVED from the current selection, and choosing a type selects
   * its first variant — so a valid label is always selected (no-deselect invariant).
   * The headphone end (brand-only) collapses to a single pill row, content unchanged. */
  Configurator.prototype._buildTermination = function (opt, num, title, help) {
    var self = this;
    var sec = h("section", { class: "acfg__section acfg__section--term" });
    sec.appendChild(sectionHead(num, title));
    self._headValue(sec, function () { return connectorType(self.selectedValue(opt).label).name; });
    var isHp = /head\s*phone|earcup|\bhp\b/.test(lc(opt.globo_field));
    sec.appendChild(h("p", { class: "acfg__q", text: opt.question || (isHp ? T("hp_question", "How does it attach to your headphones?") : T("amp_question", "How does it plug into your amp?")) }));
    sec.appendChild(h("p", { class: "acfg__q-hint", text: help || (isHp ? T("hp_hint", "The plug at the headphone end. Pick the brand / finish.") : T("amp_hint", "The plug at the source end. Pick the type, then the brand.")) }));
    // The headphone end is brand-only (its connector type is fixed by the headphone),
    // so its pills carry the full label; the amp end strips the type to the brand.
    var brandText = isHp ? clean : connectorBrand;

    // Group the flat values by connector TYPE (stable key), preserving data order.
    // The headphone end is forced into ONE implicit group so it never sprouts type
    // cards — it drops straight to the brand pills.
    var groups = [], index = {};
    if (isHp) {
      groups = [{ key: "_hp", name: title, glyph: null, items: opt.values.slice() }];
      index._hp = groups[0];
    } else {
      opt.values.forEach(function (v) {
        var t = connectorType(v.label);
        if (!index[t.key]) { index[t.key] = { key: t.key, name: t.name, glyph: t.glyph, items: [] }; groups.push(index[t.key]); }
        index[t.key].items.push(v);
      });
    }

    // Level 1 — connector-type cards, ONLY when there's a real type choice.
    var typeEls = [];
    if (!isHp && groups.length > 1) {
      var typeWrap = h("div", { class: "acfg__plugtypes" });
      typeEls = groups.map(function (g) {
        var def = g.items[0]; // first = default (cheapest in our data order)
        var card = btn({ class: "acfg__plugtype", on: { click: function () {
          if (connectorType(self.state[opt.id]).key !== g.key) self._setAmpTermination(opt, def.label);
        } } }, [
          g.glyph ? h("span", { class: "acfg__plugtype-glyph", html: plugGlyphSVG(g.glyph) }) : null,
          h("span", { class: "acfg__plugtype-name", text: g.name }),
          h("span", { class: "acfg__plugtype-sub", text: connectorBrand(def.label) }),
          h("span", { class: "acfg__plugtype-check", "aria-hidden": "true" })
        ]);
        card._key = g.key; typeWrap.appendChild(card); return card;
      });
      sec.appendChild(typeWrap);
    }

    // Finish-matching note (headphone end only): explains the automatic Furutech
    // match after a Furutech amp-end pick. Shown/hidden from state in update().
    var matchNote = null;
    if (isHp) {
      matchNote = h("p", { class: "acfg__match-note", role: "status", text: T("furutech_match_note", "Matched to your Furutech amp plug — we recommend Furutech on both ends for a balanced build. Switch back below if you prefer the standard connector.") });
      matchNote.style.display = "none";
      sec.appendChild(matchNote);
    }

    // Level 2 — brand / finish pills for the active type (rebuilt only when the type
    // changes), shown only when that type has more than one variant.
    var pillsLabel = h("div", { class: "acfg__pills-label", text: T("brand_finish", "Brand / finish") });
    var pillsWrap = h("div", { class: "acfg__pills" });
    // Visible tip line for the SELECTED pill — the title-attribute tooltip is
    // hover-only, so touch + keyboard users never saw the copy that justifies the
    // €60-85 finishes (2026-07-20 review). The title stays for desktop hover.
    var pillTip = h("p", { class: "acfg__pill-tip" });
    sec.appendChild(pillsLabel); sec.appendChild(pillsWrap); sec.appendChild(pillTip);
    var pillEls = [];
    function renderPills(activeKey) {
      if (pillsWrap._sig === activeKey) return;
      pillsWrap._sig = activeKey; pillsWrap.innerHTML = ""; pillEls = [];
      var g = index[activeKey] || groups[0], multi = !!(g && g.items.length > 1);
      // The headphone end has no type cards, so with a single variant the section
      // would otherwise render EMPTY (heading + question, nothing to see). Show the
      // lone pill there — it reads as "this is what's included".
      var show = multi || (isHp && !!(g && g.items.length === 1));
      pillsLabel.style.display = pillsWrap.style.display = show ? "" : "none";
      if (!show) return;
      g.items.forEach(function (v) {
        var tip = variantTip(v.label);
        var pill = btn({ class: "acfg__pill", title: tip, on: { click: function () {
          if (isHp) {
            // Explicit headphone-end choice: remember it (finish-matching must never
            // override it again) and clear the "matched" note.
            self._hpTouched = true;
            var p = kv(opt.id, v.label);
            p._hpMatchedNote = false;
            self.setState(p);
          } else {
            self._setAmpTermination(opt, v.label); // may auto-match the hp end (Furutech)
          }
        } } }, [
          h("span", { class: "acfg__pill-name", text: brandText(v.label) }),
          v.addon
            ? h("span", { class: "acfg__pill-price", text: "+" + money(v.addon, self.currency) })
            : h("span", { class: "acfg__pill-price", text: T("included_in_base", "Included in base price") }),
          tip ? h("span", { class: "acfg__info", "aria-hidden": "true", text: "i" }) : null
        ]);
        pill._label = v.label; pillEls.push(pill); pillsWrap.appendChild(pill);
      });
    }

    return {
      el: sec,
      update: function (st) {
        var cur = st[opt.id], activeKey = isHp ? "_hp" : connectorType(cur).key;
        typeEls.forEach(function (c) { var on = c._key === activeKey; c.classList.toggle("is-selected", on); ariaPressed(c, on); });
        renderPills(activeKey);
        pillEls.forEach(function (p) { var on = p._label === cur; p.classList.toggle("is-selected", on); ariaPressed(p, on); });
        var curTip = variantTip(cur) || "";
        pillTip.textContent = curTip;
        // The tip explains the brand pill; without a pill row (one brand per type,
        // e.g. Argentea where every plug is Furutech) it would sit orphaned under the cards.
        pillTip.style.display = (curTip && pillsWrap.style.display !== "none") ? "" : "none";
        if (matchNote) matchNote.style.display = st._hpMatchedNote ? "" : "none";
      }
    };
  };

  // Flat selectable row list wrapped in a section (generic dropdowns).
  Configurator.prototype._buildList = function (opt, num, title) {
    var sec = h("section", { class: "acfg__section acfg__section--list" });
    sec.appendChild(sectionHead(num, title));
    // v4: data-carried question/hint lines (the ProAudio plug ends ride the
    // generic list; their copy travels in the option JSON, localized by the section).
    if (opt.question) sec.appendChild(h("p", { class: "acfg__q", text: opt.question }));
    if (opt.help) sec.appendChild(h("p", { class: "acfg__q-hint", text: opt.help }));
    var list = this._rowList(opt);
    sec.appendChild(list.el);
    return { el: sec, update: list.update };
  };
  Configurator.prototype._buildGeneric = function (opt, num, title) {
    var self = this;
    // Generic swatch -> reuse a simple swatch grid; otherwise a flat row list.
    if (opt.type === "swatch") {
      var sec = h("section", { class: "acfg__section acfg__section--colours" });
      sec.appendChild(sectionHead(num, title));
      var grid = h("div", { class: "acfg__swatches" });
      var cells = opt.values.map(function (v) {
        var n = colourNumber(v.label);
        var cell = btn({
          class: "acfg__swatch", title: clean(v.label), "data-num": String(n),
          on: { click: function () { self.setState(kv(opt.id, v.label)); } }
        }, [
          h("span", { class: "acfg__swatch-img", style: self.swatchStyle(n) }),
          h("span", { class: "acfg__swatch-name", text: clean(v.label) })
        ]);
        cell._label = v.label; grid.appendChild(cell); return cell;
      });
      sec.appendChild(grid);
      return { el: sec, update: function (st) { cells.forEach(function (c) { var on = c._label === st[opt.id]; c.classList.toggle("is-selected", on); ariaPressed(c, on); }); } };
    }
    return this._buildList(opt, num, title);
  };

  // Reusable flat selectable row list (generic dropdowns + non-parsable length).
  Configurator.prototype._rowList = function (opt) {
    var self = this;
    var wrap = h("div", { class: "acfg__list" });
    var rows = h("div", { class: "acfg__rows" });
    var rowEls = opt.values.map(function (v) {
      var row = btn({
        class: "acfg__row",
        on: { click: function () { self.setState(kv(opt.id, v.label)); } }
      }, [
        h("span", { class: "acfg__row-name", text: clean(v.label) }),
        h("span", { class: "acfg__row-price" + (v.addon ? "" : " is-free"), text: v.addon ? "+" + money(v.addon, self.currency) : T("included_in_base", "Included in base price") })
      ]);
      row._label = v.label; rows.appendChild(row); return row;
    });
    wrap.appendChild(rows);
    return { el: wrap, update: function (st) { rowEls.forEach(function (r) { var on = r._label === st[opt.id]; r.classList.toggle("is-selected", on); ariaPressed(r, on); }); } };
  };

  /* ------------------------------ cart drawer ----------------------------- */
  Configurator.prototype._buildDrawer = function () {
    var self = this;
    this.elDrawerLines = h("div", { class: "acfg__drawer-lines" });
    this.elDrawerTotal = h("span", { class: "acfg__drawer-total" });
    // change 9: model name heading so a cart with two different cable configs is unambiguous.
    this.elDrawerModel = h("div", { class: "acfg__drawer-model" });
    var bodyKids = [this.elDrawerModel,
      h("div", { class: "acfg__drawer-config-label", text: T("your_configuration", "Your configuration") }),
      this.elDrawerLines];
    var panel = h("div", { class: "acfg__drawer-panel", role: "dialog", "aria-modal": "true", "aria-label": T("added_to_cart", "Added to cart"), on: { click: function (e) { e.stopPropagation(); } } }, [
      h("div", { class: "acfg__drawer-head" }, [
        h("div", { class: "acfg__drawer-title" }, [h("span", { class: "acfg__dot" }), h("span", { text: T("added_to_cart", "Added to cart") })]),
        btn({ class: "acfg__drawer-close", "aria-label": T("close_aria", "Close"), text: "×", on: { click: function () { self.closeDrawer(); } } })
      ]),
      h("div", { class: "acfg__drawer-body" }, bodyKids),
      h("div", { class: "acfg__drawer-foot" }, [
        h("div", { class: "acfg__drawer-totalrow" }, [h("span", { text: T("total", "Total") }), this.elDrawerTotal]),
        // Concrete ready-to-ship window where post-add doubt sets in (lead time was
        // absent from the drawer entirely — 2026-07-20 review).
        (function (s) { return s ? h("div", { class: "acfg__price-note acfg__price-note--drawer", text: s }) : null; })(this._shipDateText()),
        // Tax/VAT note next to the drawer total (same settings.price_note as the breakdown).
        this.settings.price_note ? h("div", { class: "acfg__price-note acfg__price-note--drawer", text: this.settings.price_note }) : null,
        btn({ class: "acfg__checkout", text: T("checkout", "Checkout"), on: { click: function () { window.location.href = "/checkout"; } } }),
        // change 10: return to the wizard with all current selections intact; the next
        // Add to cart REPLACES the just-added lines (see editConfiguration). The former
        // "Keep configuring" button was dropped — closing the drawer (× / overlay click)
        // does the same thing, and two near-identical actions read as a false choice.
        btn({ class: "acfg__ghostbtn acfg__edit", text: T("edit_configuration", "Edit configuration"), on: { click: function () { self.editConfiguration(); } } })
      ])
    ]);
    // Re-build safe: the drawer lives on root (outside the cleared mount), so drop any prior
    // one first — otherwise reset()/re-init leaves a growing stack of detached drawers + listeners.
    if (this.elDrawer && this.elDrawer.parentNode) this.elDrawer.parentNode.removeChild(this.elDrawer);
    this.elDrawer = h("div", { class: "acfg__drawer", on: { click: function () { self.closeDrawer(); } } }, [panel]);
    this.root.appendChild(this.elDrawer);
  };
  Configurator.prototype._fillDrawer = function () {
    var self = this;
    // change 9: show which cable this is, above "YOUR CONFIGURATION".
    if (this.elDrawerModel) {
      this.elDrawerModel.textContent = this.productTitle || "";
      this.elDrawerModel.style.display = this.productTitle ? "" : "none";
    }
    this.elDrawerLines.innerHTML = "";
    this.options.forEach(function (o) {
      var sv = self.selectedValue(o);
      var display = clean(sv.label);
      var chip = null;
      if (o._role === "colour" || (o.type === "swatch")) {
        chip = h("span", { class: "acfg__line-chip", style: self.swatchStyle(colourNumber(sv.label)) });
      }
      self.elDrawerLines.appendChild(h("div", { class: "acfg__line" }, [
        h("span", { class: "acfg__line-field", text: fieldTitle(o.globo_field) }),
        h("span", { class: "acfg__line-val" }, [chip, h("span", { text: display }),
          sv.addon ? h("span", { class: "acfg__line-add", text: "+" + money(sv.addon, self.currency) }) : null])
      ]));
    });
    this.elDrawerTotal.textContent = money(this.total(), this.currency);
  };
  // Real modal semantics (audit 2026-07-14 #19): remember the opener, move focus
  // in, trap Tab inside the panel, close on Esc, restore focus on close. Guards
  // keep the headless test shim (plain-object DOM) safe.
  Configurator.prototype.openDrawer = function () {
    var self = this;
    this._fillDrawer();
    this.elDrawer.classList.add("is-open");
    document.documentElement.style.overflow = "hidden";
    try { this._drawerReturnFocus = document.activeElement || null; } catch (e) { this._drawerReturnFocus = null; }
    var panel = this.elDrawer.firstChild;
    if (!this._drawerKeydown) {
      this._drawerKeydown = function (e) {
        if (e.key === "Escape" || e.keyCode === 27) { self.closeDrawer(); return; }
        if (e.key !== "Tab" && e.keyCode !== 9) return;
        if (!panel.querySelectorAll) return;
        var f = panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      };
    }
    if (this.elDrawer.addEventListener) this.elDrawer.addEventListener("keydown", this._drawerKeydown);
    var closeBtn = panel.querySelector ? panel.querySelector(".acfg__drawer-close") : null;
    if (closeBtn && closeBtn.focus) { try { closeBtn.focus(); } catch (e) {} }
  };
  Configurator.prototype.closeDrawer = function () {
    this.elDrawer.classList.remove("is-open");
    document.documentElement.style.overflow = "";
    if (this._drawerKeydown && this.elDrawer.removeEventListener) this.elDrawer.removeEventListener("keydown", this._drawerKeydown);
    if (this._drawerReturnFocus && this._drawerReturnFocus.focus) { try { this._drawerReturnFocus.focus(); } catch (e) {} }
    this._drawerReturnFocus = null;
  };
  // change 10: reopen the configurator with the current selections intact. State
  // lives on the instance and is never cleared, so this just returns the user to
  // the wizard (step 1) — no payload change, no duplicate line.
  Configurator.prototype.editConfiguration = function () {
    // The customer is editing what they JUST added — arm replace-on-edit so the next
    // Add to cart swaps the previous lines instead of duplicating the cable. Armed
    // only; nothing is removed unless (and until) a new add succeeds.
    if (this._lastAddKeys && this._lastAddKeys.length) this._pendingReplaceKeys = this._lastAddKeys.slice();
    track("edit", { product: this.productTitle });
    this.closeDrawer();
    this._setStatus("", false);
    if (this.viewMode === "wizard") { this.step = 0; this._applyView(); }
    var target = this.elProgress || this.root;
    if (target && typeof target.scrollIntoView === "function") target.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
  };

  /* ------------------------- edit prefill (change 11) --------------------- */
  // Map a saved option-label map (globo_field -> exact value label, taken from a
  // cart line's base properties) back onto the live selection state, then
  // re-render. This ONLY sets selection state -- buildPayload's output shape is
  // never touched, so the cart contract stays byte-identical. Returns true if at
  // least one option matched. The cart drawer "Edit" uses this to reopen the
  // configurator pre-filled instead of blank.
  Configurator.prototype.prefillFromLabels = function (labelMap) {
    if (!labelMap || typeof labelMap !== "object") return false;
    var self = this;
    var patch = {};
    var matched = false;
    this.options.forEach(function (o) {
      if (!Object.prototype.hasOwnProperty.call(labelMap, o.globo_field)) return;
      var saved = labelMap[o.globo_field];
      if (saved == null) return;
      // Exact label match first (preserves Globo's untrimmed bytes); then a
      // trimmed compare as a fallback. Never fabricate a value -- if nothing
      // matches, the option keeps its current (default) selection.
      // A sold-out value (organizer colour out of stock) is never restored — the
      // option keeps its default, exactly as if the label had not matched.
      var hit = null;
      for (var i = 0; i < o.values.length; i++) {
        if (o.values[i].soldout === true) continue;
        if (o.values[i].label === saved) { hit = o.values[i].label; break; }
      }
      if (hit == null) {
        var s = clean(saved);
        for (var j = 0; j < o.values.length; j++) {
          if (o.values[j].soldout === true) continue;
          if (clean(o.values[j].label) === s) { hit = o.values[j].label; break; }
        }
      }
      if (hit != null) {
        patch[o.id] = hit;
        matched = true;
        // A restored build's headphone end is an intentional choice — finish-matching
        // (Furutech both ends) must never auto-change it afterwards.
        if (self.hpTermOpt && o === self.hpTermOpt) self._hpTouched = true;
      }
    });
    if (!matched) return false;
    this._prefilled = true; // a share link / cart-edit / resume applied — the resume offer defers to it
    // Canonical Y-cable: re-derive the friendly count / arrangement / palette model
    // from the restored strand labels so the UI shows the right controls. Cosmetic —
    // it does not affect the cart payload. Apply the slot labels first, then infer.
    patch._activeColour = this.colourOrder.length ? this.colourOrder[0].id : this.state._activeColour;
    Object.assign(this.state, patch);
    if (this.canonicalColour) this._inferColourModel();
    this._update(); // single re-render in the new state
    return true;
  };

  /* ------------------- resume a build (abandonment recovery) ---------------- *
   * A €90+ hand-made purchase is a multi-session decision, but a reload used to
   * lose everything (only view mode / unit / sample email persisted). _update
   * debounce-saves the selection labels per product; on a return visit with a
   * differing save, a dismissible bar offers to restore it via the SAME
   * prefillFromLabels path share links and cart-edit use — the cart contract is
   * untouched. Share links and cart-edit handoffs always win over the offer. */
  Configurator.prototype._buildStorageKey = function () {
    return "acfg_build:" + (this.baseVariantId || clean(this.productTitle) || "x");
  };
  Configurator.prototype._saveBuild = function () {
    // Never before _maybeOfferResume has read the previous save — the init-time
    // _update would overwrite a meaningful save with pristine defaults.
    if (!this._resumeReady) return;
    var self = this;
    if (this._saveTimer) clearTimeout(this._saveTimer);
    this._saveTimer = setTimeout(function () {
      try { window.localStorage.setItem(self._buildStorageKey(), JSON.stringify({ props: self.captureSelections().props, t: Date.now() })); } catch (e) {}
    }, 400);
  };
  Configurator.prototype._maybeOfferResume = function () {
    var self = this;
    try {
      var raw = null;
      try { raw = window.localStorage.getItem(this._buildStorageKey()); } catch (e) {}
      this._resumeReady = true;
      if (!raw || this._prefilled) return;
      var data = JSON.parse(raw);
      var saved = data && data.props;
      if (!saved || typeof saved !== "object") return;
      // A build older than 30 days is stale, not "abandoned" — silently drop it.
      if (data.t && Date.now() - data.t > 30 * 86400000) {
        try { window.localStorage.removeItem(this._buildStorageKey()); } catch (e) {}
        return;
      }
      var cur = this.captureSelections().props, differs = false;
      for (var k in saved) { if (Object.prototype.hasOwnProperty.call(saved, k) && cur[k] !== undefined && saved[k] !== cur[k]) { differs = true; break; } }
      if (!differs) return;
      var bar = h("div", { class: "acfg__resume", role: "status" }, [
        h("span", { class: "acfg__resume-text", text: T("resume_prompt", "Welcome back — pick up where you left off?") }),
        btn({ class: "acfg__resume-btn", on: { click: function () {
          try { self.prefillFromLabels(saved); } catch (e) {}
          if (self.viewMode === "wizard") { self.step = 0; self._applyView(); }
          if (bar.parentNode) bar.parentNode.removeChild(bar);
          track("resume_build", { product: self.productTitle });
        } } }, T("resume_yes", "Resume build")),
        btn({ class: "acfg__resume-btn acfg__resume-btn--no", on: { click: function () {
          try { window.localStorage.removeItem(self._buildStorageKey()); } catch (e) {}
          if (bar.parentNode) bar.parentNode.removeChild(bar);
        } } }, T("resume_no", "Start fresh"))
      ]);
      if (this.elMain) this.elMain.insertBefore(bar, this.elMain.firstChild);
    } catch (e) { this._resumeReady = true; }
  };

  // One-shot handoff: when the cart-drawer "Edit" cannot prefill in place (the
  // configurator is on another page), it stashes the option map in sessionStorage
  // and navigates here. On init we apply it only if it targets THIS product, so a
  // different model is never mis-prefilled. Guarded so it is a safe no-op under
  // the test harness (no sessionStorage) and never throws.
  Configurator.prototype._consumePendingPrefill = function () {
    var raw = null;
    try { raw = window.sessionStorage && window.sessionStorage.getItem("acfg_prefill"); } catch (e) { return; }
    if (!raw) return;
    var data = null;
    try { data = JSON.parse(raw); } catch (e) {
      try { window.sessionStorage.removeItem("acfg_prefill"); } catch (e2) {}
      return;
    }
    if (!data || typeof data !== "object") return;
    if (data.model && this.productTitle && clean(data.model) !== clean(this.productTitle)) return;
    try { window.sessionStorage.removeItem("acfg_prefill"); } catch (e) {}
    try { if (data.props) this.prefillFromLabels(data.props); } catch (e) {}
    // Replace-on-edit handoff: the cart controllers pass the edited group's line
    // keys so the next successful Add to cart swaps the old lines out (see
    // addToCart). Capped defensively; a group is never more than a handful of lines.
    if (Object.prototype.toString.call(data.replace_keys) === "[object Array]" && data.replace_keys.length) {
      this._pendingReplaceKeys = data.replace_keys.slice(0, 40);
    }
    if (this.viewMode === "wizard") { this.step = 0; this._applyView(); }
    var target = this.elProgress || this.root;
    if (target && typeof target.scrollIntoView === "function") target.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
  };

  /* ----------------------- share this build (copy-link) ------------------- */
  // Copy text to the clipboard: async Clipboard API first (secure contexts),
  // then a legacy execCommand fallback (in-app / non-secure browsers). Always
  // returns a Promise so callers have one success/failure path.
  function copyToClipboard(text) {
    if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).catch(function () { return legacyCopy(text); });
    }
    return legacyCopy(text);
  }
  function legacyCopy(text) {
    return new Promise(function (resolve, reject) {
      try {
        var ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "-9999px";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        if (typeof ta.select === "function") ta.select();
        var ok = typeof document.execCommand === "function" && document.execCommand("copy");
        if (ta.parentNode) ta.parentNode.removeChild(ta);
        ok ? resolve() : reject(new Error("copy command rejected"));
      } catch (e) { reject(e); }
    });
  }

  // Reusable state capture — the seam the future "Request a sample" feature will
  // reuse. Pure read of selection state: { model, props:{ <globo_field>:<label> } }
  // for EVERY option, exact label (the same bytes buildPayload submits). No side
  // effects, no payload mutation. Inverse of prefillFromLabels.
  Configurator.prototype.captureSelections = function () {
    var self = this;
    var props = {};
    this.options.forEach(function (o) { props[o.globo_field] = self.state[o.id]; });
    return { model: this.productTitle, props: props };
  };

  // Absolute URL to THIS product page carrying the full build in ?acfg=. The
  // path + any other query params are preserved; an existing acfg is replaced.
  Configurator.prototype.buildShareUrl = function () {
    var encoded = b64urlEncode(JSON.stringify(this.captureSelections()));
    var loc = (typeof window !== "undefined" && window.location) || {};
    var origin = loc.origin || ((loc.protocol || "") + "//" + (loc.host || ""));
    var query = dropQueryParam(loc.search || "", "acfg");
    query = (query ? query + "&" : "?") + "acfg=" + encoded;
    return origin + (loc.pathname || "") + query;
  };

  // Decode + apply an inbound ?acfg= on load. Mirrors _consumePendingPrefill's
  // rules (trimmed model guard, scroll into view, wizard reset to step 0).
  // FAIL-SAFE: any decode / parse / oversize error is swallowed, so a malformed
  // param can NEVER throw out of init — the configurator simply initialises
  // blank/default. Stripped from the URL once applied so a later manual share is
  // clean. The model guard is left in place for a (hypothetical) matching
  // instance, exactly as _consumePendingPrefill leaves its sessionStorage key.
  Configurator.prototype._consumeShareParam = function () {
    var search;
    try { search = (typeof window !== "undefined" && window.location) ? (window.location.search || "") : ""; }
    catch (e) { return; }
    if (!search || search.indexOf("acfg=") === -1) return;
    var raw = getQueryParam(search, "acfg");
    if (!raw) return;
    if (raw.length > MAX_ACFG) { this._stripShareParam(); return; }   // oversize -> swallow + clean
    var data = null;
    try { data = JSON.parse(b64urlDecode(raw)); }
    catch (e) { this._stripShareParam(); return; }                    // bad base64 / bad JSON -> swallow + clean
    if (!data || typeof data !== "object") return;
    // A link built for a different cable must never mis-prefill (same trimmed
    // compare as _consumePendingPrefill). Leave the param untouched here.
    if (data.model && this.productTitle && clean(data.model) !== clean(this.productTitle)) return;
    try { if (data.props) this.prefillFromLabels(data.props); } catch (e) {}
    this._stripShareParam(); // applied -> drop ?acfg= from the address bar
    if (this.viewMode === "wizard") { this.step = 0; this._applyView(); }
    var target = this.elProgress || this.root;
    if (target && typeof target.scrollIntoView === "function") target.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
  };

  // Remove ?acfg= from the address bar (history.replaceState). Guarded: a no-op
  // when the History API is absent, and never throws.
  Configurator.prototype._stripShareParam = function () {
    try {
      if (typeof window === "undefined" || !window.history || typeof window.history.replaceState !== "function") return;
      var loc = window.location;
      var url = (loc.pathname || "") + dropQueryParam(loc.search || "", "acfg") + (loc.hash || "");
      window.history.replaceState(null, "", url);
    } catch (e) {}
  };

  // Build the share URL, copy it, and flash a transient "Link copied" on the
  // button(s). No dialogs: a clipboard failure surfaces a brief "Copy failed"
  // rather than throwing.
  Configurator.prototype.shareBuild = function () {
    var self = this, url;
    try { url = this.buildShareUrl(); }
    catch (e) { this._flashShare(T("couldnt_build_link", "Couldn't build link")); return; }
    copyToClipboard(url).then(function () { self._flashShare(T("link_copied", "Link copied"), true); track("share", { product: self.productTitle }); })
                        .catch(function () { self._flashShare(T("copy_failed", "Copy failed")); });
  };

  // One ghost "Share this build" button per action bar (commit + wizard); both
  // are tracked so they flash together (only one is visible at a time). aria-live
  // announces the label swap to assistive tech.
  Configurator.prototype._makeShareWidget = function (extraClass) {
    var self = this;
    var label = h("span", { class: "acfg__sharebtn-label", text: this._shareLabel });
    var b = btn({
      class: "acfg__sharebtn" + (extraClass ? " " + extraClass : ""),
      "aria-live": "polite",
      title: T("share_title", "Copy a link that reopens this exact build"),
      on: { click: function () { self.shareBuild(); } }
    }, [label]);
    this._shareWidgets.push({ btn: b, label: label });
    return b;
  };

  // Swap the share label (announced via aria-live), then revert after ~2s.
  // `copied` toggles the confirmed visual state.
  Configurator.prototype._flashShare = function (msg, copied) {
    var self = this;
    this._shareWidgets.forEach(function (w) {
      w.label.textContent = msg;
      w.btn.classList.toggle("is-copied", !!copied);
    });
    if (this._shareTimer) clearTimeout(this._shareTimer);
    this._shareTimer = setTimeout(function () {
      self._shareWidgets.forEach(function (w) {
        w.label.textContent = self._shareLabel;
        w.btn.classList.remove("is-copied");
      });
      self._shareTimer = null;
    }, 2000);
  };

  /* --------------------- request a sample (photo of the braid) ------------ *
   * Sits beside "Share this build" in both action bars. Lets a customer ask the
   * bench for a photo of the braided base (their colours + braiding style) before
   * buying. The backend is live at SAMPLE_ENDPOINT (CORS-locked to arachne.audio).
   * This feature only READS selection state (captureSampleProps — a SUBSET of
   * captureSelections) and POSTs it: buildPayload and the cart contract are never
   * touched. FAIL-SAFE throughout — a network / CORS / parse error can never throw
   * out of a click handler or break the configurator. */
  var SAMPLE_ENDPOINT = "https://nora.arachne.audio/sample-request";
  var SAMPLE_OK_MSG = "Request received. We'll email you a photo of this braid, usually within a day.";
  var SAMPLE_FAIL_MSG = "Couldn't send that just now. Email orders@arachne.audio and we'll sort it.";
  // Light client-side email shape check (the server is authoritative + lowercases).
  function isEmailish(s) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean(s)); }

  // The base-braid SUBSET of captureSelections().props: ONLY the four colour slots
  // (globo_field starting "color ") + the braiding-style field. Length, splitter and
  // termination are deliberately excluded — the sample is a photo of the braided
  // base only. Pure read, no side effects. Empty => the product has no colour/braid
  // options, so it is NOT sample-eligible and the button stays hidden.
  Configurator.prototype.captureSampleProps = function () {
    var all = this.captureSelections().props;
    var out = {};
    this.options.forEach(function (o) {
      var f = lc(o.globo_field);
      var isColour = f.indexOf("color ") === 0;
      var isBraid = f === "braiding style" || f === "braid style" || f === "braiding";
      if (isColour || isBraid) out[o.globo_field] = all[o.globo_field];
    });
    return out;
  };
  // Sample-eligible <=> the base-braid subset is non-empty. Medusa (sleeved) is
  // never eligible — the sample is a photo of a BRAIDED base, which sleeved
  // cables don't have (Albert 2026-07-20). Checked via both signals: `series`
  // covers single-colour Medusa products, `medusaSplit` covers Base/L/R configs
  // that arrive without a `series` key.
  Configurator.prototype._sampleEligible = function () {
    // Adapters use the headphone paracord palette, so the sample flow still applies to them.
    if (this.series === "medusa" || this.medusaSplit || (this.kind && this.kind !== "adapter")) return false;
    var p = this.captureSampleProps();
    for (var k in p) { if (Object.prototype.hasOwnProperty.call(p, k)) return true; }
    return false;
  };

  // Current product page URL with NO query string (mirrors buildShareUrl's origin
  // resolution; share_url keeps the query, product_url does not).
  Configurator.prototype._currentUrlNoQuery = function () {
    var loc = (typeof window !== "undefined" && window.location) || {};
    var origin = loc.origin || ((loc.protocol || "") + "//" + (loc.host || ""));
    return origin + (loc.pathname || "");
  };

  // The exact JSON body POSTed to the sample endpoint. Pure (no fetch, no DOM) so
  // the wire shape is unit-testable. `name` is optional in the contract and we do
  // not collect one, so it is omitted.
  Configurator.prototype._buildSampleBody = function (email, loggedIn, hp) {
    return {
      email: clean(email),
      logged_in: !!loggedIn,
      product: this.productTitle,
      product_url: this._currentUrlNoQuery(),
      share_url: this.buildShareUrl(),
      props: this.captureSampleProps(),
      hp: hp == null ? "" : String(hp)
    };
  };

  // Remembered email (logged-out path): persisted under acfg_sample_email. Guarded
  // so a Storage exception (private mode / disabled) is a safe no-op.
  Configurator.prototype._storedSampleEmail = function () {
    try { var v = window.localStorage.getItem("acfg_sample_email"); return v && isEmailish(v) ? v : ""; }
    catch (e) { return ""; }
  };
  Configurator.prototype._rememberSampleEmail = function (email) {
    try { window.localStorage.setItem("acfg_sample_email", email); } catch (e) {}
  };

  // Click on "Request a sample":
  //  - logged in (account email present) -> submit immediately, no prompt.
  //  - logged out + a remembered email   -> submit immediately, show "sending to
  //                                          x · change" so they can correct it.
  //  - logged out, first time            -> reveal the inline email field.
  Configurator.prototype.requestSample = function () {
    if (this.customerEmail) { this._submitSample(this.customerEmail, true); return; }
    var stored = this._storedSampleEmail();
    if (stored) { this._showSampleMeta(stored); this._submitSample(stored, false); return; }
    this._revealSampleForm();
  };

  // Submit from the inline field: validate shape, remember it, then POST.
  Configurator.prototype._sampleFormSubmit = function (widget) {
    var email = clean(widget && widget.email ? widget.email.value : "");
    if (!isEmailish(email)) { this._flashSample(T("invalid_email", "Please enter a valid email address."), "error"); return; }
    this._rememberSampleEmail(email);
    this._hideSampleForm();
    this._submitSample(email, false);
  };

  // POST the request. FAIL-SAFE: wrapped in try/catch AND .catch(); a network,
  // CORS or malformed-response error surfaces a quiet inline message and restores
  // the button — it never throws out of here, and never blocks the configurator.
  Configurator.prototype._submitSample = function (email, loggedIn) {
    var self = this;
    if (this._sampleSubmitting) return;
    var hp = "";
    this._sampleWidgets.forEach(function (w) { if (w && w.hp && w.hp.value) hp = w.hp.value; });
    var body;
    try { body = this._buildSampleBody(email, loggedIn, hp); }
    catch (e) { this._flashSample(T("sample_fail", SAMPLE_FAIL_MSG), "error"); return; }
    this._sampleSubmitting = true;
    this._setSampleSubmitting(true);
    this._flashSample(T("sending", "Sending…"), "pending");
    var done = function () { self._sampleSubmitting = false; self._setSampleSubmitting(false); };
    // LIVE: POST the request to SAMPLE_ENDPOINT (CORS-locked to arachne.audio).
    // Fail-safe: any network / CORS / non-2xx / missing-fetch error surfaces a quiet
    // inline error and restores the button — it never throws out of here and never
    // blocks the configurator. Only a genuine 2xx response shows the confirmation,
    // so the customer is never told the request was sent when it wasn't.
    try {
      fetch(SAMPLE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(body)
      }).then(function (res) {
        if (!res || !res.ok) throw new Error("sample request failed: " + (res && res.status));
        self._hideSampleForm();
        self._hideSampleMeta();
        self._toggleSampleTrigger(false);
        self._flashSample(T("sample_ok", SAMPLE_OK_MSG), "success");
        track("sample_request", { product: self.productTitle });
      }).catch(function () {
        self._flashSample(T("sample_fail", SAMPLE_FAIL_MSG), "error");
      }).then(done, done);
    } catch (e) {
      this._flashSample(T("sample_fail", SAMPLE_FAIL_MSG), "error");
      done();
    }
  };

  // One ghost "Request a sample" widget per action bar (commit + wizard). Like the
  // share widget, every copy is tracked in _sampleWidgets and the state helpers act
  // on all of them so whichever bar is visible stays in sync. Carries an always-on
  // honeypot (off-screen) whose value rides along as `hp`.
  Configurator.prototype._makeSampleWidget = function (extraClass) {
    var self = this;
    var hp = h("input", { class: "acfg__sample-hp", type: "text", "aria-hidden": "true", tabindex: "-1", autocomplete: "off" });
    var label = h("span", { class: "acfg__samplebtn-label", text: this._sampleLabel });
    var trigger = btn({
      class: "acfg__samplebtn" + (extraClass ? " " + extraClass : ""),
      title: T("sample_title", "Ask for a photo of this braid before you buy"),
      on: { click: function () { self.requestSample(); } }
    }, [label]);
    var email = h("input", { class: "acfg__sample-email", type: "email", inputmode: "email", autocomplete: "email",
      "aria-label": T("sample_email_aria", "Your email for the sample photo"), placeholder: T("email_placeholder", "you@email.com") });
    var send = btn({ class: "acfg__sample-send", text: T("send", "Send") });
    var form = h("div", { class: "acfg__sample-form", style: "display:none;" }, [email, send]);
    var metaText = h("span", { class: "acfg__sample-meta-text" });
    var change = btn({ class: "acfg__sample-change", text: T("change", "change") });
    var meta = h("div", { class: "acfg__sample-meta", style: "display:none;" }, [metaText, change]);
    var status = h("div", { class: "acfg__sample-status", role: "status", "aria-live": "polite" });
    var widget = { root: null, btn: trigger, label: label, hp: hp, form: form, email: email, send: send, meta: meta, metaText: metaText, change: change, status: status };
    send.addEventListener("click", function () { self._sampleFormSubmit(widget); });
    email.addEventListener("keydown", function (e) {
      if (e && (e.key === "Enter" || e.keyCode === 13)) { if (e.preventDefault) e.preventDefault(); self._sampleFormSubmit(widget); }
    });
    change.addEventListener("click", function () { self._hideSampleMeta(); self._revealSampleForm(self._storedSampleEmail() || ""); });
    var root = h("div", { class: "acfg__sample" }, [hp, trigger, form, meta, status]);
    widget.root = root;
    this._sampleWidgets.push(widget);
    return root;
  };

  /* ---- sample widget display helpers (act on every tracked widget) ---- */
  Configurator.prototype._revealSampleForm = function (prefill) {
    this._sampleWidgets.forEach(function (w) {
      if (w.form) w.form.style.display = "";
      if (w.meta) w.meta.style.display = "none";
      if (w.email && prefill != null) w.email.value = prefill;
      if (w.email && typeof w.email.focus === "function") { try { w.email.focus(); } catch (e) {} }
    });
  };
  Configurator.prototype._hideSampleForm = function () {
    this._sampleWidgets.forEach(function (w) { if (w.form) w.form.style.display = "none"; });
  };
  Configurator.prototype._showSampleMeta = function (email) {
    this._sampleWidgets.forEach(function (w) {
      if (!w.meta) return;
      if (w.metaText) w.metaText.textContent = TF("sending_to", "Sending to %{email} · ", { email: email });
      w.meta.style.display = "";
    });
  };
  Configurator.prototype._hideSampleMeta = function () {
    this._sampleWidgets.forEach(function (w) { if (w.meta) w.meta.style.display = "none"; });
  };
  Configurator.prototype._toggleSampleTrigger = function (show) {
    this._sampleWidgets.forEach(function (w) { if (w.btn) w.btn.style.display = show ? "" : "none"; });
  };
  Configurator.prototype._setSampleSubmitting = function (on) {
    this._sampleWidgets.forEach(function (w) {
      [w.btn, w.send].forEach(function (b) { if (!b) return; b.disabled = on; b.classList.toggle("is-loading", on); });
    });
  };
  // Swap the status line (announced via aria-live). Terminal states (success /
  // error) auto-revert to the resting button after a short hold; "pending" persists
  // until the response replaces it.
  Configurator.prototype._flashSample = function (msg, kind) {
    var self = this;
    if (this._sampleTimer) { clearTimeout(this._sampleTimer); this._sampleTimer = null; }
    this._sampleWidgets.forEach(function (w) {
      if (!w.status) return;
      w.status.textContent = msg || "";
      w.status.classList.toggle("is-error", kind === "error");
      w.status.classList.toggle("is-success", kind === "success");
      w.status.classList.toggle("is-pending", kind === "pending");
    });
    if (kind === "success" || kind === "error") {
      this._sampleTimer = setTimeout(function () { self._resetSample(); }, kind === "success" ? 4000 : 6000);
    }
  };
  Configurator.prototype._resetSample = function () {
    if (this._sampleTimer) { clearTimeout(this._sampleTimer); this._sampleTimer = null; }
    this._toggleSampleTrigger(true);
    this._sampleWidgets.forEach(function (w) {
      if (!w.status) return;
      w.status.textContent = "";
      w.status.classList.remove("is-error");
      w.status.classList.remove("is-success");
      w.status.classList.remove("is-pending");
    });
  };

  /* -------------------------------- add to cart --------------------------- */
  Configurator.prototype.buildPayload = function () {
    var self = this;
    var configId = uuid();
    var properties = { _config_id: configId };
    // change 9: tag the base line with the model name so two different cable
    // configs in one cart are unambiguous. NEW internal key (leading "_"), added
    // ONLY to the base line — existing Globo globo_field keys are untouched.
    if (this.productTitle) properties._model = this.productTitle;
    var addonLines = [];

    this.options.forEach(function (o) {
      var sv = self.selectedValue(o);
      // Property value = the EXACT label from the data (preserves Globo's
      // leading-space quirks for order back-compatibility). Keys are EXACTLY
      // the globo_field names plus the internal _config_id — nothing else.
      properties[o.globo_field] = sv.label;
      if (sv.variant_id) {
        var lineProps = { _config_id: configId, _hidden: true };
        // Fermata strap attribution: continues the retired cart-upsell's _source
        // measurement lineage (attach rate = orders with this value / cable
        // orders; see HANDOFF-FERMATA-CONFIGURATOR-STEP.md). Underscore key ->
        // hidden everywhere, ignored by grouping.
        if (o._role === "strap") lineProps._source = "configurator-step";
        addonLines.push({ id: sv.variant_id, quantity: 1, properties: lineProps });
      }
    });

    var items = [{ id: numericId(this.baseVariantId), quantity: 1, properties: properties }];
    addonLines.forEach(function (l) { l.id = numericId(l.id); items.push(l); });
    return { items: items, configId: configId };
  };

  // opts.checkout: the "Buy it now" express path — same full-build add (all add-on
  // lines included), then straight to /checkout instead of opening the recap drawer.
  Configurator.prototype.addToCart = function (opts) {
    var self = this;
    var checkoutAfter = !!(opts && opts.checkout);
    if (this.submitting) return;
    if (!this.baseVariantId) { this._setStatus(T("error_no_base_variant", "No base variant configured for this product."), true); return; }
    this.submitting = true;
    this._setSubmitting(true);
    this._setStatus(checkoutAfter ? T("status_preparing_checkout", "Preparing checkout…") : T("status_adding", "Adding…"), false);

    var payload = this.buildPayload();
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ items: payload.items })
    }).then(function (res) {
      return res.json().then(function (body) { return { ok: res.ok, body: body }; });
    }).then(function (r) {
      if (!r.ok) throw new Error((r.body && (r.body.description || r.body.message)) || T("error_could_not_add", "Could not add to cart."));
      // Remember the just-added line keys so an "Edit configuration" from the recap
      // drawer can REPLACE them on the next add (instead of duplicating the cable).
      var added = (r.body && r.body.items) || [];
      self._lastAddKeys = [];
      for (var ai = 0; ai < added.length; ai++) { if (added[ai] && added[ai].key) self._lastAddKeys.push(added[ai].key); }
      // The build is in the cart — it's no longer an ABANDONED build, so drop the
      // resume save (else every buyer gets the "welcome back" bar forever after).
      try { window.localStorage.removeItem(self._buildStorageKey()); } catch (e) {}
      self._resumeReady = false; // and stop re-saving until the next fresh interaction cycle
      setTimeout(function () { self._resumeReady = true; }, 1000);
      // Replace-on-edit: when this add came from an Edit (drawer/cart handoff), drop
      // the original group's lines now that the new ones are safely in the cart.
      // FAIL-SAFE: a failed removal must never undo a successful add — swallow errors
      // (worst case the customer sees both builds, exactly the pre-fix behaviour).
      var replaceKeys = self._pendingReplaceKeys || [];
      self._pendingReplaceKeys = null;
      var finish = function () {
        // `value` + `currency` follow the GA4 convention so GTM can map them 1:1;
        // `total` stays for anything already listening to the old key.
        track(checkoutAfter ? "buy_now" : "add_to_cart", { product: self.productTitle, total: self.total(), value: self.total(), currency: self.settings.currency_code || "EUR", config_id: payload.configId, replaced: replaceKeys.length > 0 });
        // Express path: everything is in the cart — go straight to checkout. NOT in
        // the theme editor: checkout refuses to render inside the customizer iframe
        // (frame-ancestors CSP), which would leave the button stranded on
        // "Preparing checkout…" — there we fall through to the recap drawer instead.
        var inEditor = false;
        try { inEditor = !!(window.Shopify && window.Shopify.designMode); } catch (e) {}
        if (checkoutAfter && !inEditor) {
          try { window.location.assign("/checkout"); } catch (e) { window.location.href = "/checkout"; }
          // Belt-and-braces: if navigation is BLOCKED (sandboxed preview, in-app
          // webview rules), recover to the drawer — the build IS in the cart.
          // Gated on pagehide/visibility so a merely SLOW checkout nav (mobile
          // networks routinely exceed a fixed timer) doesn't pop the drawer
          // mid-navigation (audit 2026-07-14 #14).
          var navCommitted = false;
          var onPageHide = function () { navCommitted = true; };
          try { window.addEventListener("pagehide", onPageHide, { once: true }); } catch (e) {}
          setTimeout(function () {
            try { window.removeEventListener("pagehide", onPageHide); } catch (e) {}
            var hidden = false;
            try { hidden = document.visibilityState === "hidden"; } catch (e) {}
            if (navCommitted || hidden) return;
            self._setStatus("", false); self.openDrawer();
          }, 6000);
          return;
        }
        self._setStatus(checkoutAfter && inEditor ? T("status_added_editor", "Added to cart. (Checkout can't open inside the theme editor — use a preview tab.)") : "", false);
        self.openDrawer();
        // Let the host theme refresh its cart UI if it listens for these.
        document.dispatchEvent(new CustomEvent("arachne:cart:added", { detail: { configId: payload.configId, items: r.body } }));
        try { document.dispatchEvent(new CustomEvent("cart:refresh")); } catch (e) {}
      };
      if (replaceKeys.length) {
        var updates = {};
        for (var ri = 0; ri < replaceKeys.length; ri++) updates[replaceKeys[ri]] = 0;
        return fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ updates: updates })
        }).catch(function () {}).then(finish);
      }
      finish();
    }).catch(function (err) {
      track("add_error", { product: self.productTitle, message: (err && err.message) || "" });
      self._setStatus(err.message || T("error_generic", "Something went wrong. Please try again."), true);
    }).then(function () {
      self.submitting = false;
      self._setSubmitting(false);
    });
  };
  // Reflect submit state on every primary button (in-flow pair + sticky-bar commit).
  Configurator.prototype._setSubmitting = function (on) {
    [this.elAddBtn, this.elAddBtnMain, this.elBuyNowBtn].forEach(function (b) {
      if (!b) return;
      b.classList.toggle("is-loading", on);
      b.disabled = on;
    });
  };
  Configurator.prototype._setStatus = function (msg, isError) {
    [this.elStatus, this.elMainStatus].forEach(function (el) {
      if (!el) return;
      el.textContent = msg || "";
      el.classList.toggle("is-error", !!isError);
    });
  };

  function numericId(v) { var n = parseInt(v, 10); return isNaN(n) ? v : n; }
  // Tiny helper: build a state patch from alternating key/value args.
  function kv() { var o = {}; for (var i = 0; i < arguments.length; i += 2) o[arguments[i]] = arguments[i + 1]; return o; }

  /* --------------------------------- init --------------------------------- */
  function initAll() {
    _cssAccent = null; // theme-editor re-init may carry a NEW accent — drop the cache
    var roots = document.querySelectorAll("[data-acfg-root]");
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__acfg) continue;
      var inst = roots[i].__acfg = new Configurator(roots[i]);
      // Apply an inbound "Share this build" ?acfg= link first, then any pending
      // cart-drawer "Edit" handoff (the in-session Edit wins if both are present).
      // Both are guarded -- neither may ever break init on malformed input.
      try { if (inst && inst._consumeShareParam) inst._consumeShareParam(); } catch (e) {}
      try { if (inst && inst._consumePendingPrefill) inst._consumePendingPrefill(); } catch (e) {}
      // After both: offer to resume a locally-saved build (they win when present).
      try { if (inst && inst._maybeOfferResume) inst._maybeOfferResume(); } catch (e) {}
    }
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initAll);
  else initAll();
  // Re-init on Shopify theme-editor section load.
  document.addEventListener("shopify:section:load", initAll);

  // Expose for debugging / theme integration.
  window.ArachneConfigurator = Configurator;
})();

